<template>
  <div
    id="app"
    data-deploy-marker="DEPLOY-CHECK-20260312-A"
    :class="{ 'app-numerical-mode': isNumericalMode }"
    :style="layoutCssVars"
  >
    <div v-if="!isChatRoute" class="login-wrapper">
      <div
        class="login-card"
        :class="{ 'login-card-wide': requiresNumericalPlayerAssignment }"
      >
        <div class="login-header">
          <h2 class="login-title">
            {{
              demoMode || multiCh.length > 0 ? "使用者登入" : "系統管理員登入"
            }}
          </h2>
          <p class="login-subtitle">
            {{
              multiCh.length > 0
                ? `請輸入多 NPC 模式通行密鑰（${multiCh.join("、")}）`
                : requiresNumericalPlayerAssignment && loginCh
                ? `請輸入玩家代號與 ${loginCh} 的 6 位數通行密鑰，登入時會自動對應試算表`
                : requiresNumericalPlayerAssignment
                ? "請輸入玩家代號與 6 位數通行密鑰，登入時會自動對應個人試算表"
                : demoMode && loginCh
                ? `請輸入 ${loginCh} 的 6 位數通行密鑰`
                : demoMode
                ? "請輸入您的 6 位數通行密鑰以進入對話"
                : "請輸入您的專屬帳號與密碼以進入後台"
            }}
          </p>
        </div>

        <div
          v-if="demoMode || multiCh.length > 0"
          class="login-form-group"
          :class="
            requiresNumericalPlayerAssignment
              ? 'login-col-group login-numerical-group'
              : 'login-row-group'
          "
        >
          <div
            v-if="requiresNumericalPlayerAssignment"
            class="numerical-player-code-box"
          >
            <label
              class="numerical-player-code-label"
              for="numerical-player-code-input"
            >
              玩家代號
            </label>
            <div class="numerical-player-code-row">
              <input
                id="numerical-player-code-input"
                ref="numericalPlayerCodeInput"
                v-model.trim="numericalPlayerCode"
                type="text"
                class="login-form-input numerical-player-code-input"
                maxlength="4"
                autocapitalize="characters"
                placeholder="請輸入玩家代號"
                @input="syncNumericalPlayerCodeFromInput"
                @change="syncNumericalPlayerCodeFromInput"
                @blur="syncNumericalPlayerCodeFromInput"
                @keyup.enter="
                  multiCh.length > 0 ? loginStudentMulti() : loginStudent()
                "
              />
            </div>
          </div>
          <div
            class="login-access-code-group"
            :class="{
              'login-access-code-group-inline':
                !requiresNumericalPlayerAssignment,
            }"
          >
            <input
              v-model.trim="code"
              type="text"
              class="login-form-input login-code-input"
              maxlength="6"
              autocapitalize="characters"
              placeholder="請輸入 6 位數通行密鑰"
              @keyup.enter="
                multiCh.length > 0 ? loginStudentMulti() : loginStudent()
              "
            />
            <button
              class="login-btn login-btn-primary login-btn-submit"
              :disabled="isResolvingNumericalPlayerSheet"
              @click="multiCh.length > 0 ? loginStudentMulti() : loginStudent()"
            >
              {{ isResolvingNumericalPlayerSheet ? "驗證中..." : "登入" }}
            </button>
          </div>
        </div>

        <div v-else class="login-form-group login-col-group">
          <input
            v-model.trim="username"
            type="text"
            placeholder="管理員帳號"
            class="login-form-input"
          />
          <input
            v-model.trim="password"
            type="password"
            placeholder="管理員密碼"
            class="login-form-input"
            @keyup.enter="loginAdmin"
          />
          <button
            class="login-btn login-btn-primary login-btn-block"
            @click="loginAdmin"
          >
            進入編輯器
          </button>
          <!-- 開發測試專用，正式環境不會顯示 -->
          <button
            v-if="devBypassEnabled"
            class="login-btn login-btn-block login-btn-dev-bypass"
            @click="enterDevBypass"
          >
            本機測試模式
          </button>
        </div>

        <transition name="fade">
          <div v-if="error" class="login-error-box">
            <svg
              class="login-error-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
            >
              <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              />
            </svg>
            {{ error }}
          </div>
        </transition>
      </div>
    </div>

    <div v-else-if="render && adminRole === 'viewer'">
      <div style="min-height: 100vh; background: white"></div>
    </div>

    <div v-else-if="render && adminRole === 'keyManager'" class="py-4">
      <b-container>
        <b-row class="justify-content-center">
          <b-col cols="12" xl="10">
            <b-card header="通行密鑰管理">
              <div
                v-if="accessCodeList.length > 0"
                class="access-code-list-scroll"
              >
                <div
                  v-for="item in accessCodeList"
                  :key="item.id"
                  class="border rounded p-2 mb-2"
                >
                  <div><b>ID：</b>{{ item.id }}</div>
                  <div><b>密鑰：</b>{{ item.code }}</div>
                  <div><b>NPC：</b>{{ item.game_id }}</div>
                  <div>
                    <b>類型：</b
                    >{{ item.code_type === "one_time" ? "一次性" : "多次使用" }}
                  </div>
                  <div><b>開始：</b>{{ item.start_time }}</div>
                  <div><b>結束：</b>{{ item.end_time }}</div>
                  <div><b>備註：</b>{{ item.note || "-" }}</div>
                </div>
              </div>
              <p v-else class="text-muted mb-0">目前沒有通行密鑰資料</p>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!--判斷要不要進數值鷹架模式-->
    <div v-else-if="render && isNumericalMode">
      <NumericalNPC
        :show-config-controls="false"
        :sheet-url="numericalModeSheetUrl"
        :sheet-link-url="numericalModeSheetEditUrl"
        :sheet-id="numericalSCSheetID"
        :sheet-id-locked="Boolean($route.query.sheetId)"
        :sheet-range="numericalSCSheetRange"
        :task-description="situationText"
        :task-embed-url="numericalTaskIframeUrl"
        :npc-name="showName || name || $route.query.ch || studentGameId"
        :room-code="roomCode || loginRoom || $route.query.room || ''"
        :user-name="userName"
        :chat-log="visibleChatLog"
        :chat-content="chatContent"
        :sheet-changes="numericalSheetChanges"
        :is-loading-sheet="
          isLoadingNumericalSheet || isLoadingNumericalBaseline
        "
        :is-sheet-submit-disabled="!numericalBaselineReady"
        :sheet-status-message="numericalBaselineStatusMessage"
        :is-ai-message-locked="isAiMessageLocked"
        :wait-animation="waitAnimation"
        :end-game="endGame"
        @update:sheetId="numericalSCSheetID = $event"
        @update:sheetRange="numericalSCSheetRange = $event"
        @update:taskEmbedUrl="handleNumericalTaskIframeInput($event)"
        @update:chatContent="chatContent = $event"
        @resize-chat-input="resizeChatInput($event)"
        @composition-start="isComposing = true"
        @composition-end="isComposing = false"
        @send-message="sendMessage(true, $event)"
        @submit-sheet="onSubmitSheet"
      >
        <template #chat-panel>
          <div class="numerical-chat-shell">
            <div v-if="!numericalNameSubmitted" class="numerical-name-prompt">
              <div class="numerical-name-card">
                <h3>請先輸入玩家名稱</h3>
                <p>名稱將顯示在聊天室訊息中。</p>
                <label for="numerical-user-name">玩家名稱</label>
                <b-input-group>
                  <b-form-input
                    id="numerical-user-name"
                    v-model.trim="userName"
                    autocomplete="name"
                    autofocus
                    placeholder="請輸入玩家名稱"
                    @keyup.enter="submitNumericalName"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button
                      variant="outline-success"
                      @click="submitNumericalName"
                      >送出</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
            <div v-if="situationText" class="numerical-situation-banner">
              <b-badge variant="info" class="layout-guide-badge">
                {{ situationText }}
              </b-badge>
            </div>
            <b-nav fill class="numerical-chat-status">
              <b-nav-item disabled
                >玩家：{{ userName || "尚未設定" }}</b-nav-item
              >
              <b-nav-item disabled>
                連線狀態：{{ isConnected ? "已連線" : "斷線" }}
              </b-nav-item>
            </b-nav>
            <div v-if="avatarUrl" class="numerical-avatar-section">
              <div class="avatar-container">
                <img
                  :src="avatarUrl"
                  :alt="showName + '的頭像'"
                  class="npc-avatar"
                  @error="onImageError"
                />
                <h6 class="text-center mt-2 mb-0">{{ showName }}</h6>
              </div>
            </div>

            <div class="chatLayout numerical-chat-layout">
              <b-col
                cols="12"
                ref="chatRef"
                class="chatBox numerical-chat-box"
                :class="{ 'messenger-chat-box': layoutSetting.messenger_style }"
              >
                <p
                  v-for="(item, index) in visibleChatLog"
                  :key="index"
                  class="animate__animated animate__fadeInLeft animate__faster"
                  :class="messageRowClass(item)"
                >
                  <b v-if="!layoutSetting.messenger_style"
                    >{{ item.userName == userName ? "我" : item.userName }}：</b
                  >
                  <span
                    v-else
                    class="messenger-message-name"
                    :class="{
                      'messenger-message-name-own': isOwnMessage(item),
                      'messenger-message-name-other': !isOwnMessage(item),
                    }"
                    >{{ item.userName }}</span
                  >
                  <vue-typed-js
                    :strings="[item.content]"
                    :style="
                      layoutSetting.messenger_style
                        ? { display: 'block', width: '100%' }
                        : { display: 'inline' }
                    "
                    :showCursor="false"
                    :typeSpeed="typeSpeed"
                    @preStringTyped="handleTypingStart(item)"
                    @onComplete="handleTypingComplete(item)"
                  >
                    <span
                      class="typing"
                      :class="messageBubbleClass(item)"
                    ></span>
                  </vue-typed-js>
                </p>
                <transition
                  enter-active-class="animate__animated animate__fadeInLeft"
                  leave-active-class="animate__animated animate__fadeOutLeft"
                >
                  <p v-if="waitAnimation" class="text-left">
                    <b class="float-left">{{ showName }}</b
                    >思考回應中... (若無回應，請換一句話來提問...)
                    <b-skeleton width="300px">hi</b-skeleton>
                  </p>
                </transition>
                <transition
                  enter-active-class="animate__animated animate__fadeInLeft"
                  leave-active-class="animate__animated animate__fadeOutLeft"
                >
                  <p v-if="ttsLoading" class="text-left text-secondary">
                    <b-spinner small></b-spinner>
                    <span class="ml-2">語音回覆中...</span>
                  </p>
                </transition>
                <b-card
                  title="遊戲結束"
                  bg-variant="danger"
                  text-variant="white"
                  v-if="endGame || endConTrigger === true"
                >
                </b-card>
                <b-card
                  title="表現評估"
                  bg-variant="primary"
                  text-variant="white"
                  v-if="
                    (endGame || endConTrigger === true) &&
                    (scaleSCSwitch == true || (manualEndGame && !hideRoomCode))
                  "
                >
                  <b-card-text class="text-left">
                    <span style="display: none">{{ scaleMessage() }}</span>
                    <div v-html="renderMarkdown(endResult)"></div>
                  </b-card-text>
                </b-card>
              </b-col>

              <div class="numerical-chat-input-wrap" v-if="!endGame">
                <b-input-group>
                  <textarea
                    v-model="chatContent"
                    class="form-control border border-dark chat-input-autosize"
                    rows="1"
                    @input="resizeChatInput"
                    @focus="resizeChatInput"
                    @keydown.enter="sendMessage(true, $event)"
                    @compositionstart="isComposing = true"
                    @compositionend="isComposing = false"
                    :disabled="isAiMessageLocked || endGame"
                    :placeholder="
                      endGame
                        ? '遊戲已結束'
                        : askLimit > 0 && askLimitCal <= 0
                        ? 'NPC提問次數已用完'
                        : '請輸入您的問題...'
                    "
                  ></textarea>
                  <b-input-group-append>
                    <b-button
                      variant="primary"
                      @click="sendMessage(true)"
                      :disabled="isAiMessageLocked || endGame"
                    >
                      <img
                        src="@/assets/toNPC.svg"
                        alt="送出給NPC"
                        class="peer-icon"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
          </div>
        </template>
      </NumericalNPC>
    </div>

    <!--判斷要不要進數值鷹架模式-->
    <div v-else-if="render && isNumericalMode">
      <NumericalNPC
        :show-config-controls="false"
        :sheet-url="numericalModeSheetUrl"
        :sheet-link-url="numericalModeSheetEditUrl"
        :sheet-id="numericalSCSheetID"
        :sheet-id-locked="Boolean($route.query.sheetId)"
        :sheet-range="numericalSCSheetRange"
        :task-description="situationText"
        :task-embed-url="numericalTaskIframeUrl"
        :npc-name="showName || name || $route.query.ch || studentGameId"
        :room-code="roomCode || loginRoom || $route.query.room || ''"
        :user-name="userName"
        :chat-log="visibleChatLog"
        :chat-content="chatContent"
        :sheet-changes="numericalSheetChanges"
        :is-loading-sheet="
          isLoadingNumericalSheet || isLoadingNumericalBaseline
        "
        :is-sheet-submit-disabled="!numericalBaselineReady"
        :sheet-status-message="numericalBaselineStatusMessage"
        :is-ai-message-locked="isAiMessageLocked"
        :wait-animation="waitAnimation"
        :end-game="endGame"
        @update:sheetId="numericalSCSheetID = $event"
        @update:sheetRange="numericalSCSheetRange = $event"
        @update:taskEmbedUrl="handleNumericalTaskIframeInput($event)"
        @update:chatContent="chatContent = $event"
        @resize-chat-input="resizeChatInput($event)"
        @composition-start="isComposing = true"
        @composition-end="isComposing = false"
        @send-message="sendMessage(true, $event)"
        @submit-sheet="onSubmitSheet"
      >
        <template #chat-panel>
          <div class="numerical-chat-shell">
            <div v-if="!numericalNameSubmitted" class="numerical-name-prompt">
              <div class="numerical-name-card">
                <h3>請先輸入玩家名稱</h3>
                <p>名稱將顯示在聊天室訊息中。</p>
                <label for="numerical-user-name">玩家名稱</label>
                <b-input-group>
                  <b-form-input
                    id="numerical-user-name"
                    v-model.trim="userName"
                    autocomplete="name"
                    autofocus
                    placeholder="請輸入玩家名稱"
                    @keyup.enter="submitNumericalName"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button
                      variant="outline-success"
                      @click="submitNumericalName"
                      >送出</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
            <div v-if="situationText" class="numerical-situation-banner">
              <b-badge variant="info" class="layout-guide-badge">
                {{ situationText }}
              </b-badge>
            </div>
            <b-nav fill class="numerical-chat-status">
              <b-nav-item disabled
                >玩家：{{ userName || "尚未設定" }}</b-nav-item
              >
              <b-nav-item disabled>
                連線狀態：{{ isConnected ? "已連線" : "斷線" }}
              </b-nav-item>
            </b-nav>
            <div v-if="avatarUrl" class="numerical-avatar-section">
              <div class="avatar-container">
                <img
                  :src="avatarUrl"
                  :alt="showName + '的頭像'"
                  class="npc-avatar"
                  @error="onImageError"
                />
                <h6 class="text-center mt-2 mb-0">{{ showName }}</h6>
              </div>
            </div>

            <div class="chatLayout numerical-chat-layout">
              <b-col
                cols="12"
                ref="chatRef"
                class="chatBox numerical-chat-box"
                :class="{ 'messenger-chat-box': layoutSetting.messenger_style }"
              >
                <p
                  v-for="(item, index) in visibleChatLog"
                  :key="index"
                  class="animate__animated animate__fadeInLeft animate__faster"
                  :class="messageRowClass(item)"
                >
                  <b v-if="!layoutSetting.messenger_style"
                    >{{ item.userName == userName ? "我" : item.userName }}：</b
                  >
                  <span
                    v-else
                    class="messenger-message-name"
                    :class="{
                      'messenger-message-name-own': isOwnMessage(item),
                      'messenger-message-name-other': !isOwnMessage(item),
                    }"
                    >{{ item.userName }}</span
                  >
                  <vue-typed-js
                    :strings="[item.content]"
                    :style="
                      layoutSetting.messenger_style
                        ? { display: 'block', width: '100%' }
                        : { display: 'inline' }
                    "
                    :showCursor="false"
                    :typeSpeed="typeSpeed"
                    @preStringTyped="handleTypingStart(item)"
                    @onComplete="handleTypingComplete(item)"
                  >
                    <span
                      class="typing"
                      :class="messageBubbleClass(item)"
                    ></span>
                  </vue-typed-js>
                </p>
                <transition
                  enter-active-class="animate__animated animate__fadeInLeft"
                  leave-active-class="animate__animated animate__fadeOutLeft"
                >
                  <p v-if="waitAnimation" class="text-left">
                    <b class="float-left">{{ showName }}</b
                    >思考回應中... (若無回應，請換一句話來提問...)
                    <b-skeleton width="300px">hi</b-skeleton>
                  </p>
                </transition>
                <transition
                  enter-active-class="animate__animated animate__fadeInLeft"
                  leave-active-class="animate__animated animate__fadeOutLeft"
                >
                  <p v-if="ttsLoading" class="text-left text-secondary">
                    <b-spinner small></b-spinner>
                    <span class="ml-2">語音回覆中...</span>
                  </p>
                </transition>
                <b-card
                  title="遊戲結束"
                  bg-variant="danger"
                  text-variant="white"
                  v-if="endGame || endConTrigger === true"
                >
                </b-card>
                <b-card
                  title="表現評估"
                  bg-variant="primary"
                  text-variant="white"
                  v-if="
                    (endGame || endConTrigger === true) &&
                    (scaleSCSwitch == true || (manualEndGame && !hideRoomCode))
                  "
                >
                  <b-card-text class="text-left">
                    <span style="display: none">{{ scaleMessage() }}</span>
                    <div v-html="renderMarkdown(endResult)"></div>
                  </b-card-text>
                </b-card>
              </b-col>

              <div class="numerical-chat-input-wrap" v-if="!endGame">
                <b-input-group>
                  <textarea
                    v-model="chatContent"
                    class="form-control border border-dark chat-input-autosize"
                    rows="1"
                    @input="resizeChatInput"
                    @focus="resizeChatInput"
                    @keydown.enter="sendMessage(true, $event)"
                    @compositionstart="isComposing = true"
                    @compositionend="isComposing = false"
                    :disabled="isAiMessageLocked || endGame"
                    :placeholder="
                      endGame
                        ? '遊戲已結束'
                        : askLimit > 0 && askLimitCal <= 0
                        ? 'NPC提問次數已用完'
                        : '請輸入您的問題...'
                    "
                  ></textarea>
                  <b-input-group-append>
                    <b-button
                      variant="primary"
                      @click="sendMessage(true)"
                      :disabled="isAiMessageLocked || endGame"
                    >
                      <img
                        src="@/assets/toNPC.svg"
                        alt="送出給NPC"
                        class="peer-icon"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
          </div>
        </template>
      </NumericalNPC>
    </div>

    <div
      v-else-if="render"
      :class="{ 'doc-split-layout': isDocMode && !multiCh.length }"
    >
      <!-- 同步文件模式：左側文件編輯器 -->
      <doc-editor
        v-if="
          isDocMode &&
          !multiCh.length &&
          (demoMode == false || (demoMode == true && name != null))
        "
        class="doc-split-left"
        :room-id="loginRoom"
        :user-name="userName || name || '匿名'"
        :hocuspocus-url="hocuspocusUrl"
        @content-update="currentDocContent = $event"
        @typing-end="postTypingLog($event)"
      />
      <!-- 多 NPC 模式 -->
      <multi-n-p-c v-if="multiCh.length > 0" :multi-ch="multiCh" />
      <!-- 一般 NPC / 管理員模式 -->
      <b-container
        v-else-if="demoMode == false || (demoMode == true && name != null)"
        :class="{ 'chat-in-doc-mode': isDocMode }"
      >
        <b-row class="justify-content-center">
          <b-col cols="12" sm="6">
            <b-nav fill>
              <b-nav-item disabled>名稱：{{ userName }}</b-nav-item>
              <b-nav-item disabled v-if="!hideRoomCode"
                >聊天室：{{ roomCode }}</b-nav-item
              >
              <b-nav-item disabled
                >連線狀態：{{ isConnected ? "已連線" : "斷線" }}</b-nav-item
              >
              <!-- 遊戲結束按鈕 - 未來可能會開放 -->
              <!-- <b-nav-item v-if="askLimit > 0 && !endGame">
              <h4>
                <b-badge
                  href="#"
                  variant="danger"
                  @click="forceEndGame"
                  style="cursor: pointer"
                >
                  遊戲結束
                </b-badge>
              </h4>
            </b-nav-item> -->
              <b-nav-item disabled v-if="!isPeerOnlyMode && askLimit > 0">
                <h4>
                  <b-badge
                    href="#"
                    variant="primary"
                    class="layout-ask-limit-badge"
                    ><span>剩餘提問次數 (個人) </span>
                    <b-badge variant="light">{{
                      askLimitCal
                    }}</b-badge></b-badge
                  >
                </h4>
              </b-nav-item>
              <b-nav-item disabled v-if="!isPeerOnlyMode && challengeMode">
                <h4>
                  <b-badge href="#" variant="warning"
                    >已提問次數
                    <b-badge variant="light">{{
                      challengeCal
                    }}</b-badge></b-badge
                  >
                </h4>
              </b-nav-item>
            </b-nav>
          </b-col>
        </b-row>
        <b-row class="justify-content-center">
          <b-col cols="6" v-show="this.demoMode == false">
            <b-col cols="12" class="text-left">
              <b-card no-body>
                <b-card-header v-b-toggle.projectSetting>
                  <b-card-title>專案設定</b-card-title>
                </b-card-header>

                <b-collapse id="projectSetting" accordion visible>
                  <b-card-body>
                    <b-button variant="outline-success" @click="setRoomCode"
                      >輸入房間號碼</b-button
                    >
                    <br />
                    <p>目前暱稱: {{ userName }}</p>
                    <p>目前連線狀態: {{ isConnected }}</p>
                    <p>
                      目前房號: {{ roomCode }}
                      <span v-if="hideRoomCode == true"
                        >(單人模式代理房號)</span
                      >
                    </p>

                    <b-input-group prepend="角色名稱 (只能是英文代碼)">
                      <b-form-input
                        v-model="name"
                        autocomplete="off"
                      ></b-form-input>
                    </b-input-group>
                    <b-input-group prepend="角色顯示名稱 (中英文皆可)">
                      <b-form-input
                        v-model="showName"
                        autocomplete="off"
                      ></b-form-input>
                    </b-input-group>

                    <b-button variant="outline-danger" @click="toogleOpen"
                      >專案開關：目前是 {{ open ? "開" : "關" }}</b-button
                    >
                    <p>記得要儲存才會變動</p>

                    <b-button variant="outline-success" @click="setGPTData"
                      >新增/複寫</b-button
                    >
                    <b-button
                      variant="outline-success"
                      @click="getGPTData(name)"
                      >讀取</b-button
                    >

                    <br />
                    <br />
                  </b-card-body>
                </b-collapse>
              </b-card>

              <b-card no-body>
                <b-card-header v-b-toggle.layoutSetting>
                  <b-card-title>對話框版面風格</b-card-title>
                </b-card-header>

                <b-collapse id="layoutSetting" accordion>
                  <b-card-body>
                    <b-input-group prepend="文字大小">
                      <b-form-select
                        v-model="layoutSetting.text_size_level"
                        :options="layoutTextSizeOptions"
                      ></b-form-select>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="文字顏色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.text_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="底板顏色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.background_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="輸入框文字顏色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.input_text_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="輸入框底板顏色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.input_background_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="輸入框按鈕顏色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.button_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="剩餘次數框底色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.ask_limit_background_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="引導語框底色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.guide_background_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-form-checkbox
                      v-model="layoutSetting.messenger_style"
                      switch
                    >
                      使用 Messenger 風格對話框
                    </b-form-checkbox>
                    <br />
                    <b-input-group prepend="對方訊息框底色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.messenger_other_bubble_color"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="我的訊息框底色">
                      <b-form-input
                        type="color"
                        v-model="layoutSetting.messenger_own_bubble_color"
                      ></b-form-input>
                    </b-input-group>
                  </b-card-body>
                </b-collapse>
              </b-card>

              <b-card no-body>
                <b-card-header v-b-toggle.basicSetting>
                  <b-card-title>基礎設定</b-card-title>
                </b-card-header>

                <b-collapse id="basicSetting" accordion visible>
                  <b-card-body>
                    <b-input-group prepend="npc頭像URL">
                      <b-form-textarea
                        id="textarea-large"
                        v-model="avatarUrl"
                        autocomplete="off"
                      >
                      </b-form-textarea>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="人設">
                      <b-form-textarea
                        id="textarea-large"
                        v-model="character"
                        autocomplete="off"
                      >
                      </b-form-textarea>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="知識資料">
                      <b-form-textarea
                        id="textarea-large"
                        v-model="knowledge"
                        autocomplete="off"
                      >
                      </b-form-textarea>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="溫度">
                      <b-form-input
                        type="number"
                        v-model="temperature"
                        min="0"
                        max="1"
                      ></b-form-input>
                    </b-input-group>
                    <p>0.0 ~ 1.0，預設值為 1.0，越大回覆變化越多</p>
                    <br />
                    <b-input-group prepend="最高權杖數">
                      <b-form-input
                        type="number"
                        v-model="maxTokens"
                        min="100"
                        max="800"
                      ></b-form-input>
                    </b-input-group>
                    <p>
                      0 ~ 128000 ，預設值 800，用來管理回應的長度，建議 2000
                      以內。
                    </p>
                    <b-input-group prepend="情境文字">
                      <b-form-input
                        v-model="situationText"
                        autocomplete="off"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="開場白">
                      <b-form-input
                        v-model="firstTalk"
                        autocomplete="off"
                      ></b-form-input>
                    </b-input-group>
                    <br />
                    <b-input-group prepend="提問次數限制">
                      <b-form-input
                        type="number"
                        v-model.number="askLimit"
                        min="0"
                        @input="onAskLimitInput"
                      ></b-form-input>
                    </b-input-group>
                    <p>0 代表不限制</p>
                    <b-input-group prepend="打字機特效速度">
                      <b-form-input
                        type="number"
                        v-model.number="typeSpeed"
                        min="0"
                        max="1000"
                      ></b-form-input>
                    </b-input-group>
                    <p>0代表無特效，請輸入整數</p>
                    <h6>挑戰模式 (挑戰模式會顯示向 NPC 提問的次數)</h6>
                    <b-form-checkbox switch v-model="challengeMode">
                      {{ challengeMode ? "啟用" : "關閉" }}
                    </b-form-checkbox>
                  </b-card-body>

                  <b-card no-body>
                    <b-card-header class="bg-success">
                      <b-card-title>
                        <b-row>
                          <b-col cols="5">關鍵字結束設定 </b-col>
                          <b-col cols="5">
                            <b-form-checkbox switch v-model="endConSwitch">
                              {{ endConSwitch ? "啟用" : "關閉" }}
                            </b-form-checkbox>
                          </b-col>
                          <b-col cols="2"> </b-col>
                        </b-row>
                      </b-card-title>
                    </b-card-header>
                    <b-card-body v-if="endConSwitch">
                      <b-input-group prepend="當此詞彙" autocomplete="off">
                        <b-form-input v-model="endConConcept"></b-form-input>
                      </b-input-group>
                      <b-input-group
                        prepend="當玩家有"
                        append="個訊息包含此詞彙"
                      >
                        <b-form-input
                          v-model="endConQ"
                          type="number"
                          min="0"
                          max="100"
                        ></b-form-input>
                      </b-input-group>
                      <b-input-group prepend="當NPC" append="個訊息包含此詞彙">
                        <b-form-input
                          v-model="endConA"
                          type="number"
                          min="0"
                          max="100"
                        ></b-form-input>
                      </b-input-group>
                      <br />

                      <span>就回饋</span>
                      <b-input-group>
                        <b-form-textarea
                          v-model="endConString"
                          autocomplete="off"
                        ></b-form-textarea>
                      </b-input-group>
                      <b-button variant="outline-success" @click="addEndCon"
                        >新增</b-button
                      >
                      <br />
                      <b-badge
                        v-for="(item, index) in endCon"
                        :key="'endCon' + index"
                        href="#"
                        variant="success"
                        @click="removeEndCon(index)"
                        >當{{ item.concept }}出現在玩家訊息{{
                          item.q
                        }}且在NPC訊息出現{{ item.a }}就以{{
                          item.content
                        }}進行回饋，並結束對話</b-badge
                      >
                      <br />
                    </b-card-body>
                  </b-card>
                </b-collapse>
              </b-card>
              <br />
              <b-card no-body>
                <b-card-header v-b-toggle.scaffoldSetting>
                  <b-card-title>鷹架設定</b-card-title>
                </b-card-header>

                <b-collapse id="scaffoldSetting" accordion visible>
                  <b-card-body>
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">主動鷹架設定</b-col>
                            <b-col cols="5">
                              <b-form-checkbox switch v-model="initSwitch">
                                {{ initSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2">
                              <h5>
                                <b-badge variant="primary">限單人</b-badge>
                              </h5>
                            </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-show="initSwitch">
                        <b-input-group prepend="固定鷹架顯示名稱">
                          <b-form-input
                            v-model="hintName"
                            autocomplete="off"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group prepend="固定內容提示">
                          <b-form-input
                            v-model="initSCcontent"
                            autocomplete="off"
                          ></b-form-input>
                        </b-input-group>
                        <p class="text-secondary">
                          範例：「[提示]：請多留意老者的健康喔」
                        </p>
                        <b-button variant="outline-success" @click="addInitSC"
                          >新增</b-button
                        >
                        <br />
                        <br />

                        <b-input-group prepend="在第幾句之後">
                          <b-form-input
                            type="number"
                            v-model.number="initStart"
                            min="0"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group prepend="出現機率" append="%">
                          <b-form-input
                            type="number"
                            v-model.number="initPro"
                            min="0"
                            max="100"
                          ></b-form-input>
                        </b-input-group>
                        <b-form-checkbox
                          v-model="initRandom"
                          name="check-button"
                          switch
                        >
                          隨機順序出現
                        </b-form-checkbox>

                        <p class="text-secondary">
                          規則總結，在第句 {{ initStart }} 後 (亦即第
                          {{ parseInt(initStart) + 1 }} 句開始)，有
                          {{ initPro }}% 的機率從下列提示詞中<b>{{
                            initRandom == true ? "隨機" : "依序"
                          }}</b
                          >觸發
                        </p>
                        <b-badge
                          v-for="(item, index) in initSC"
                          :key="'initsc' + index"
                          href="#"
                          variant="success"
                          @click="removeInitSC(index)"
                          >{{ item }}</b-badge
                        >
                        <p v-show="initSC.length != 0" class="text-secondary">
                          點擊綠色方塊可刪除提示詞
                        </p>
                      </b-card-body>
                    </b-card>
                    <br />

                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">條件鷹架設定 </b-col>
                            <b-col cols="5">
                              <b-form-checkbox
                                switch
                                v-model="progressiveSCSwitch"
                              >
                                {{ progressiveSCSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2"> </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-if="progressiveSCSwitch">
                        <b-input-group prepend="條件鷹架顯示名稱">
                          <b-form-input
                            v-model="hintName2"
                            autocomplete="off"
                          ></b-form-input>
                        </b-input-group>
                        <p>預設是「[提示]」</p>
                        <b-input-group prepend="當此詞彙" autocomplete="off">
                          <b-form-input
                            v-model="progressiveSCConcept"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group
                          prepend="當玩家有"
                          append="個訊息包含此詞彙"
                        >
                          <b-form-input
                            v-model="progressiveSCQ"
                            type="number"
                            min="0"
                            max="100"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group
                          prepend="當NPC"
                          append="個訊息包含此詞彙"
                        >
                          <b-form-input
                            v-model="progressiveSCA"
                            type="number"
                            min="0"
                            max="100"
                          ></b-form-input>
                        </b-input-group>
                        <br />
                        <b-form-checkbox switch v-model="progessiveSCGen">
                          {{ progessiveSCGen ? "生成" : "固定" }}
                        </b-form-checkbox>

                        <span
                          >就依照下列<b>{{
                            progessiveSCGen ? "提示詞生成" : "文字進行"
                          }}</b
                          >回饋</span
                        >
                        <b-input-group>
                          <b-form-textarea
                            v-model="progressiveSCString"
                            autocomplete="off"
                          ></b-form-textarea>
                        </b-input-group>
                        <b-button
                          variant="outline-success"
                          @click="addProgressiveSC"
                          >新增</b-button
                        >
                        <b-badge
                          v-for="(item, index) in progressiveSC"
                          :key="'progressiveSC' + index"
                          href="#"
                          variant="success"
                          @click="removeProgressiveSC(index)"
                          >當{{ item.concept }}出現在玩家訊息{{
                            item.q
                          }}且在NPC訊息出現{{ item.a }}就以{{
                            item.gen ? "提示詞" : "文字"
                          }}{{ item.content
                          }}{{ item.gen ? "生成回饋" : "進行回饋" }}</b-badge
                        >
                        <br />
                      </b-card-body>
                    </b-card>
                    <br />
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">自動鷹架設定 </b-col>
                            <b-col cols="5">
                              <b-form-checkbox switch v-model="autoSCSwitch">
                                {{ autoSCSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2">
                              <h5>
                                <b-badge variant="danger">限多人</b-badge>
                              </h5>
                            </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-if="autoSCSwitch">
                        <b-input-group prepend="自動鷹架顯示名稱">
                          <b-form-input
                            v-model="hintName3"
                            autocomplete="off"
                          ></b-form-input>
                        </b-input-group>
                        <br />
                        <b-input-group prepend="大於這個數字就進行總整">
                          <b-form-input
                            v-model.number="autoSC"
                            type="number"
                            min="2"
                            max="10"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group prepend="出現機率" append="%">
                          <b-form-input
                            v-model="autoSCProbability"
                            type="number"
                            min="0"
                            max="100"
                          ></b-form-input>
                        </b-input-group>
                        <span>就依照下列提示詞進行總整</span>
                        <b-input-group>
                          <b-form-textarea
                            v-model="autoMessagePrompt"
                            autocomplete="off"
                          ></b-form-textarea>
                        </b-input-group>
                        <p class="text-secondary">
                          預設值是：請根據目前討論紀錄，依據討論的內容與討論的目的發表你的看法
                        </p>
                      </b-card-body>
                    </b-card>
                    <br />
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">指標性總結鷹架設定</b-col>
                            <b-col cols="5">
                              <b-form-checkbox switch v-model="scaleSCSwitch">
                                {{ scaleSCSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2"> </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-if="scaleSCSwitch">
                        <span
                          >當使用者用光所有提問次數，就依照下列指標提示詞進行總整
                          (提問次數為無限時不生效)</span
                        >
                        <b-input-group>
                          <b-form-textarea
                            v-model="scaleSC"
                            autocomplete="off"
                          ></b-form-textarea>
                        </b-input-group>
                        <p>
                          參考提示詞：請根據對話紀錄進行分析。每當我提到一種水果獲得
                          2 分，最後請依照下列格式回報我分數：「你總共提到了 N
                          種水果，因次獲得 X分」，並最後給我飲食分析建議
                        </p>
                      </b-card-body>
                    </b-card>
                    <br />
                    <!-- 階段式文件回饋鷹架設定 -->
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">階段式文件回饋鷹架</b-col>
                            <b-col cols="5">
                              <b-form-checkbox
                                switch
                                v-model="docFeedbackSwitch"
                              >
                                {{ docFeedbackSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2"> </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-if="docFeedbackSwitch">
                        <span>累積指定對話數後由 NPC 提供文件回饋。</span>
                        <b-form-group
                          label="每累積幾句對話觸發（玩家 + NPC 合計）"
                          class="mt-2"
                        >
                          <b-form-input
                            type="number"
                            v-model.number="docFeedbackN"
                            min="2"
                            max="100"
                          />
                        </b-form-group>
                        <b-form-group
                          label="文件回饋 Prompt（文件內容將自動附加在後方）"
                        >
                          <b-form-textarea
                            v-model="docFeedbackPrompt"
                            rows="4"
                            placeholder="例如：請根據以下學生寫的文件，給予具體改善建議，並指出邏輯不清晰的地方..."
                          />
                        </b-form-group>
                      </b-card-body>
                    </b-card>
                    <br />
                    <!-- Step Scale 設定 -->
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">階段性評分鷹架</b-col>
                            <b-col cols="5">
                              <b-form-checkbox
                                switch
                                v-model="stepScaleSCSwitch"
                              >
                                {{ stepScaleSCSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2">
                              <h5>
                                <b-badge variant="primary">限單人</b-badge>
                              </h5>
                            </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>

                      <b-card-body v-if="stepScaleSCSwitch">
                        <b-form-group label="名稱">
                          <b-form-input
                            v-model.trim="hintName4"
                            maxlength="64"
                            placeholder="[提示]"
                          />
                        </b-form-group>
                        <b-form-group
                          label="每幾句用提示詞分析並記錄（結果不對使用者顯示）"
                        >
                          <b-form-textarea
                            v-model.trim="recordSC"
                            rows="4"
                            placeholder="分析聊天內容"
                          />
                        </b-form-group>

                        <b-form-group label="每幾句紀錄">
                          <b-input-group>
                            <b-form-input
                              type="number"
                              min="0"
                              step="1"
                              v-model.number="recordSCStep"
                            />
                            <b-input-group-append>
                              <b-input-group-text>句</b-input-group-text>
                            </b-input-group-append>
                          </b-input-group>
                          <small class="text-muted d-block mt-1">
                            例：填 3 代表在第 4 / 7 / 10 句送出分析。
                          </small>
                        </b-form-group>

                        <b-form-group label="回饋用提示詞">
                          <b-form-textarea
                            v-model.trim="stepScaleSC"
                            rows="4"
                            placeholder="分析聊天內容"
                          />
                        </b-form-group>

                        <b-form-group label="每幾句進行回饋">
                          <b-input-group>
                            <b-form-input
                              type="number"
                              min="0"
                              step="1"
                              v-model.number="stepScaleSCStep"
                            />
                            <b-input-group-append>
                              <b-input-group-text>句</b-input-group-text>
                            </b-input-group-append>
                          </b-input-group>
                          <small class="text-muted d-block mt-1">
                            例：填 3 代表在第 4 / 7 / 10 句送出回饋。
                          </small>
                        </b-form-group>
                      </b-card-body>
                    </b-card>

                    <br />
                    <!-- 數值鷹架設定 -->
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">數值鷹架設定</b-col>
                            <b-col cols="5">
                              <b-form-checkbox
                                switch
                                v-model="numericalSCSwitch"
                              >
                                {{ numericalSCSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2">
                              <h5>
                                <b-badge variant="primary">限單人</b-badge>
                              </h5>
                            </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>

                      <b-card-body v-if="numericalSCSwitch">
                        <b-input-group prepend="數值鷹架顯示名稱">
                          <b-form-input
                            v-model="hintName5"
                            autocomplete="off"
                          ></b-form-input>
                        </b-input-group>
                        <p>預設是「[提示]」</p>

                        <b-input-group prepend="當試算表欄位在">
                          <b-form-input
                            v-model="numericalSCPosition"
                            type="text"
                          ></b-form-input>
                        </b-input-group>
                        <p>例如 : F2、A15</p>
                        <b-input-group prepend="當數值">
                          <b-form-select
                            v-model="numericalSCOperator"
                            :options="['>', '<', '=']"
                            style="flex: 0 0 80px; min-width: 60px"
                          >
                          </b-form-select>
                          <b-form-input
                            v-model="numericalSCNumber"
                            type="number"
                          ></b-form-input>
                        </b-input-group>
                        <br />
                        <b-form-checkbox switch v-model="numericalSCGen">
                          {{ numericalSCGen ? "生成" : "固定" }}
                        </b-form-checkbox>

                        <span
                          >就依照下列<b>{{
                            numericalSCGen ? "提示詞生成" : "文字進行"
                          }}</b
                          >回饋</span
                        >
                        <b-input-group>
                          <b-form-textarea
                            v-model="numericalSCString"
                            autocomplete="off"
                          ></b-form-textarea>
                        </b-input-group>
                        <b-button
                          variant="outline-success"
                          @click="addNumericalSC"
                          >新增</b-button
                        >

                        <p></p>
                        <b-input-group
                          prepend="Google Sheet ID"
                          autocomplete="off"
                        >
                          <b-form-input
                            v-model="numericalSCSheetID"
                          ></b-form-input>
                        </b-input-group>
                        <b-input-group
                          prepend="任務 iframe URL"
                          autocomplete="off"
                          class="mt-2"
                        >
                          <b-form-input
                            v-model.trim="normalizedNumericalTaskIframeUrl"
                          ></b-form-input>
                        </b-input-group>
                        <p>
                          可直接貼 Google Form 的 src 網址、分享網址或整段
                          iframe，系統會自動清理成可嵌入網址。
                        </p>
                        <b-button
                          variant="outline-success"
                          @click="onSubmitSheet"
                          >提交</b-button
                        >
                        <b-button
                          class="ml-2"
                          variant="outline-primary"
                          @click="openNumericalModePreview"
                        >
                          另開新分頁預覽
                        </b-button>
                        <p class="mt-2 mb-0 text-muted">
                          預覽會直接開啟目前 NPC
                          的數值模式成品頁，並帶入你尚未儲存的數值鷹架設定。
                        </p>
                        <p>按下提交按鈕後去抓特定的 Google Sheet 資料</p>

                        <b-badge
                          v-for="(item, index) in numericalSC"
                          :key="'numericalSC' + index"
                          href="#"
                          variant="success"
                          @click="removeNumericalSC(index)"
                          >當欄位在{{ item.position }} " {{ item.operator }} "
                          數值{{ item.number }}就以{{
                            item.gen ? "提示詞" : "文字"
                          }}{{ item.content
                          }}{{ item.gen ? "生成回饋" : "進行回饋" }}</b-badge
                        >
                        <br />
                      </b-card-body>
                    </b-card>
                  </b-card-body>
                </b-collapse>
              </b-card>

              <b-card no-body>
                <b-card-header v-b-toggle.voiceSetting>
                  <b-card-title>語音設定</b-card-title>
                </b-card-header>

                <b-collapse id="voiceSetting" accordion visible>
                  <b-card-body>
                    <b-card no-body>
                      <b-card-header class="bg-warning">
                        <b-card-title>
                          <b-row>
                            <b-col cols="5">語音設定 </b-col>
                            <b-col cols="5">
                              <b-form-checkbox switch v-model="voiceSwitch">
                                {{ voiceSwitch ? "啟用" : "關閉" }}
                              </b-form-checkbox>
                            </b-col>
                            <b-col cols="2">
                              <h5>
                                <b-badge variant="primary">限單人</b-badge>
                              </h5>
                            </b-col>
                          </b-row>
                        </b-card-title>
                      </b-card-header>
                      <b-card-body v-if="voiceSwitch">
                        <h6>選擇音色</h6>
                        <b-form-select
                          v-model="voice"
                          :options="voiceOptions"
                        ></b-form-select>
                        <b-form-input
                          class="mt-2"
                          v-model="voiceSampleText"
                          placeholder="輸入試聽內容（留空使用預設句子）"
                          autocomplete="off"
                        ></b-form-input>
                        <b-button
                          class="mt-2"
                          variant="info"
                          @click="playVoiceSample"
                          :disabled="!voice || voiceSampleLoading"
                        >
                          {{ voiceSampleLoading ? "播放中..." : "試聽" }}
                        </b-button>
                        <h6 class="mt-2">語氣提示（instructions）</h6>
                        <b-form-input
                          v-model="ttsInstructions"
                          placeholder="例：用自然、親切、清楚的語氣朗讀。"
                          autocomplete="off"
                        ></b-form-input>
                        <h6 class="mt-2">語速（speed）</h6>
                        <b-form-select
                          v-model="ttsSpeed"
                          :options="ttsSpeedOptions"
                        ></b-form-select>
                      </b-card-body>
                    </b-card>
                  </b-card-body>
                </b-collapse>
              </b-card>
              <b-toast
                title="測試區"
                :visible="
                  !demoMode &&
                  chatDataResults.length === 0 &&
                  !showAccessCodeModal
                "
                toaster="b-toaster-bottom-center"
                no-close-button
                no-auto-hide
                variant="info"
              >
                <b-input-group prepend="問題">
                  <b-input-group-prepend>
                    <b-button
                      class="speech-button"
                      :class="{ listening: isSpeechListening }"
                      variant="outline-secondary"
                      :title="speechButtonTitle"
                      :aria-label="speechButtonTitle"
                      @click="toggleSpeechRecognition"
                      :disabled="
                        !isSpeechRecognitionSupported ||
                        (!debug && isAiMessageLocked) ||
                        endGame
                      "
                    >
                      <b-icon
                        :icon="isSpeechListening ? 'pause-circle-fill' : 'mic'"
                      ></b-icon>
                    </b-button>
                  </b-input-group-prepend>
                  <textarea
                    v-model="chatContent"
                    class="form-control chat-input-autosize layout-chat-input"
                    :style="layoutChatInputStyle"
                    rows="1"
                    @input="resizeChatInput"
                    @focus="resizeChatInput"
                    @keydown.enter="sendMessage(true, $event)"
                    @compositionstart="isComposing = true"
                    @compositionend="isComposing = false"
                    autocomplete="off"
                    :disabled="(!debug && isAiMessageLocked) || endGame"
                    :placeholder="
                      askLimit > 0 && askLimitCal <= 0
                        ? '提問次數已用完（可發送給同儕）'
                        : '請輸入您的問題...'
                    "
                  ></textarea>
                </b-input-group>
                <b-button
                  class="layout-send-button"
                  variant="success"
                  :style="layoutButtonStyle"
                  @click="sendMessage(true)"
                  :disabled="isAiMessageLocked || endGame"
                  >送出 (NPC)</b-button
                >
                <b-button
                  v-if="debug"
                  variant="primary"
                  @click="sendMessage(false)"
                  :disabled="endGame"
                  >送出 (同儕)</b-button
                >
              </b-toast>
              <br />
            </b-col>
          </b-col>

          <!-- NPC 頭像區域 -->
          <b-col
            :cols="demoMode ? '6' : '3'"
            class="avatar-section"
            v-if="!isPeerOnlyMode && avatarUrl"
          >
            <div class="avatar-container">
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                :alt="showName + '的頭像'"
                class="npc-avatar"
                @error="onImageError"
              />
              <h6 class="text-center mt-2">{{ showName }}</h6>
            </div>
          </b-col>

          <b-col class="chatLayout">
            <h5 v-if="situationText.length > 0">
              <b-badge variant="info" class="layout-guide-badge">{{
                situationText
              }}</b-badge>
            </h5>
            <b-col
              cols="12"
              ref="chatRef"
              class="chatBox"
              :class="{ 'messenger-chat-box': layoutSetting.messenger_style }"
            >
              <p
                v-for="(item, index) in visibleChatLog"
                :key="index"
                class="animate__animated animate__fadeInLeft animate__faster"
                :class="messageRowClass(item)"
              >
                <b v-if="!layoutSetting.messenger_style"
                  >{{ item.userName == userName ? "我" : item.userName }}：</b
                >
                <span
                  v-else
                  class="messenger-message-name"
                  :class="{
                    'messenger-message-name-own': isOwnMessage(item),
                    'messenger-message-name-other': !isOwnMessage(item),
                  }"
                  >{{ item.userName }}</span
                >
                <!-- <span> : {{ item.content }}</span> -->
                <vue-typed-js
                  :strings="[item.content]"
                  :style="
                    layoutSetting.messenger_style
                      ? { display: 'block', width: '100%' }
                      : { display: 'inline' }
                  "
                  :showCursor="false"
                  :typeSpeed="typeSpeed"
                  @preStringTyped="handleTypingStart(item)"
                  @onComplete="handleTypingComplete(item)"
                >
                  <span class="typing" :class="messageBubbleClass(item)"></span>
                </vue-typed-js>
              </p>
              <transition
                enter-active-class="animate__animated animate__fadeInLeft"
                leave-active-class="animate__animated animate__fadeOutLeft"
              >
                <p v-if="!isPeerOnlyMode && waitAnimation" class="text-left">
                  <b class="float-left">{{ showName }}</b
                  >思考回應中... (若無回應，請換一句話來提問...)
                  <b-skeleton width="300px">hi</b-skeleton>
                </p>
              </transition>
              <transition
                enter-active-class="animate__animated animate__fadeInLeft"
                leave-active-class="animate__animated animate__fadeOutLeft"
              >
                <p
                  v-if="!isPeerOnlyMode && ttsLoading"
                  class="text-left text-secondary"
                >
                  <b-spinner small></b-spinner>
                  <span class="ml-2">語音回覆中...</span>
                </p>
              </transition>
              <b-card
                title="遊戲結束"
                bg-variant="danger"
                text-variant="white"
                v-if="endGame || endConTrigger === true"
              >
              </b-card>
              <b-card
                title="表現評估"
                bg-variant="primary"
                text-variant="white"
                v-if="
                  !isPeerOnlyMode &&
                  (endGame || endConTrigger === true) &&
                  (scaleSCSwitch == true || (manualEndGame && !hideRoomCode))
                "
              >
                <b-card-text class="text-left">
                  <span style="display: none">{{ scaleMessage() }}</span>
                  <div v-html="renderMarkdown(endResult)"></div>
                </b-card-text>
              </b-card>
              <br />
            </b-col>
          </b-col>

          <b-container class="fixed-bottom" v-if="demoMode && !endGame">
            <b-row class="justify-content-center">
              <b-col cols="12">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-button
                      class="speech-button"
                      :class="{ listening: isSpeechListening }"
                      variant="outline-secondary"
                      :title="speechButtonTitle"
                      :aria-label="speechButtonTitle"
                      @click="toggleSpeechRecognition"
                      :disabled="
                        !isSpeechRecognitionSupported ||
                        (hideRoomCode && isAiMessageLocked) ||
                        endGame
                      "
                    >
                      <b-icon
                        :icon="isSpeechListening ? 'pause-circle-fill' : 'mic'"
                      ></b-icon>
                    </b-button>
                  </b-input-group-prepend>
                  <textarea
                    v-model="chatContent"
                    class="form-control border border-dark chat-input-autosize layout-chat-input"
                    :style="layoutChatInputStyle"
                    rows="1"
                    @input="resizeChatInput"
                    @focus="resizeChatInput"
                    @keydown.enter="
                      sendMessage(isPeerOnlyMode ? false : hideRoomCode, $event)
                    "
                    @compositionstart="isComposing = true"
                    @compositionend="isComposing = false"
                    :disabled="
                      (!isPeerOnlyMode && hideRoomCode && isAiMessageLocked) ||
                      endGame
                    "
                    :placeholder="
                      endGame
                        ? '遊戲已結束'
                        : isPeerOnlyMode
                        ? '請輸入要傳送給組員的訊息...'
                        : askLimit > 0 && askLimitCal <= 0
                        ? 'NPC提問次數已用完（可發送給同儕）'
                        : '請輸入您的問題...'
                    "
                  ></textarea>
                  <b-input-group-append>
                    <b-button
                      v-if="hideRoomCode != true || isPeerOnlyMode"
                      class="layout-send-button"
                      variant="primary"
                      :style="layoutButtonStyle"
                      @click="sendMessage(false)"
                      :disabled="endGame"
                    >
                      <b-icon icon="arrow-right-circle"></b-icon>
                    </b-button>
                    <b-button
                      v-if="!isPeerOnlyMode"
                      class="layout-send-button"
                      variant="primary"
                      :style="layoutButtonStyle"
                      @click="sendMessage(true)"
                      :disabled="isAiMessageLocked || endGame"
                    >
                      <img
                        src="@/assets/toNPC.svg"
                        alt="送出給NPC"
                        class="peer-icon"
                      />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-row>
          </b-container>
          <b-modal
            ref="nameModal"
            id="nameModal"
            :visible="true"
            title="使用者名稱"
            hide-footer
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
          >
            <b-input-group>
              <b-form-input v-model="userName"></b-form-input>
              <b-input-group-append>
                <b-button variant="outline-success" @click="sendName"
                  >送出</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-modal>
        </b-row>

        <!-- 聊天數據查詢按鈕 -->
        <b-button
          v-if="!demoMode"
          variant="info"
          class="sidebar-btn"
          @click="showChatDataModal = true"
          v-b-tooltip.hover
          title="查詢聊天記錄"
        >
          <b-icon icon="search" aria-hidden="true"></b-icon>
        </b-button>

        <!--  新增角色清單按鈕 -->
        <b-button
          v-if="!demoMode"
          variant="info"
          class="sidebar-btn role-btn"
          @click="showRoleSidebar = true"
          v-b-tooltip.hover
          title="顯示所有角色"
        >
          <b-icon icon="people" aria-hidden="true"></b-icon>
        </b-button>

        <b-button
          v-if="!demoMode"
          variant="info"
          class="sidebar-btn access-code-btn"
          @click="showAccessCodeModal = true"
          v-b-tooltip.hover
          title="通行密鑰管理"
        >
          <b-icon icon="key" aria-hidden="true"></b-icon>
        </b-button>

        <b-button
          v-if="!demoMode && adminRole === 'super_admin'"
          variant="info"
          class="sidebar-btn admin-account-btn"
          @click="showAdminAccountModal = true"
          v-b-tooltip.hover
          title="管理員帳號管理"
        >
          <b-icon icon="person-plus" aria-hidden="true"></b-icon>
        </b-button>

        <!--  側邊欄顯示角色清單 -->
        <b-sidebar
          id="role-sidebar"
          title="角色清單"
          shadow
          bg-variant="light"
          :visible="showRoleSidebar"
          @hidden="showRoleSidebar = false"
        >
          <div v-if="npcOptions.length > 0" class="role-list">
            <ul class="list-unstyled mb-0">
              <li
                v-for="(opt, idx) in npcOptions"
                :key="idx"
                class="mb-2 border-bottom pb-1"
              >
                <b>{{ opt.text }}</b>
              </li>
            </ul>
          </div>
          <div v-else>
            <p class="text-muted">尚未載入角色資料</p>
          </div>
        </b-sidebar>
        <b-modal
          v-model="showAccessCodeModal"
          id="accessCodeModal"
          title="通行密鑰管理"
          size="lg"
          hide-footer
        >
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="對應 NPC 代碼 (game_id)">
                <b-dropdown
                  variant="outline-secondary"
                  class="w-100 npc-dropdown"
                  ref="accessCodeNpcDropdown"
                  :text="accessCodeSelectedNpcText || '請選擇 NPC'"
                >
                  <b-dropdown-form>
                    <b-form-input
                      v-model="accessCodeNpcSearch"
                      placeholder="輸入關鍵字搜尋..."
                      autocomplete="off"
                    />
                  </b-dropdown-form>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item
                    v-for="(opt, idx) in filteredAccessCodeNpcOptions"
                    :key="idx"
                    @click="selectAccessCodeNpc(opt)"
                  >
                    {{ opt.text }}
                  </b-dropdown-item>
                  <b-dropdown-text
                    v-if="filteredAccessCodeNpcOptions.length === 0"
                    class="text-muted"
                  >
                    沒有符合的角色
                  </b-dropdown-text>
                </b-dropdown>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="多 NPC 共用 ch（選填，可複選）">
                <b-dropdown
                  variant="outline-secondary"
                  class="w-100 npc-dropdown"
                  :text="accessCodeAllowedChText || '請選擇（可複選）'"
                >
                  <b-dropdown-form>
                    <b-form-input
                      v-model="accessCodeAllowedChSearch"
                      placeholder="輸入關鍵字搜尋..."
                      autocomplete="off"
                    />
                  </b-dropdown-form>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-form>
                    <div
                      v-for="(opt, idx) in filteredAccessCodeAllowedChOptions"
                      :key="idx"
                    >
                      <b-form-checkbox
                        v-model="accessCodeAllowedChSelected"
                        :value="opt.value"
                      >
                        {{ opt.text }}
                      </b-form-checkbox>
                    </div>
                    <div
                      v-if="filteredAccessCodeAllowedChOptions.length === 0"
                      class="text-muted small"
                    >
                      沒有符合的角色
                    </div>
                  </b-dropdown-form>
                </b-dropdown>
                <small class="text-muted d-block mt-1">
                  填入後此密鑰可跨多個 NPC 聊天室使用。留空表示僅限 game_id
                  指定的 NPC。
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="密鑰類型">
                <b-form-select
                  v-model="accessCodeForm.code_type"
                  :options="accessCodeTypeOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="一次新增數量">
                <b-form-input
                  v-model.number="accessCodeForm.quantity"
                  type="number"
                  min="1"
                  max="500"
                  placeholder="預設 1"
                ></b-form-input>
                <small class="text-muted d-block mt-1">
                  會用相同的 NPC、時間、類型與備註，一次建立多組密鑰。
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="開始時間">
                <b-form-input
                  v-model="accessCodeForm.start_time"
                  type="datetime-local"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="結束時間">
                <b-form-input
                  v-model="accessCodeForm.end_time"
                  type="datetime-local"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="備註">
            <b-form-input
              v-model.trim="accessCodeForm.note"
              placeholder="例如：測試用永久密鑰"
            ></b-form-input>
            <small class="text-muted d-block mt-2">
              一次性密鑰：在有效時間內只能成功登入一次，登入後即失效。<br />
              多次使用密鑰：在有效時間內可重複登入使用，不會因登入一次就失效。
            </small>
          </b-form-group>

          <b-button
            variant="success"
            class="mr-2"
            @click="createAccessCode"
            :disabled="isSavingAccessCode"
          >
            {{ isSavingAccessCode ? "新增中..." : "新增通行密鑰" }}
          </b-button>
          <b-button
            variant="outline-primary"
            @click="fetchAccessCodes"
            :disabled="isLoadingAccessCodes"
          >
            {{ isLoadingAccessCodes ? "更新中..." : "重新整理清單" }}
          </b-button>
          <b-button variant="outline-info" class="ml-2" @click="copyNPCUrl">
            網址生成
          </b-button>
          <b-button
            variant="outline-success"
            class="ml-2"
            @click="copyNPCUrl(true)"
          >
            無 GAI 共同聊天室網址
          </b-button>
          <b-button
            variant="outline-info"
            class="ml-2"
            @click="showDocUrlRoomInput = !showDocUrlRoomInput"
          >
            文件共編網址生成
          </b-button>

          <b-form-group
            v-if="showDocUrlRoomInput"
            label="請輸入房間 ID（不可以 solo 開頭）"
            class="mt-2"
          >
            <b-input-group>
              <b-form-input
                v-model.trim="docUrlRoomId"
                placeholder="例如：room123"
              ></b-form-input>
              <b-input-group-append>
                <b-button variant="success" @click="copyDocUrl">
                  生成網址
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-button
            variant="outline-warning"
            class="ml-2"
            @click="copyNumericalModeUrl"
          >
            數值模式網址生成
          </b-button>

          <hr />

          <div v-if="accessCodeList.length > 0" class="access-code-list-scroll">
            <div
              v-for="item in accessCodeList"
              :key="item.id"
              class="border rounded p-2 mb-2"
            >
              <div><b>ID：</b>{{ item.id }}</div>
              <div><b>密鑰：</b>{{ item.code }}</div>
              <div><b>NPC：</b>{{ item.game_id }}</div>
              <div>
                <b>類型：</b
                >{{ item.code_type === "one_time" ? "一次性" : "多次使用" }}
              </div>
              <div><b>開始：</b>{{ item.start_time }}</div>
              <div><b>結束：</b>{{ item.end_time }}</div>
              <div><b>備註：</b>{{ item.note || "-" }}</div>
              <div class="mt-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-2"
                  @click="copyAccessCode(item.code)"
                >
                  複製密鑰
                </b-button>
                <b-button
                  size="sm"
                  variant="warning"
                  class="mr-2"
                  @click="regenerateAccessCode(item.id)"
                >
                  重新生成
                </b-button>
                <b-button
                  size="sm"
                  variant="danger"
                  @click="deleteAccessCode(item.id)"
                >
                  刪除
                </b-button>
              </div>
            </div>
          </div>
          <p v-else class="text-muted mb-0">目前沒有通行密鑰資料</p>
        </b-modal>

        <b-modal
          v-model="showAdminAccountModal"
          id="adminAccountModal"
          title="管理員帳號管理"
          size="lg"
          hide-footer
        >
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="登入帳號">
                <b-form-input
                  v-model.trim="adminAccountForm.username"
                  placeholder="例如：admin02"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="顯示名稱">
                <b-form-input
                  v-model.trim="adminAccountForm.admin_name"
                  placeholder="例如：助教 A"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="登入密碼">
                <b-form-input
                  v-model="adminAccountForm.password"
                  type="password"
                  placeholder="請輸入密碼"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="角色">
                <b-form-select
                  v-model="adminAccountForm.role"
                  :options="adminRoleOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-button
            variant="success"
            class="mr-2"
            @click="createAdminAccount"
            :disabled="isSavingAdminAccount"
          >
            {{ isSavingAdminAccount ? "建立中..." : "建立管理員帳號" }}
          </b-button>
          <b-button
            variant="outline-primary"
            @click="fetchAdminAccounts"
            :disabled="isLoadingAdminAccounts"
          >
            {{ isLoadingAdminAccounts ? "更新中..." : "重新整理清單" }}
          </b-button>

          <hr />

          <div
            v-if="adminAccountList.length > 0"
            class="access-code-list-scroll"
          >
            <div
              v-for="item in adminAccountList"
              :key="item.id"
              class="border rounded p-2 mb-2"
            >
              <div><b>ID：</b>{{ item.id }}</div>
              <div><b>帳號：</b>{{ item.username }}</div>
              <div><b>名稱：</b>{{ item.admin_name }}</div>
              <div>
                <b>角色：</b
                >{{
                  item.role === "super_admin"
                    ? "總管理者"
                    : item.role === "keyManager"
                    ? "金鑰管理者"
                    : "檢視者"
                }}
              </div>
              <div><b>建立時間：</b>{{ item.created_at }}</div>
            </div>
          </div>
          <p v-else class="text-muted mb-0">目前沒有管理員帳號資料</p>
        </b-modal>

        <!-- 聊天數據查詢模態框 -->
        <b-modal
          v-model="showChatDataModal"
          id="chatDataModal"
          title="聊天數據查詢"
          size="lg"
          hide-footer
          @hidden="clearChatDataQuery"
        >
          <!--提交時重置頁碼至第一頁並查詢-->
          <b-form
            @submit.prevent="
              () => {
                currentPage = 1;
                searchChatData(true);
              }
            "
          >
            <b-row>
              <b-col cols="6">
                <b-form-group label="角色名稱(gameId)">
                  <b-dropdown
                    variant="outline-secondary"
                    class="w-100 npc-dropdown"
                    ref="npcDropdown"
                    :text="selectedNpcText || '請輸入或選擇角色'"
                  >
                    <b-dropdown-form>
                      <b-form-input
                        v-model="npcSearch"
                        placeholder="輸入關鍵字搜尋..."
                        autocomplete="off"
                      />
                    </b-dropdown-form>

                    <b-dropdown-divider></b-dropdown-divider>

                    <b-dropdown-item
                      v-for="(opt, idx) in filteredNpcOptions"
                      :key="idx"
                      @click="selectNpc(opt)"
                    >
                      {{ opt.text }}
                    </b-dropdown-item>

                    <b-dropdown-text
                      v-if="filteredNpcOptions.length === 0"
                      class="text-muted"
                    >
                      沒有符合的角色
                    </b-dropdown-text>
                  </b-dropdown>
                </b-form-group>
              </b-col>

              <b-col cols="6">
                <b-form-group label="使用者名稱 (userId)">
                  <b-form-input
                    v-model="chatDataQuery.userId"
                    placeholder="例如: student01"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="6">
                <b-form-group label="開始時間">
                  <b-form-input
                    v-model="chatDataQuery.startTime"
                    type="datetime-local"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="結束時間">
                  <b-form-input
                    v-model="chatDataQuery.endTime"
                    type="datetime-local"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col cols="6">
                <b-form-group label="輸入金額">
                  <b-input-group append="1M tokens / 美金">
                    <b-form-input
                      v-model="costInputAmount"
                      type="number"
                      min="0"
                      step="0.000001"
                      placeholder="請輸入每 1M tokens 單價"
                    ></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="輸出金額">
                  <b-input-group append="1M tokens / 美金">
                    <b-form-input
                      v-model="costOutputAmount"
                      type="number"
                      min="0"
                      step="0.000001"
                      placeholder="請輸入每 1M tokens 單價"
                    ></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col>
                <b-button
                  type="submit"
                  variant="primary"
                  :disabled="isLoadingChatData"
                  class="mr-2"
                >
                  <b-spinner small v-if="isLoadingChatData"></b-spinner>
                  {{ isLoadingChatData ? "查詢中..." : "查詢" }}
                </b-button>
                <b-button variant="secondary" @click="clearChatDataQuery">
                  清空
                </b-button>
                <!-- CSV 匯出按鈕 -->
                <b-button
                  variant="info"
                  @click="exportChatData($event)"
                  :disabled="total === 0"
                  class="ml-2"
                >
                  匯出 CSV(原始資料)
                </b-button>

                <!-- Excel 匯出按鈕-->
                <b-button
                  variant="success"
                  @click="exportExcelData($event)"
                  :disabled="total === 0"
                  class="ml-2"
                >
                  匯出 EXCEL(整理後資料)
                </b-button>

                <!-- 試算費用按鈕 -->
                <b-button
                  variant="warning"
                  @click="calculateManualCost"
                  class="ml-2"
                >
                  試算
                </b-button>

                <span class="ml-3">
                  費用參考連結：
                  <a
                    href="https://developers.openai.com/api/docs/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    >https://developers.openai.com/api/docs/pricing</a
                  >
                </span>
                <span v-if="costCalculated" class="ml-3">
                  試算結果：{{ costTotalAmount }} 美金
                </span>
                <div class="text-danger mt-2">
                  試算僅供參考，實際請查詢 OpenAI 帳單。
                </div>
              </b-col>
            </b-row>
          </b-form>

          <b-card class="mt-4" v-if="chatDataResults.length > 0">
            <b-card-title>查詢結果（共 {{ total }} 筆）</b-card-title>

            <b-table
              :items="chatDataResults"
              :fields="chatDataFields"
              :current-page="currentPage"
              :per-page="pageSize"
              striped
              hover
              small
              responsive
            >
              <template #cell(timestamp)="ctx">
                {{ formatTimestampISO(ctx.value) }}
              </template>

              <template #cell(chatContent)="ctx">
                <span :title="ctx.value">
                  {{
                    ctx.value && ctx.value.length > 50
                      ? ctx.value.slice(0, 50) + "..."
                      : ctx.value
                  }}
                </span>
              </template>

              <template #cell(tokenP)="ctx">
                {{ parseTokenCal(ctx.item.tokenCal, "p") }}
              </template>

              <template #cell(tokenT)="ctx">
                {{ parseTokenCal(ctx.item.tokenCal, "t") }}
              </template>
            </b-table>

            <!-- 分頁：用 total 與 pageSize，切頁時重新查 -->
            <b-pagination
              v-if="total > pageSize"
              v-model="currentPage"
              :total-rows="total"
              :per-page="pageSize"
              :align="'center'"
              @input="handlePageChange"
            />
          </b-card>

          <b-alert
            v-else-if="!isLoadingChatData && chatDataSearched"
            variant="warning"
            show
            class="mt-4"
          >
            沒有找到符合條件的聊天記錄
          </b-alert>
        </b-modal>
      </b-container>
    </div>
  </div>
</template>

<script>
import axios from "axios";
// import { BIconTrashFill } from "bootstrap-vue";
import Swal from "sweetalert2";
import "animate.css";
import * as XLSX from "xlsx";
//import { is } from "core-js/core/object";
import MarkdownIt from "markdown-it";
import OpenCC from "opencc-js";
import MultiNPC from "./components/MultiNPC.vue";
import NumericalNPC from "./components/NumericalNPC.vue";
import DocEditor from "./components/DocEditor.vue";

//開發測試專用，正式環境不會顯示
const DEV_BYPASS_TOKEN = "dev-bypass-token";

// 本機數值模式測試開關：
// - true: 允許用 mock NPC 測數值模式，不依賴資料庫 NPC 資料。
// - false: 一律走正式 NPC 載入流程（getGPTData / getGPTDataCheck）。
const ENABLE_NUMERICAL_LOCAL_TEST = false;
const NUMERICAL_ROUTE_QUERY_STORAGE_KEY = "numerical-route-query";
const NUMERICAL_PREVIEW_STORAGE_KEY = "numerical-preview";

const convertSpeechToTaiwanTraditional = OpenCC.Converter({
  from: "cn",
  to: "twp",
});

export default {
  name: "App",
  components: { MultiNPC, NumericalNPC, DocEditor },
  data() {
    return {
      render: true,
      demoMode: true,
      demoChs: [],
      multiCh: [],
      loginCh: "",
      loginRoom: "",
      initialRouteQuery: {},
      debug: false,
      code: "",
      username: "",
      password: "",
      error: "",
      studentGameId: "",
      chatLog: [],
      lastAutoScrolledChatLogLength: 0,
      npcOptions: [], // 下拉選單的選項
      isLoadingNPCs: false, // 載入 NPC 清單時顯示 loading 狀態
      npcSearch: "",
      showRoleSidebar: false, // 控制角色清單 sidebar 開關
      accessCodeNpcSearch: "",
      accessCodeSelectedNpcText: "",
      accessCodeAllowedChSearch: "",
      accessCodeAllowedChSelected: [],
      showDocUrlRoomInput: false,
      docUrlRoomId: "",
      apiNetWork:
        process.env.VUE_APP_AZURE_API_BASE ||
        "https://megachatfunction.azurewebsites.net/api/",
      apiNetWork2: process.env.VUE_APP_API_BASE || "https://api.ntustmeg.com",
      apiNetWorkPay: process.env.VUE_APP_PAY || process.env.pay || "",
      sheetApiBase:
        process.env.VUE_APP_SHEET_API_BASE ||
        process.env.VUE_APP_API_BASE ||
        "https://api.ntustmeg.com",
      idleTimer: null,
      heartbeatInterval: null,
      character: "",
      knowledge: "",
      chatContent: "",
      selected: "",
      selectedNpcText: "",
      callBackTime: 0,
      temperature: 1,
      maxTokens: 800,
      preTextInput: "",
      replaceTextInput: "",
      replaceRule: [],
      name: null,
      showName: "",
      userName: "",
      gameId: "test",
      startDate: null,
      startTime: null,
      startDateTime: null,
      endDate: null,
      endTime: null,
      endDateTime: null,
      initSC: [],
      initSCcontent: "",
      initStart: 0,
      initPro: 0,
      initRandom: false,
      initState: 0,
      initProShow: 0,
      initSwitch: false,
      chatInitSC: [],
      firstTalk: "",
      situationText: "",
      progressiveSC: [],
      progressiveSCDb: [],
      progressiveSCConcept: "",
      progressiveSCID: Math.floor(Math.random() * 1000000) + Date.now(),
      progressiveSCQ: 0,
      progressiveSCA: 0,
      progressiveSCString: "",
      progressiveSCSwitch: false,
      isConnected: false,
      roomCode: "",
      hideRoomCode: false,
      hintName: "[提示]",
      hintName2: "[提示]",
      hintName3: "[提示]",
      isComposing: false,
      speechRecognition: null,
      isSpeechListening: false,
      speechFinalTranscript: "",
      speechBaseContent: "",
      speechError: "",
      speechLang: "zh-TW",
      autoSC: 0,
      autoSCSwitch: false,
      autoComment: 0,
      autoSCProbability: 0,
      traceback: 10,
      voiceSwitch: false,
      voiceOptions: [
        null,
        "alloy",
        "ash",
        "ballad",
        "coral",
        "echo",
        "fable",
        "nova",
        "onyx",
        "sage",
        "shimmer",
        "verse",
      ],
      voice: null,
      voiceSampleLoading: false,
      voiceSampleText: "",
      ttsInstructions: "",
      ttsSpeed: null,
      ttsSpeedOptions: [
        { value: null, text: "預設語速" },
        { value: 0.5, text: "0.5x（最慢）" },
        { value: 0.75, text: "0.75x（慢）" },
        { value: 1.0, text: "1.0x（正常）" },
        { value: 1.25, text: "1.25x（稍快）" },
        { value: 1.5, text: "1.5x（快）" },
        { value: 1.75, text: "1.75x（很快）" },
        { value: 2.0, text: "2.0x（最快）" },
      ],
      autoMessagePrompt:
        "請根據目前討論紀錄，依據討論的內容與討論的目的發表你的看法",
      waitAnimation: false,
      awaitingNpcReply: false,
      ttsLoading: false,
      typingActiveMessageId: null,
      typingLockIdCounter: 0,
      timerId: null,
      beforeTimerId: null,
      progessiveSCGen: false,
      askLimit: 0,
      askLimitCal: 0,
      scaleSC: "",
      scaleSCSwitch: false,
      endResult: "",
      endGame: false,
      manualEndGame: false, // 追踪是否是手動結束遊戲
      gameStartTime: null,
      open: true,
      challengeMode: false,
      challengeCal: 0,
      endCon: [],
      endConDb: [],
      endConConcept: "",
      endConQ: 0,
      endConA: 0,
      endConString: "",
      endConSwitch: false,
      endConTrigger: false,
      typeSpeed: 0,
      showChatDataModal: false,
      isLoadingChatData: false,
      chatDataSearched: false,
      costInputAmount: "",
      costOutputAmount: "",
      costTotalAmount: "",
      costCalculated: false,
      stepScaleSCSwitch: false,
      stepScaleSC: "",
      stepScaleSCStep: 0,
      hintName4: "[提示]",
      suppressNextDeduct: false,
      pendingStepScale: null,
      userTurnCount: 0,
      lastStepScaleFiredAt: 0,
      avatarUrl: "",
      recordSC: "",
      recordSCStep: 0,
      lastRecordFiredAt: 0,
      lastSenderName: "", // 多人模式下追蹤最後發言的使用者，避免重複寫入 DB
      showHiddenAnalysis: false,
      showAccessCodeModal: false,
      analysisLogs: [],
      scaleFailed: false,
      md: new MarkdownIt({ breaks: true }),
      roomPlayersAskLimit: {}, // 追蹤房間內所有玩家的提問次數
      accessCodeList: [],
      isLoadingAccessCodes: false,
      isSavingAccessCode: false,
      accessCodeTypeOptions: [
        { value: "permanent", text: "多次使用" },
        { value: "one_time", text: "一次性" },
      ],

      adminRole: localStorage.getItem("admin_role") || "",
      showAdminAccountModal: false,
      adminAccountList: [],
      isLoadingAdminAccounts: false,
      isSavingAdminAccount: false,
      adminRoleOptions: [
        { value: "super_admin", text: "總管理者" },
        { value: "viewer", text: "檢視者" },
        { value: "keyManager", text: "金鑰管理者" },
      ],
      adminAccountForm: {
        username: "",
        password: "",
        admin_name: "",
        role: "viewer",
      },

      accessCodeForm: {
        game_id: "",
        allowed_ch: "",
        start_time: "",
        end_time: "",
        code_type: "permanent",
        note: "",
        quantity: 1,
      },
      layoutTextSizeOptions: [
        { value: "small", text: "小" },
        { value: "medium", text: "中" },
        { value: "large", text: "大" },
        { value: "xlarge", text: "極大" },
      ],
      layoutSetting: {
        text_size_level: "medium",
        text_color: "#2c3e50",
        background_color: "#ffffff",
        input_text_color: "#2c3e50",
        input_background_color: "#ffffff",
        button_color: "#007bff",
        ask_limit_background_color: "#007bff",
        guide_background_color: "#17a2b8",
        messenger_style: false,
        messenger_other_bubble_color: "#f0f0f0",
        messenger_own_bubble_color: "#2f6bff",
      },

      // 查詢條件
      currentPage: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,

      chatDataQuery: { gameId: "", userId: "", startTime: "", endTime: "" },

      // 後端回在 data 陣列裡
      chatDataResults: [],

      chatDataFields: [
        { key: "timestamp", label: "時間", sortable: true },
        { key: "gameId", label: "GameID", sortable: true },
        { key: "userId", label: "UserID", sortable: true },
        { key: "roomId", label: "Room", sortable: true },
        { key: "chatName", label: "發言者" },
        { key: "chatContent", label: "對話內容" },
        { key: "tokenP", label: "詢問Token", sortable: true },
        { key: "tokenT", label: "回答Token", sortable: true },
      ],
      //數值鷹架變數
      numericalSCSwitch: false,
      hintName5: "[提示]",
      numericalTaskIframeUrl: "",
      numericalPlayerCode: "",
      numericalResolvedSheetUrl: "",
      numericalNameSubmitted: false,
      isResolvingNumericalPlayerSheet: false,
      numericalSCGen: false,
      numericalSCPosition: "",
      numericalSCOperator: ">",
      numericalSCNumber: 0,
      numericalCString: "",
      numericalSC: [],
      numericalSCDb: [],
      numericalSCID: Math.floor(Math.random() * 1000000) + Date.now(),
      numericalSCString: "",
      numericalSCSheetID: "",
      numericalSCSheetRange: "",
      numericalSheetSnapshot: {},
      numericalSheetChanges: [],
      isLoadingNumericalSheet: false,
      isLoadingNumericalBaseline: false,
      numericalBaselineReady: false,
      numericalBaselineError: "",
      numericalSheetSubmitMessage: "",

      // 同步文件模式
      loginDoc: false,
      currentDocContent: "",
      hocuspocusUrl:
        process.env.VUE_APP_HOCUSPOCUS_URL ||
        "wss://api.ntustmeg.com/hocuspocus",

      // 階段式文件回饋鷹架
      docFeedbackSwitch: false,
      docFeedbackN: 10,
      docFeedbackPrompt: "",
    };
  },
  computed: {
    numericalBaselineStatusMessage() {
      if (!this.isNumericalMode) return "";
      if (this.isLoadingNumericalBaseline) {
        return "正在載入管理者設定的初始資料…";
      }
      if (this.numericalBaselineError) return this.numericalBaselineError;
      if (this.numericalSheetSubmitMessage) {
        return this.numericalSheetSubmitMessage;
      }
      if (this.numericalBaselineReady) {
        return "初始資料已載入，可以提交試算表。";
      }
      return "初始資料尚未載入。";
    },
    devBypassEnabled() {
      return (
        process.env.NODE_ENV !== "production" &&
        process.env.VUE_APP_DEV_BYPASS_LOGIN === "true"
      );
    },
    //開發測試專用，正式環境不會顯示
    isDevBypassSession() {
      return (
        this.devBypassEnabled &&
        localStorage.getItem("session_token") === DEV_BYPASS_TOKEN
      );
    },
    isNumericalMode() {
      return (
        String(this.$route?.query?.mode || "").toLowerCase() === "numerical"
      );
    },
    isNumericalPreviewRoute() {
      return (
        this.isNumericalMode &&
        Boolean(String(this.$route?.query?.previewKey || "").trim())
      );
    },
    requiresNumericalPlayerAssignment() {
      return this.isNumericalMode && !this.hasResolvedNumericalPlayerSheet;
    },
    hasResolvedNumericalPlayerSheet() {
      const sheetId = String(
        this.numericalSCSheetID || this.$route?.query?.sheetId || ""
      ).trim();
      return Boolean(sheetId);
    },
    isNumericalMockMode() {
      return (
        this.isNumericalMode &&
        this.devBypassEnabled &&
        ENABLE_NUMERICAL_LOCAL_TEST &&
        String(this.$route?.query?.mockNpc || "") === "1"
      );
    },
    numericalModeSheetUrl() {
      const explicitUrl = String(this.$route?.query?.sheetUrl || "").trim();
      if (explicitUrl) return explicitUrl;

      const sheetId = String(
        this.numericalSCSheetID || this.$route?.query?.sheetId || ""
      ).trim();
      if (!sheetId) return "";

      return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(
        sheetId
      )}/edit`;
    },
    numericalModeSheetEditUrl() {
      const explicitUrl = String(this.$route?.query?.sheetUrl || "").trim();
      if (explicitUrl) return explicitUrl;

      const sheetId = String(
        this.numericalSCSheetID || this.$route?.query?.sheetId || ""
      ).trim();
      if (!sheetId) return "";

      return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(
        sheetId
      )}/edit`;
    },
    /////
    // ref=noai 是純同儕共同聊天室：保留既有房間廣播，只關閉所有 GAI 入口。
    isPeerOnlyMode() {
      return String(this.$route?.query?.ref || "").toLowerCase() === "noai";
    },
    isChatRoute() {
      const path = this.$route?.path || "/login";
      return path === "/chat";
    },
    // 即時依照 npcSearch 過濾選項
    filteredNpcOptions() {
      if (!this.npcSearch) return this.npcOptions; // 沒輸入時顯示全部
      const keyword = this.npcSearch.toLowerCase();
      return this.npcOptions.filter(
        (opt) =>
          opt.text.toLowerCase().includes(keyword) || // 比對顯示文字
          String(opt.value).toLowerCase().includes(keyword) // 比對代碼
      );
    },
    //通行密鑰管理game_id下拉過濾
    filteredAccessCodeNpcOptions() {
      if (!this.accessCodeNpcSearch) return this.npcOptions;
      const keyword = this.accessCodeNpcSearch.toLowerCase();
      return this.npcOptions.filter(
        (opt) =>
          opt.text.toLowerCase().includes(keyword) ||
          String(opt.value).toLowerCase().includes(keyword)
      );
    },
    //allowed_ch 多選下拉過濾
    filteredAccessCodeAllowedChOptions() {
      if (!this.accessCodeAllowedChSearch) return this.npcOptions;
      const keyword = this.accessCodeAllowedChSearch.toLowerCase();
      return this.npcOptions.filter(
        (opt) =>
          opt.text.toLowerCase().includes(keyword) ||
          String(opt.value).toLowerCase().includes(keyword)
      );
    },
    accessCodeAllowedChText() {
      return this.accessCodeAllowedChSelected.join(", ");
    },

    // 回傳不含 hidden 的 chatLog（方便 template 使用）
    visibleChatLog() {
      return Array.isArray(this.chatLog)
        ? this.chatLog.filter(
            (i) => !i.hidden && (!this.isPeerOnlyMode || i.role === "user")
          )
        : [];
    },
    isTypingEffectActive() {
      return this.typeSpeed > 0 && this.typingActiveMessageId !== null;
    },
    isAiMessageLocked() {
      return this.awaitingNpcReply || this.isTypingEffectActive;
    },
    isSpeechRecognitionSupported() {
      if (typeof window === "undefined") return false;
      return Boolean(
        window.SpeechRecognition || window.webkitSpeechRecognition
      );
    },
    speechButtonTitle() {
      if (!this.isSpeechRecognitionSupported) {
        return "此瀏覽器不支援語音輸入";
      }
      if (this.speechError) return this.speechError;
      return this.isSpeechListening ? "停止語音輸入" : "開始語音輸入";
    },
    layoutCssVars() {
      const fontSizeMap = {
        small: "14px",
        medium: "16px",
        large: "20px",
        xlarge: "26px",
      };

      return {
        "--chat-font-size":
          fontSizeMap[this.layoutSetting.text_size_level] || "16px",
        "--chat-text-color": this.layoutSetting.text_color || "#2c3e50",
        "--chat-bg-color": this.layoutSetting.background_color || "#ffffff",
        "--chat-input-text-color":
          this.layoutSetting.input_text_color || "#2c3e50",
        "--chat-input-bg-color":
          this.layoutSetting.input_background_color || "#ffffff",
        "--chat-button-color": this.layoutSetting.button_color || "#007bff",
        "--chat-ask-limit-bg-color":
          this.layoutSetting.ask_limit_background_color || "#007bff",
        "--chat-guide-bg-color":
          this.layoutSetting.guide_background_color || "#17a2b8",
        "--messenger-other-bubble-color":
          this.layoutSetting.messenger_other_bubble_color || "#f0f0f0",
        "--messenger-own-bubble-color":
          this.layoutSetting.messenger_own_bubble_color || "#2f6bff",
      };
    },
    layoutChatInputStyle() {
      return {
        color: this.layoutSetting.input_text_color || "#2c3e50",
        backgroundColor: this.layoutSetting.input_background_color || "#ffffff",
        "--chat-input-text-color":
          this.layoutSetting.input_text_color || "#2c3e50",
      };
    },
    layoutButtonStyle() {
      const color = this.layoutSetting.button_color || "#007bff";
      return {
        backgroundColor: color,
        borderColor: color,
      };
    },
    normalizedNumericalTaskIframeUrl: {
      get() {
        return this.normalizeNumericalTaskIframeUrl(
          this.numericalTaskIframeUrl
        );
      },
      set(value) {
        this.handleNumericalTaskIframeInput(value);
      },
    },
    isDocMode() {
      return (
        this.loginDoc && this.loginRoom && !this.loginRoom.startsWith("solo")
      );
    },
  },
  methods: {
    isOwnMessage(item) {
      return item && item.userName === this.userName;
    },
    messageRowClass(item) {
      if (!this.layoutSetting.messenger_style) return "text-left";
      return {
        "messenger-message-row": true,
        "messenger-message-row-own": this.isOwnMessage(item),
        "messenger-message-row-other": !this.isOwnMessage(item),
      };
    },
    messageBubbleClass(item) {
      if (!this.layoutSetting.messenger_style) return "";
      return {
        "messenger-message-bubble": true,
        "messenger-message-bubble-own": this.isOwnMessage(item),
        "messenger-message-bubble-other": !this.isOwnMessage(item),
      };
    },
    normalizeNumericalTaskIframeUrl(value) {
      const rawValue = String(value || "").trim();
      if (!rawValue) return "";

      const iframeSrcMatch = rawValue.match(
        /<iframe[^>]*\ssrc=["']([^"']+)["']/i
      );
      if (iframeSrcMatch && iframeSrcMatch[1]) {
        return this.normalizeNumericalTaskIframeUrl(iframeSrcMatch[1].trim());
      }

      try {
        const parsedUrl = new URL(rawValue);
        const hostname = parsedUrl.hostname.toLowerCase();
        const pathname = parsedUrl.pathname;

        if (hostname === "docs.google.com") {
          parsedUrl.hash = "";
        }

        if (hostname === "docs.google.com" && pathname.includes("/forms/")) {
          if (pathname.endsWith("/formResponse")) {
            parsedUrl.pathname = pathname.replace(
              /\/formResponse$/i,
              "/viewform"
            );
          }

          if (pathname.endsWith("/edit")) {
            parsedUrl.pathname = pathname.replace(/\/edit$/i, "/viewform");
          }

          if (parsedUrl.pathname.endsWith("/viewform")) {
            const keepParams = new URLSearchParams();

            Array.from(parsedUrl.searchParams.entries()).forEach(
              ([key, paramValue]) => {
                if (key.startsWith("entry.")) {
                  keepParams.set(key, paramValue);
                }
              }
            );

            keepParams.set("embedded", "true");
            parsedUrl.search = keepParams.toString();
            return parsedUrl.toString();
          }
        }
      } catch (error) {
        // 保留原始值，讓非 URL 類型輸入仍可走原本流程。
      }

      return rawValue;
    },
    handleNumericalTaskIframeInput(value) {
      this.numericalTaskIframeUrl = this.normalizeNumericalTaskIframeUrl(value);
    },
    buildNumericalPreviewNpcPayload() {
      return {
        character: String(this.character || ""),
        knowledge: String(this.knowledge || ""),
        firstTalk: String(this.firstTalk || ""),
        avatar: String(this.avatarUrl || ""),
        temperature: Number(this.temperature),
        maxTokens: Number(this.maxTokens),
      };
    },
    buildNumericalPreviewPayload() {
      const npcCode = String(
        this.name || this.loginCh || this.$route?.query?.ch || ""
      ).trim();

      return {
        name: npcCode,
        showName: String(this.showName || "").trim(),
        situationText: String(this.situationText || "").trim(),
        ...this.buildNumericalPreviewNpcPayload(),
        ...this.buildExtendedNumericalNpcPayload(),
      };
    },
    persistNumericalPreviewPayload(payload = {}) {
      const previewKey = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

      localStorage.setItem(
        `${NUMERICAL_PREVIEW_STORAGE_KEY}:${previewKey}`,
        JSON.stringify(payload)
      );

      return previewKey;
    },
    getNumericalPreviewPayload(previewKey = "") {
      const normalizedKey = String(previewKey || "").trim();
      if (!normalizedKey) return null;

      try {
        return JSON.parse(
          localStorage.getItem(
            `${NUMERICAL_PREVIEW_STORAGE_KEY}:${normalizedKey}`
          ) || "null"
        );
      } catch (error) {
        console.warn("數值模式預覽資料解析失敗:", error);
        return null;
      }
    },
    applyNumericalPreviewPayload(payload = {}) {
      if (!payload || typeof payload !== "object") {
        return;
      }

      if (payload.name) {
        this.name = String(payload.name).trim();
      }
      if (payload.showName !== undefined) {
        this.showName = String(payload.showName || "").trim();
      }
      if (payload.situationText !== undefined) {
        this.situationText = String(payload.situationText || "").trim();
      }
      if (payload.character !== undefined) {
        this.character = String(payload.character || "");
      }
      if (payload.knowledge !== undefined) {
        this.knowledge = String(payload.knowledge || "");
      }
      if (payload.firstTalk !== undefined) {
        this.firstTalk = String(payload.firstTalk || "");
      }
      if (payload.avatar !== undefined) {
        this.avatarUrl = String(payload.avatar || "");
      }
      if (
        payload.temperature !== undefined &&
        Number.isFinite(Number(payload.temperature))
      ) {
        this.temperature = Number(payload.temperature);
      }
      if (
        payload.maxTokens !== undefined &&
        Number.isFinite(Number(payload.maxTokens))
      ) {
        this.maxTokens = Number(payload.maxTokens);
      }

      this.applyExtendedNumericalNpcPayload(payload);
    },
    applyNumericalPreviewPayloadIfNeeded(npcCode = "") {
      if (!this.isNumericalMode) {
        return;
      }

      const previewKey = String(this.$route?.query?.previewKey || "").trim();
      if (!previewKey) {
        return;
      }

      const payload = this.getNumericalPreviewPayload(previewKey);
      if (!payload) {
        return;
      }

      const routeNpcCode = String(
        npcCode || this.$route?.query?.ch || ""
      ).trim();
      const payloadNpcCode = String(payload.name || "").trim();

      if (payloadNpcCode && routeNpcCode && payloadNpcCode !== routeNpcCode) {
        return;
      }

      this.applyNumericalPreviewPayload(payload);
      this.$nextTick(() => {
        this.loadNumericalPreviewBaseline();
      });
    },
    openNumericalModePreview() {
      const npcCode = String(
        this.name || this.loginCh || this.$route?.query?.ch || ""
      ).trim();

      if (!npcCode) {
        alert("請先選擇或建立 NPC，再預覽數值模式");
        return;
      }

      const previewPayload = this.buildNumericalPreviewPayload();
      const previewKey = this.persistNumericalPreviewPayload(previewPayload);
      const params = new URLSearchParams();

      params.set("mode", "numerical");
      params.set("ch", npcCode);
      params.set("previewKey", previewKey);

      const room = String(
        this.roomCode || this.loginRoom || this.$route?.query?.room || ""
      ).trim();
      if (room) {
        params.set("room", room);
      }

      const previewUrl = `${window.location.origin}${
        window.location.pathname
      }#/chat?${params.toString()}`;
      const previewWindow = window.open(previewUrl, "_blank");

      if (!previewWindow) {
        prompt("請手動開啟以下數值模式預覽網址：", previewUrl);
      }
    },
    applyNumericalMockNpc(ch) {
      // 本機測試專用：保留正式版 getGPTData 流程，不碰後端資料。
      const npcCode = String(ch || "mocknpc").trim() || "mocknpc";
      this.name = npcCode;
      this.showName = npcCode;
      this.character = "你是本機數值模式測試用 NPC。";
      this.knowledge =
        "此角色不依賴資料庫，用於驗證數值模式、Sheet API 與前端流程。";
      this.firstTalk = `你好，我是 ${npcCode}。`;
      this.open = true;
      this.temperature = 1;
      this.maxTokens = 800;
      this.avatarUrl = "";
    },

    buildRouteQuery(overrides = {}) {
      let preservedQuery = {};
      try {
        preservedQuery = JSON.parse(
          sessionStorage.getItem(NUMERICAL_ROUTE_QUERY_STORAGE_KEY) || "{}"
        );
      } catch (error) {
        preservedQuery = {};
      }

      const baseQuery = {
        ...preservedQuery,
        ...(this.initialRouteQuery || {}),
        ...(this.$route?.query || {}),
      };
      const nextQuery = { ...baseQuery, ...overrides };

      Object.keys(nextQuery).forEach((key) => {
        if (
          nextQuery[key] === "" ||
          nextQuery[key] == null ||
          nextQuery[key] === false
        ) {
          delete nextQuery[key];
        }
      });

      return nextQuery;
    },
    persistNumericalRouteQuery(query = {}) {
      const nextQuery = {
        mode: String(query.mode || "").toLowerCase(),
        ch: String(query.ch || "").trim(),
        playerCode: String(query.playerCode || "").trim(),
        sheetId: String(query.sheetId || "").trim(),
        range: String(query.range || "").trim(),
        room: String(query.room || "").trim(),
        sheetUrl: String(query.sheetUrl || "").trim(),
      };

      if (nextQuery.mode !== "numerical" || !nextQuery.ch) {
        return;
      }

      Object.keys(nextQuery).forEach((key) => {
        if (!nextQuery[key]) {
          delete nextQuery[key];
        }
      });

      sessionStorage.setItem(
        NUMERICAL_ROUTE_QUERY_STORAGE_KEY,
        JSON.stringify(nextQuery)
      );
    },
    getPreservedNumericalRouteQuery() {
      try {
        return JSON.parse(
          sessionStorage.getItem(NUMERICAL_ROUTE_QUERY_STORAGE_KEY) || "{}"
        );
      } catch (error) {
        return {};
      }
    },
    applyNumericalRouteState(query = {}) {
      const playerCode = String(query.playerCode || "").trim();
      const sheetId = String(query.sheetId || "").trim();
      const sheetUrl = String(query.sheetUrl || "").trim();
      const range = String(query.range || "").trim();

      if (playerCode) {
        this.numericalPlayerCode = playerCode;
      }
      if (sheetId) {
        this.numericalSCSheetID = sheetId;
      }
      if (sheetUrl) {
        this.numericalResolvedSheetUrl = sheetUrl;
      }
      if (range && !this.numericalSCSheetRange) {
        this.numericalSCSheetRange = range;
      }
    },

    async resolveNumericalPlayerSheetAssignment(authToken = "") {
      this.error = "";

      const playerCode = String(this.numericalPlayerCode || "")
        .trim()
        .toUpperCase();
      const npcCode = String(
        this.loginCh || this.$route?.query?.ch || ""
      ).trim();

      if (!playerCode) {
        this.error = "請先輸入玩家代號";
        return;
      }

      if (!npcCode) {
        this.error = "目前數值模式網址未指定 NPC，無法對應試算表";
        return;
      }

      this.isResolvingNumericalPlayerSheet = true;

      try {
        const endpoint = `${String(this.resolveSheetApiBase()).replace(
          /\/$/,
          ""
        )}/api/sheets/assign`;
        const response = await axios.post(
          endpoint,
          {
            playerCode,
            npcCode,
            room: String(
              this.loginRoom || this.$route?.query?.room || ""
            ).trim(),
          },
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined
        );
        this.logApiNetWork2Response(
          "resolveNumericalPlayerSheetAssignment",
          response
        );

        const payload = response?.data?.data || response?.data || {};
        const resolvedSheetId = String(
          payload.sheetId || payload.spreadsheetId || ""
        ).trim();

        if (!resolvedSheetId) {
          throw new Error("API 未回傳對應的試算表 ID");
        }

        const resolvedRange = String(payload.range || "").trim();
        const resolvedSheetUrl = String(
          payload.sheetUrl ||
            payload.editUrl ||
            (resolvedSheetId
              ? `https://docs.google.com/spreadsheets/d/${encodeURIComponent(
                  resolvedSheetId
                )}/edit`
              : "")
        ).trim();

        this.numericalPlayerCode = playerCode;
        this.numericalSCSheetID = resolvedSheetId;
        this.numericalResolvedSheetUrl = resolvedSheetUrl;
        this.numericalSCSheetRange =
          resolvedRange || this.numericalSCSheetRange;
        this.numericalSheetSnapshot = {};
        this.numericalSheetChanges = [];
        this.numericalBaselineReady = false;
        this.numericalBaselineError = "";

        const nextQuery = this.buildRouteQuery({
          ch: npcCode,
          playerCode,
          sheetId: resolvedSheetId,
          range: resolvedRange || undefined,
          room: String(this.loginRoom || this.$route?.query?.room || "").trim(),
          sheetUrl: resolvedSheetUrl || undefined,
        });

        this.persistNumericalRouteQuery(nextQuery);
        this.$router.replace({ path: this.$route.path, query: nextQuery });
        return true;
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "玩家代號對應試算表失敗，請稍後再試";
        return false;
      } finally {
        this.isResolvingNumericalPlayerSheet = false;
      }
    },
    async ensureNumericalPlayerSheetResolved(authToken = "") {
      if (!this.isNumericalMode || this.isNumericalMockMode) {
        return true;
      }

      if (this.hasResolvedNumericalPlayerSheet) {
        return true;
      }

      if (!String(this.numericalPlayerCode || "").trim()) {
        this.error = "請輸入玩家代號";
        return false;
      }

      await this.resolveNumericalPlayerSheetAssignment(authToken);
      return this.hasResolvedNumericalPlayerSheet;
    },

    //開發測試專用，正式環境不會顯示
    enterDevBypass() {
      localStorage.removeItem("game_id");
      localStorage.removeItem("session_allowed_ch");
      localStorage.setItem("session_token", DEV_BYPASS_TOKEN);
      localStorage.setItem("admin_role", "super_admin");

      this.adminRole = "super_admin";
      this.error = "";
      this.$router.replace({ path: "/chat", query: this.$route.query || {} });
    },

    isNpcTypingLockTarget(item) {
      return (
        item &&
        item.role === "assistant" &&
        item.userName === this.showName &&
        item.isStepScale !== true &&
        String(item.content || "").length > 0
      );
    },
    prepareTypingEffectLock(item) {
      if (!this.typeSpeed || this.typeSpeed <= 0) return false;
      if (!this.isNpcTypingLockTarget(item)) return false;

      this.typingLockIdCounter += 1;
      this.$set(item, "_typingLockId", this.typingLockIdCounter);
      return true;
    },
    handleTypingStart(item) {
      if (!item || item._typingLockId == null) return;
      if (!this.isNpcTypingLockTarget(item)) return;

      this.typingActiveMessageId = item._typingLockId;
      this.awaitingNpcReply = false;
    },
    handleTypingComplete(item) {
      if (!item || item._typingLockId == null) return;

      if (this.typingActiveMessageId === item._typingLockId) {
        this.typingActiveMessageId = null;
      }
      this.awaitingNpcReply = false;
      this.$delete(item, "_typingLockId");
    },

    async playVoiceSample() {
      if (!this.voice || this.voiceSampleLoading) return;
      this.voiceSampleLoading = true;
      try {
        const ttsBody = {
          text:
            this.voiceSampleText.trim() ||
            `這是一段測試音檔，你好我是${this.voice}，很高興為您服務`,
          voice: this.voice,
          format: "mp3",
        };
        if (this.ttsInstructions) ttsBody.instructions = this.ttsInstructions;
        if (this.ttsSpeed) {
          ttsBody.speed = Math.min(2.0, Math.max(0.5, Number(this.ttsSpeed)));
        }
        const res = await fetch(this.apiNetWork + "tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ttsBody),
        });
        const data = await res.json();
        const audio = new Audio(
          `data:${data.mimeType};base64,${data.audioBase64}`
        );
        audio.onended = () => {
          this.voiceSampleLoading = false;
        };
        audio.onerror = () => {
          this.voiceSampleLoading = false;
        };
        audio.play().catch(() => {
          this.voiceSampleLoading = false;
        });
      } catch (e) {
        console.error("試聽失敗:", e);
        this.voiceSampleLoading = false;
      }
    },
    async loginStudent() {
      this.error = "";
      this.commitNumericalPlayerCodeInput();

      const normalizedCode = String(this.code || "")
        .trim()
        .toUpperCase();
      const letters = normalizedCode.match(/[A-Z]/g) || [];
      const digits = normalizedCode.match(/[0-9]/g) || [];

      if (
        normalizedCode.length !== 6 ||
        letters.length !== 3 ||
        digits.length !== 3
      ) {
        this.error = "請輸入 6 碼通行密鑰（3 個英文字母 + 3 個數字）";
        return;
      }

      this.code = normalizedCode;

      if (
        this.isNumericalMode &&
        !String(this.numericalPlayerCode || "").trim()
      ) {
        this.error = "請先輸入玩家代號";
        return;
      }

      try {
        const loginPayload = this.loginCh
          ? { code: normalizedCode, ch: this.loginCh }
          : { code: normalizedCode };
        const response = await axios.post(
          `${this.apiNetWork2}/api/student/login`,
          loginPayload
        );
        this.logApiNetWork2Response("loginStudent", response);
        const { data } = response;

        if (this.loginCh && data.game_id !== this.loginCh) {
          this.error = "此密鑰不適用於此聊天室，請使用正確的通行密鑰";
          return;
        }

        const numericalSheetReady =
          await this.ensureNumericalPlayerSheetResolved(data.token);
        if (!numericalSheetReady) {
          return;
        }

        localStorage.removeItem("game_id");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("session_allowed_ch");
        localStorage.setItem("session_token", data.token);

        if (data.game_id) {
          localStorage.setItem("game_id", data.game_id);
          this.studentGameId = data.game_id;
        }

        if (Array.isArray(data.allowed_ch) && data.allowed_ch.length > 0) {
          localStorage.setItem(
            "session_allowed_ch",
            JSON.stringify(data.allowed_ch)
          );
        }

        const targetGameId = data.game_id || "";

        if (this.demoMode && this.demoChs.length > 0) {
          // demoMode 多 NPC：保留 ch1/ch2/ch3，不使用後端回傳的 game_id
          const navQuery = this.buildRouteQuery({ demoMode: "true" });
          this.demoChs.forEach((ch, idx) => {
            navQuery[`ch${idx + 1}`] = ch;
          });
          if (this.loginRoom) navQuery.room = this.loginRoom;
          if (this.loginDoc) navQuery.doc = "true";
          if (this.isPeerOnlyMode) navQuery.ref = "noai";
          this.$router.replace({ path: "/chat", query: navQuery });
        } else if (this.loginCh) {
          const navQuery = this.buildRouteQuery({ ch: this.loginCh });
          if (this.loginRoom) navQuery.room = this.loginRoom;
          if (this.loginDoc) navQuery.doc = "true";
          if (this.isPeerOnlyMode) navQuery.ref = "noai";
          console.info("[loginStudent] navigate /chat query:", navQuery);
          this.$router.replace({ path: "/chat", query: navQuery });
        } else {
          const navQuery = this.buildRouteQuery(
            targetGameId ? { ch: targetGameId } : {}
          );
          if (this.loginRoom) navQuery.room = this.loginRoom;
          if (this.loginDoc) navQuery.doc = "true";
          if (this.isPeerOnlyMode) navQuery.ref = "noai";
          console.info("[loginStudent] navigate /chat query:", navQuery);
          this.$router.replace({
            path: "/chat",
            query: navQuery,
          });
        }
      } catch (e) {
        this.error =
          e?.response?.data?.message || "登入失敗，請檢查密鑰或網路連線";
      }
    },

    async loginStudentMulti() {
      this.error = "";
      const normalizedCode = String(this.code || "")
        .trim()
        .toUpperCase();
      const letters = normalizedCode.match(/[A-Z]/g) || [];
      const digits = normalizedCode.match(/[0-9]/g) || [];

      if (
        normalizedCode.length !== 6 ||
        letters.length !== 3 ||
        digits.length !== 3
      ) {
        this.error = "請輸入 6 碼通行密鑰（3 個英文字母 + 3 個數字）";
        return;
      }

      this.code = normalizedCode;

      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/student/login`,
          { code: normalizedCode, ch: this.multiCh }
        );
        this.logApiNetWork2Response("loginStudentMulti", response);
        const { data } = response;

        // 前端驗證：後端回傳的 allowed_ch 必須涵蓋全部 multiCh
        const returnedAllowedCh = Array.isArray(data.allowed_ch)
          ? data.allowed_ch
          : [];
        const coversAll = this.multiCh.every((ch) =>
          returnedAllowedCh.includes(ch)
        );
        if (!coversAll) {
          this.error =
            "此密鑰不適用於此多 NPC 連結，請使用具備所有 NPC 權限的共用密鑰";
          return;
        }

        localStorage.removeItem("game_id");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("session_allowed_ch");
        localStorage.setItem("session_token", data.token);

        if (data.game_id) {
          localStorage.setItem("game_id", data.game_id);
          this.studentGameId = data.game_id;
        }

        // 將多 NPC ch 清單全部存入 session，讓各 iframe 通過驗證
        localStorage.setItem(
          "session_allowed_ch",
          JSON.stringify(this.multiCh)
        );

        const navQuery = this.buildRouteQuery();
        this.multiCh.forEach((ch, idx) => {
          navQuery[`ch${idx + 1}`] = ch;
        });
        if (this.loginRoom) navQuery.room = this.loginRoom;
        if (this.isPeerOnlyMode) navQuery.ref = "noai";
        this.$router.replace({ path: "/chat", query: navQuery });
      } catch (e) {
        this.error =
          e?.response?.data?.message || "登入失敗，請檢查密鑰或網路連線";
      }
    },

    async loginAdmin() {
      this.error = "";
      if (!this.username || !this.password) {
        this.error = "請輸入帳號密碼";
        return;
      }

      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/admin/login`,
          {
            username: this.username,
            password: this.password,
          }
        );
        this.logApiNetWork2Response("loginAdmin", response);
        const { data } = response;

        localStorage.removeItem("game_id");
        localStorage.setItem("session_token", data.token);
        localStorage.setItem("admin_role", data.role || "viewer");
        this.adminRole = data.role || "viewer";
        if (data.role === "viewer") {
          this.$router.replace("/viewer");
        } else {
          this.$router.replace("/chat");
        }
      } catch (e) {
        this.error =
          e?.response?.data?.message || "登入失敗，請檢查帳密或網路連線";
      }
    },

    resetIdleTimer() {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        alert("閒置超過 2 小時，請重新登入");
        localStorage.removeItem("game_id");
        localStorage.removeItem("session_token");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("session_allowed_ch");
        this.$router.replace("/login");
      }, 2 * 60 * 60 * 1000);
    },
    getTotalTurnCount() {
      if (!Array.isArray(this.chatLog)) return 0;
      return this.chatLog.filter(
        (msg) =>
          (msg.role === "user" || msg.role === "assistant") &&
          !msg.hidden &&
          !msg.isRecord &&
          !msg.isStepScale
      ).length;
    },

    cleanMarkdownFence(text) {
      if (!text) return "";
      return text
        .replace(/```+\s*markdown\s*/gi, "")
        .replace(/```+\s*md\s*/gi, "")
        .replace(/```/g, "")
        .replace(/~~~/g, "")
        .trim();
    },

    renderMarkdown(text) {
      const cleaned = this.cleanMarkdownFence(text);
      return this.md.render(cleaned);
    },

    logApiNetWork2Response(label, response) {
      if (this.debug === true) {
        console.log(`[apiNetWork2] ${label} response:`, response);
      }
    },

    logAzureResponse(label, response, extra = undefined) {
      if (this.debug === true) {
        console.log(`[azure] ${label} response:`, response);
        if (extra !== undefined) {
          console.log(`[azure] ${label} extra:`, extra);
        }
      }
    },

    extractTokenCal(source) {
      if (!source || typeof source !== "object") return null;
      return Object.prototype.hasOwnProperty.call(source, "tokenCal")
        ? source.tokenCal
        : null;
    },

    formatTokenCal(value) {
      if (value == null || value === "") return "";
      if (typeof value === "string") return value;

      try {
        return JSON.stringify(value);
      } catch (error) {
        console.error("tokenCal 格式化失敗:", error);
        return String(value);
      }
    },

    parseTokenCal(value, key) {
      if (value == null || value === "") return "";
      try {
        const obj = typeof value === "string" ? JSON.parse(value) : value;
        return obj[key] ?? "";
      } catch {
        return "";
      }
    },

    calcToken() {
      let val = Number(this.maxTokens);
      if (val < 512) {
        return 512;
      } else if (val > 800) {
        return val + 1000;
      }
      return val;
    },

    // 建立時間格式
    formatDateTime(a, b) {
      return `${a} ${b}`;
    },

    // 存成開始或結束時間
    setDateTime(a, b, c) {
      if ((b != null) & (c != null)) {
        var result = this.formatDateTime(b, c);
        if (a == 0) {
          this.startDateTime = result;
        } else if (a == 1) {
          this.endDateTime = result;
        }
      }
    },

    normalizeDateTimeLocal(value) {
      if (!value) return "";
      return value.length === 16
        ? value.replace("T", " ") + ":00"
        : value.replace("T", " ");
    },

    normalizeSheetCellPosition(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },

    normalizeSheetCellValue(value) {
      if (value == null) return "";
      return String(value).trim();
    },

    sheetColumnLabel(index) {
      let num = Number(index) + 1;
      let label = "";

      while (num > 0) {
        const remainder = (num - 1) % 26;
        label = String.fromCharCode(65 + remainder) + label;
        num = Math.floor((num - 1) / 26);
      }

      return label;
    },

    buildSheetSnapshot(sheetPayload) {
      const snapshot = {};
      const cells = Array.isArray(sheetPayload?.cells)
        ? sheetPayload.cells
        : null;

      if (cells) {
        cells.forEach((cell) => {
          const position = this.normalizeSheetCellPosition(
            cell?.a1Notation || cell?.position
          );

          if (!position) return;

          snapshot[position] = {
            value: this.normalizeSheetCellValue(cell?.newValue ?? cell?.value),
            editedBy: cell?.editedBy || cell?.editor || "",
            editedAt: cell?.editedAt || cell?.updatedAt || "",
          };
        });

        return snapshot;
      }

      const values = Array.isArray(sheetPayload?.values)
        ? sheetPayload.values
        : [];

      values.forEach((row, rowIdx) => {
        if (!Array.isArray(row)) return;

        row.forEach((cellValue, colIdx) => {
          const position = `${this.sheetColumnLabel(colIdx)}${rowIdx + 1}`;
          snapshot[position] = {
            value: this.normalizeSheetCellValue(cellValue),
            editedBy: "",
            editedAt: "",
          };
        });
      });

      return snapshot;
    },

    extractSheetChanges(sheetPayload, nextSnapshot, previousSnapshot) {
      const payloadChanges =
        sheetPayload?.changes ||
        sheetPayload?.editedCells ||
        sheetPayload?.diffs;

      if (Array.isArray(payloadChanges) && payloadChanges.length > 0) {
        return payloadChanges
          .map((change) => {
            const position = this.normalizeSheetCellPosition(
              change?.a1Notation || change?.position
            );
            if (!position) return null;

            return {
              a1Notation: position,
              oldValue: this.normalizeSheetCellValue(
                change?.oldValue ?? previousSnapshot[position]?.value
              ),
              newValue: this.normalizeSheetCellValue(
                change?.newValue ?? nextSnapshot[position]?.value
              ),
              editedBy: change?.editedBy || change?.editor || "未知編輯者",
              editedAt:
                change?.editedAt ||
                change?.updatedAt ||
                nextSnapshot[position]?.editedAt ||
                "",
            };
          })
          .filter(Boolean)
          .filter((change) => change.oldValue !== change.newValue);
      }

      return Array.from(
        new Set([
          ...Object.keys(previousSnapshot || {}),
          ...Object.keys(nextSnapshot || {}),
        ])
      )
        .map((position) => {
          const before = previousSnapshot[position]?.value ?? "";
          const after = nextSnapshot[position]?.value ?? "";

          if (before === after) return null;

          return {
            a1Notation: position,
            oldValue: before,
            newValue: after,
            editedBy: nextSnapshot[position]?.editedBy || "未知編輯者",
            editedAt: nextSnapshot[position]?.editedAt || "",
          };
        })
        .filter(Boolean);
    },

    compareNumericalCondition(actualValue, operator, expectedValue) {
      const actualNumber = Number(actualValue);
      const expectedNumber = Number(expectedValue);

      if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) {
        return false;
      }

      if (operator === ">") return actualNumber > expectedNumber;
      if (operator === "<") return actualNumber < expectedNumber;
      return actualNumber === expectedNumber;
    },

    buildNumericalPromptContent(rule) {
      return String(rule?.content || "").trim();
    },

    buildNumericalPromptContext(change) {
      const editor = change.editedBy || "未知編輯者";
      const previousValue = change.oldValue === "" ? "空白" : change.oldValue;
      const currentValue = change.newValue === "" ? "空白" : change.newValue;

      return `試算表欄位：${change.a1Notation}\n原始值：${previousValue}\n目前值：${currentValue}\n編輯者：${editor}`;
    },

    resolveSheetApiBase() {
      const configuredBase = String(this.sheetApiBase || "").trim();
      const hostName =
        typeof window !== "undefined"
          ? String(window.location.hostname || "")
          : "";
      const isLocalDevHost = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(
        hostName
      );
      const pointsToLocalDevServer =
        /localhost:8081|127\.0\.0\.1:8081|0\.0\.0\.0:8081/i.test(
          configuredBase
        );

      if (isLocalDevHost && (!configuredBase || pointsToLocalDevServer)) {
        return "";
      }

      return configuredBase || "https://api.ntustmeg.com";
    },

    async fetchSheetData(sheetId, range = "") {
      const normalizedSheetId = String(sheetId || "").trim();
      if (!normalizedSheetId) {
        throw new Error("請先輸入 Google Sheet ID");
      }

      if (this.isNumericalPreviewRoute) {
        return this.fetchNumericalPreviewSheetData(normalizedSheetId, range);
      }

      const endpoint = `${String(this.resolveSheetApiBase()).replace(
        /\/$/,
        ""
      )}/api/sheets/read`;

      const payload = {
        sheetId: normalizedSheetId,
        npcCode: String(
          this.loginCh || this.$route?.query?.ch || this.name || ""
        ).trim(),
        playerCode: String(this.numericalPlayerCode || "")
          .trim()
          .toUpperCase(),
      };
      if (range) payload.range = range;

      const response = await axios.post(endpoint, payload);
      this.logApiNetWork2Response("fetchSheetData", response);

      const sheetPayload = response?.data?.data || response?.data || {};
      console.log("[fetchSheetData] sheet payload:", sheetPayload);

      if (Array.isArray(sheetPayload?.values)) {
        console.log("[fetchSheetData] sheet values:", sheetPayload.values);
        console.table(sheetPayload.values);
      }

      return sheetPayload;
    },

    async fetchNumericalPreviewSheetData(sheetId, range = "") {
      const endpoint = `${String(this.resolveSheetApiBase()).replace(
        /\/$/,
        ""
      )}/api/admin/numerical-sheets/preview/read`;
      const payload = { sheetId };
      if (range) payload.range = range;

      const response = await axios.post(endpoint, payload);
      this.logApiNetWork2Response("fetchNumericalPreviewSheetData", response);
      return response?.data?.data || response?.data || {};
    },

    async loadNumericalPreviewBaseline() {
      const sheetId = String(this.numericalSCSheetID || "").trim();
      const range = String(this.numericalSCSheetRange || "").trim();

      if (!sheetId) {
        this.numericalBaselineReady = false;
        this.numericalBaselineError = "預覽設定缺少 Google Sheet ID";
        return false;
      }

      this.isLoadingNumericalBaseline = true;
      this.numericalBaselineReady = false;
      this.numericalBaselineError = "";

      try {
        const baselinePayload = await this.fetchNumericalPreviewSheetData(
          sheetId,
          range
        );
        this.numericalSheetSnapshot = this.buildSheetSnapshot(baselinePayload);
        this.numericalSheetChanges = [];
        this.numericalBaselineReady = true;
        return true;
      } catch (error) {
        this.numericalSheetSnapshot = {};
        this.numericalSheetChanges = [];
        this.numericalBaselineReady = false;
        this.numericalBaselineError =
          error?.response?.data?.message ||
          error?.message ||
          "載入預覽試算表初始資料失敗";
        console.error("載入數值模式預覽 baseline 失敗:", error);
        return false;
      } finally {
        this.isLoadingNumericalBaseline = false;
      }
    },

    async loadNumericalBaseline() {
      if (!this.isNumericalMode || this.isNumericalMockMode) {
        this.numericalBaselineReady = true;
        return true;
      }

      if (this.isNumericalPreviewRoute) {
        return this.loadNumericalPreviewBaseline();
      }

      const npcCode = String(
        this.loginCh || this.$route?.query?.ch || ""
      ).trim();
      const playerCode = String(
        this.numericalPlayerCode || this.$route?.query?.playerCode || ""
      )
        .trim()
        .toUpperCase();

      if (!npcCode || !playerCode) {
        this.numericalBaselineReady = false;
        this.numericalBaselineError =
          "缺少 NPC 代碼或玩家代號，無法載入初始資料";
        return false;
      }

      this.isLoadingNumericalBaseline = true;
      this.numericalBaselineReady = false;
      this.numericalBaselineError = "";

      try {
        const endpoint = `${String(this.resolveSheetApiBase()).replace(
          /\/$/,
          ""
        )}/api/sheets/baseline`;
        const response = await axios.post(endpoint, {
          npcCode,
          playerCode,
        });
        const baselinePayload = response?.data?.data || response?.data || {};

        this.numericalSheetSnapshot = this.buildSheetSnapshot(baselinePayload);
        this.numericalSheetChanges = [];
        this.numericalBaselineReady = true;
        return true;
      } catch (error) {
        this.numericalSheetSnapshot = {};
        this.numericalSheetChanges = [];
        this.numericalBaselineReady = false;
        this.numericalBaselineError =
          error?.response?.data?.message ||
          error?.message ||
          "載入數值模式初始資料失敗";
        console.error("載入數值模式 baseline 失敗:", error);
        return false;
      } finally {
        this.isLoadingNumericalBaseline = false;
      }
    },

    handleSheetEdit(change) {
      if (!change || !change.a1Notation) return;

      this.numericalSheetChanges.unshift({
        ...change,
        triggeredAt: new Date().toISOString(),
      });
      this.numericalSheetChanges = this.numericalSheetChanges.slice(0, 20);

      this.useNumericalSC(change);
    },

    resetAccessCodeForm() {
      this.accessCodeForm = {
        game_id: "",
        allowed_ch: "",
        start_time: "",
        end_time: "",
        code_type: "permanent",
        note: "",
        quantity: 1,
      };
      this.accessCodeNpcSearch = "";
      this.accessCodeSelectedNpcText = "";
      this.accessCodeAllowedChSearch = "";
      this.accessCodeAllowedChSelected = [];
    },

    resetAdminAccountForm() {
      this.adminAccountForm = {
        username: "",
        password: "",
        admin_name: "",
        role: "viewer",
      };
    },

    async fetchAdminAccounts() {
      this.isLoadingAdminAccounts = true;
      try {
        const response = await axios.get(
          `${this.apiNetWork2}/api/admin/accounts`
        );
        this.logApiNetWork2Response("fetchAdminAccounts", response);
        const { data } = response;
        this.adminAccountList = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("載入管理員帳號失敗:", err);
        alert(err?.response?.data?.message || "載入管理員帳號失敗");
      } finally {
        this.isLoadingAdminAccounts = false;
      }
    },

    async createAdminAccount() {
      const payload = {
        username: (this.adminAccountForm.username || "").trim(),
        password: this.adminAccountForm.password || "",
        admin_name: (this.adminAccountForm.admin_name || "").trim(),
        role: this.adminAccountForm.role || "viewer",
      };

      if (
        !payload.username ||
        !payload.password ||
        !payload.admin_name ||
        !payload.role
      ) {
        alert("請完整填寫管理員帳號資料");
        return;
      }

      this.isSavingAdminAccount = true;
      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/admin/accounts`,
          payload
        );
        this.logApiNetWork2Response("createAdminAccount", response);
        alert("建立管理員帳號成功");
        this.resetAdminAccountForm();
        await this.fetchAdminAccounts();
      } catch (err) {
        console.error("建立管理員帳號失敗:", err);
        alert(err?.response?.data?.message || "建立管理員帳號失敗");
      } finally {
        this.isSavingAdminAccount = false;
      }
    },

    async fetchAccessCodes() {
      this.isLoadingAccessCodes = true;
      try {
        const response = await axios.get(
          `${this.apiNetWork2}/api/admin/access-codes`
        );
        this.logApiNetWork2Response("fetchAccessCodes", response);
        const { data } = response;
        this.accessCodeList = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("載入通行密鑰失敗:", err);
        alert(err?.response?.data?.message || "載入通行密鑰失敗");
      } finally {
        this.isLoadingAccessCodes = false;
      }
    },

    async createAccessCode() {
      const allowedChArray = this.accessCodeAllowedChSelected;

      const payload = {
        game_id: this.accessCodeForm.game_id,
        allowed_ch: allowedChArray.length > 0 ? allowedChArray : undefined,
        start_time: this.normalizeDateTimeLocal(this.accessCodeForm.start_time),
        end_time: this.normalizeDateTimeLocal(this.accessCodeForm.end_time),
        code_type: this.accessCodeForm.code_type,
        note: this.accessCodeForm.note,
        quantity: Number(this.accessCodeForm.quantity || 1),
      };
      console.log("createAccessCode payload", payload);

      if (
        !payload.game_id ||
        !payload.start_time ||
        !payload.end_time ||
        !payload.code_type
      ) {
        alert("請完整填寫通行密鑰資料");
        return;
      }

      if (
        !Number.isInteger(payload.quantity) ||
        payload.quantity <= 0 ||
        payload.quantity > 500
      ) {
        alert("一次新增數量請輸入 1 到 500 的整數");
        return;
      }

      this.isSavingAccessCode = true;
      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/admin/access-codes`,
          payload
        );
        this.logApiNetWork2Response("createAccessCode", response);
        const { data } = response;
        if (data.count > 1 && Array.isArray(data.codes)) {
          alert(
            `新增成功，共建立 ${data.count} 組密鑰：\n${data.codes.join("、")}`
          );
        } else {
          alert(`新增成功，密鑰為：${data.code}`);
        }
        this.resetAccessCodeForm();
        await this.fetchAccessCodes();
      } catch (err) {
        console.error("新增通行密鑰失敗:", err);
        alert(err?.response?.data?.message || "新增通行密鑰失敗");
      } finally {
        this.isSavingAccessCode = false;
      }
    },

    async deleteAccessCode(id) {
      if (!confirm("確定要刪除此通行密鑰嗎？")) return;
      try {
        const response = await axios.delete(
          `${this.apiNetWork2}/api/admin/access-codes/${id}`
        );
        this.logApiNetWork2Response("deleteAccessCode", response);
        alert("刪除成功");
        await this.fetchAccessCodes();
      } catch (err) {
        console.error("刪除通行密鑰失敗:", err);
        alert(err?.response?.data?.message || "刪除通行密鑰失敗");
      }
    },

    async regenerateAccessCode(id) {
      if (!confirm("確定要重新生成此通行密鑰嗎？")) return;
      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/admin/access-codes/${id}/regenerate`
        );
        this.logApiNetWork2Response("regenerateAccessCode", response);
        const { data } = response;
        alert(`重新生成成功，新密鑰為：${data.code}`);
        await this.fetchAccessCodes();
      } catch (err) {
        console.error("重新生成通行密鑰失敗:", err);
        alert(err?.response?.data?.message || "重新生成通行密鑰失敗");
      }
    },

    async copyAccessCode(code) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
          alert("已複製密鑰");
        } else {
          alert(`請手動複製密鑰：${code}`);
        }
      } catch (err) {
        console.error("複製密鑰失敗:", err);
        alert(`請手動複製密鑰：${code}`);
      }
    },
    // 抓 NPC 名單
    async fetchNPCOptions() {
      this.isLoadingNPCs = true;
      try {
        const response = await axios.get(
          `${this.apiNetWork2}/getAllNPCMegaNames`
        );
        this.logApiNetWork2Response("fetchNPCOptions", response);
        const { data } = response;
        this.npcOptions = (data || [])
          .filter((row) => row && row.name) // 過濾掉沒有 name 的資料
          .map((row) => ({
            value: row.name,
            text:
              row.showName && row.showName.trim() !== ""
                ? `${row.name} (${row.showName})`
                : row.name,
          }));
      } catch (err) {
        console.error("載入 NPC 名單失敗:", err);
        this.npcOptions = [];
      } finally {
        this.isLoadingNPCs = false;
      }
    },

    // 當使用者點選某個選項
    selectNpc(opt) {
      this.selectedNpcText = opt.text; // 顯示名稱
      this.chatDataQuery.gameId = opt.value; // 存成後端需要的代碼
      this.$refs.npcDropdown.hide(true); // 選完就收起 dropdown
    },

    selectAccessCodeNpc(opt) {
      this.accessCodeSelectedNpcText = opt.text;
      this.accessCodeForm.game_id = opt.value;
      this.$refs.accessCodeNpcDropdown.hide(true);
    },

    // 清除搜尋框
    clearNpcSearch() {
      this.selectedNpcText = "";
      this.chatDataQuery.gameId = "";
    },

    // 預設時間
    defaultDateTime() {
      const now = new Date();
      this.startDate = now.toISOString().slice(0, 10);
      this.startTime = now.toTimeString().slice(0, 5);
      this.setDateTime(0, this.startDate, this.startTime);
      const future = new Date();
      future.setMonth(future.getMonth() + 2);
      this.endDate = future.toISOString().slice(0, 10);
      this.endTime = now.toTimeString().slice(0, 5);
      this.setDateTime(1, this.endDate, this.endTime);
    },

    // 處理分頁變更
    handlePageChange(page) {
      this.currentPage = page;
    },

    // 讀取時間
    loadDateTime(a, b) {
      const startObj = new Date(a);

      // 格式化日期部分為 YYYY-MM-DD
      const syear = startObj.getFullYear();
      const smonth = String(startObj.getMonth() + 1).padStart(2, "0");
      const sday = String(startObj.getDate()).padStart(2, "0");
      this.startDate = `${syear}-${smonth}-${sday}`;

      // 格式化時間部分為 HH:MM:SS
      const shours = String(startObj.getHours()).padStart(2, "0");
      const sminutes = String(startObj.getMinutes()).padStart(2, "0");
      const sseconds = String(startObj.getSeconds()).padStart(2, "0");
      this.startTime = `${shours}:${sminutes}:${sseconds}`;

      const endObj = new Date(b);
      // 格式化日期部分為 YYYY-MM-DD
      const eyear = endObj.getFullYear();
      const emonth = String(endObj.getMonth() + 1).padStart(2, "0");
      const eday = String(endObj.getDate()).padStart(2, "0");
      this.endDate = `${eyear}-${emonth}-${eday}`;

      // 格式化時間部分為 HH:MM:SS
      const ehours = String(endObj.getHours()).padStart(2, "0");
      const eminutes = String(endObj.getMinutes()).padStart(2, "0");
      const eseconds = String(endObj.getSeconds()).padStart(2, "0");
      this.endTime = `${ehours}:${eminutes}:${eseconds}`;
      this.setDateTime(0, this.startDate, this.startTime);
      this.setDateTime(1, this.endDate, this.endTime);
    },
    // 比較時間
    checkTimeInRange() {
      // 取得當前時間
      const now = new Date();

      // 將 MySQL DATETIME 格式轉換為 Date 物件
      const startDateTime = new Date(this.startDateTime);
      const endDateTime = new Date(this.endDateTime);

      // 檢查當前時間是否在區間內
      if (now >= startDateTime && now <= endDateTime) {
        return true;
      } else {
        return false;
      }
    },
    resizeChatInput(event = null) {
      const inputs = event?.target
        ? [event.target]
        : this.$el.querySelectorAll(".chat-input-autosize");

      inputs.forEach((el) => this.resizeChatInputElement(el));
    },
    resizeChatInputElement(el) {
      if (!el) return;

      const styles = getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight);
      const paddingY =
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const borderY =
        parseFloat(styles.borderTopWidth) +
        parseFloat(styles.borderBottomWidth);
      const maxHeight = lineHeight * 3 + paddingY + borderY;

      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    },
    syncSpeechInputSize() {
      const inputs = this.$el.querySelectorAll(".chat-input-autosize");
      inputs.forEach((el) => {
        if (el.value !== this.chatContent) el.value = this.chatContent;
        this.resizeChatInputElement(el);
      });
    },
    toggleSpeechRecognition() {
      if (this.isSpeechListening) {
        this.stopSpeechRecognition();
      } else {
        this.startSpeechRecognition();
      }
    },
    startSpeechRecognition() {
      if (!this.isSpeechRecognitionSupported) {
        alert("此瀏覽器不支援語音輸入，請改用 Chrome 或 Edge。");
        return;
      }

      const Recognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new Recognition();

      this.stopSpeechRecognition();
      this.speechError = "";
      this.speechFinalTranscript = "";
      this.speechBaseContent = this.chatContent || "";
      this.speechRecognition = recognition;

      recognition.lang = this.speechLang;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        this.isSpeechListening = true;
      };

      recognition.onresult = (event) => {
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0]?.transcript || "";
          const traditionalTranscript =
            convertSpeechToTaiwanTraditional(transcript);
          if (event.results[i].isFinal) {
            this.speechFinalTranscript += traditionalTranscript;
          } else {
            interimTranscript += traditionalTranscript;
          }
        }

        const spokenText = (
          this.speechFinalTranscript + interimTranscript
        ).trim();
        const baseText = this.speechBaseContent.trim();
        this.chatContent = baseText
          ? `${baseText} ${spokenText}`.trim()
          : spokenText;
        this.syncSpeechInputSize();
        this.$nextTick(() => this.resizeChatInput());
      };

      recognition.onerror = (event) => {
        const errorMessages = {
          "not-allowed": "麥克風權限被拒絕",
          "service-not-allowed": "語音辨識服務不可用",
          "no-speech": "沒有偵測到語音",
          "audio-capture": "找不到可用的麥克風",
          network: "語音辨識網路連線失敗",
        };
        this.speechError = errorMessages[event.error] || "語音輸入發生錯誤";
      };

      recognition.onend = () => {
        this.isSpeechListening = false;
        if (this.speechRecognition === recognition) {
          this.speechRecognition = null;
        }
      };

      try {
        recognition.start();
      } catch (error) {
        this.speechRecognition = null;
        this.isSpeechListening = false;
        this.speechError = "語音輸入無法啟動";
      }
    },
    stopSpeechRecognition() {
      if (!this.speechRecognition) {
        this.isSpeechListening = false;
        return;
      }

      const recognition = this.speechRecognition;
      this.speechRecognition = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch (error) {
        // Recognition may already be stopped by the browser.
      }
      this.isSpeechListening = false;
    },
    // 處理發送訊息
    async sendMessage(a, event = null) {
      if (event && this.isComposing) return;
      if (event?.shiftKey) return;
      if (event?.preventDefault) event.preventDefault();
      // ref=noai 時，包含 Enter 與任何既有按鈕都只能走同儕廣播。
      if (this.isPeerOnlyMode) a = false;
      if (a && this.isAiMessageLocked) return;
      if (!this.chatContent || this.chatContent.trim() === "") return;
      if (this.isSpeechListening) this.stopSpeechRecognition();

      if (a && this.askLimit > 0 && this.askLimitCal <= 0) {
        alert("提問次數已用盡");
        return;
      }

      try {
        const response = await axios.post(
          `${this.apiNetWork2}/api/session/touch`
        );
        this.logApiNetWork2Response("sendMessage touchSession", response);
        this.resetIdleTimer();
      } catch (e) {
        const status = e?.response?.status;

        if (status === 401) {
          // Session 失效的跳轉由全域 interceptor 統一處理，並保留 ch/room。
          alert(e?.response?.data?.message || "登入已失效，請重新登入");
        } else if (status !== 403) {
          // 500、逾時或斷線都保留 Session、房間及尚未送出的訊息。
          alert("連線暫時異常，訊息尚未送出，請稍後再試");
        }
        return;
      }

      var message = {};
      if (a) {
        message.message = [
          this.mergeSystemCharacter(9999),
          {
            role: "user",
            content: this.chatContent,
            userName: this.userName,
            room: this.roomCode,
          },
        ];
        // this.pushChatLog(message.message);
      } else {
        message.message = [
          {
            role: "user",
            content: this.chatContent,
            userName: this.userName,
            room: this.roomCode,
          },
        ];
      }
      message.temperature = this.temperature;
      message.maxTokens = this.calcToken();
      message.gpt = a;
      message.character = this.showName;
      message.room = this.roomCode;
      message.hint = null;
      if (this.voiceSwitch) {
        message.voice = this.voice;
      } else {
        message.voice = null;
      }
      if (this.debug) {
        console.log("送出的訊息");
        console.log(message);
      }

      if (message.gpt) {
        this.awaitingNpcReply = true;
        this.startAnimationTimer();

        if (this.askLimit > 0 && this.askLimitCal > 0) {
          this.askLimitCal -= 1;

          if (!this.hideRoomCode) {
            this.$nextTick(() => {
              this.broadcastAskLimitUpdate();
            });
          }
        }
      }
      axios
        .post(this.apiNetWork + "message", this.withApiNetWorkPay(message))
        .then((response) => {
          this.logAzureResponse("sendMessage message", response);
          this.chatContent = "";
          this.$nextTick(() => this.resizeChatInput(event));
          this.initState++;
        })
        .catch(() => {
          this.awaitingNpcReply = false;
          const blank = [
            {
              role: "assistant",
              content: "",
              userName: this.showName,
            },
          ];
          this.stopAnimationTimer();
          this.pushChatLog(blank);
        });
    },

    pushChatLog(a) {
      const bePush = a[a.length - 1];
      // 純組員模式不處理 AI 訊息，避免顯示、語音播放及後續副作用。
      if (this.isPeerOnlyMode && bePush.role !== "user") return;
      if (bePush.mark === 1) {
        const isDuplicate = this.chatLog.some(
          (item) => item.mark === 1 && item.stepScaleID === bePush.stepScaleID
        );
        if (isDuplicate) return;
      } else if (bePush.stepScaleID) {
        const isDuplicate = this.chatLog.some(
          (item) => item.stepScaleID === bePush.stepScaleID
        );
        if (isDuplicate) return;
      }

      // 只有「NPC 的一般回覆」才插入暫存的階段性鷹架
      const isNpc =
        bePush.role === "assistant" && bePush.userName === this.showName;
      const isStepScale = bePush.isStepScale === true;
      const shouldLockNpcTyping =
        isNpc && !isStepScale && this.prepareTypingEffectLock(bePush);

      // 語音延遲播完後才顯示文字
      if (this.voiceSwitch && isNpc && !isStepScale) {
        this.stopAnimationTimer();

        const showText = () => {
          this.ttsLoading = false;
          this.chatLog.push(bePush);
          if (!shouldLockNpcTyping) {
            this.awaitingNpcReply = false;
          }
          this.maybeAnalyze();

          if (this.pendingStepScale) {
            this.chatLog.push(this.pendingStepScale);
            this.pendingStepScale = null;
          }

          if (this.endConDb.length > 0 && this.endConSwitch) this.useEndCon(a);
          if (this.progressiveSCDb.length > 0 && this.progressiveSCSwitch)
            this.useProgressiveSC(a);

          const shouldWriteToDb =
            this.hideRoomCode ||
            (bePush.role === "assistant" &&
              this.lastSenderName === this.userName);
          if (shouldWriteToDb) {
            axios
              .post(`${this.apiNetWork2}/megaGPTRecord`, {
                gameId: this.name,
                userId: this.userName,
                roomId: this.roomCode,
                chatName: bePush.userName,
                chatContent: bePush.content,
                tokenCal: this.extractTokenCal(bePush),
              })
              .then((response) => {
                this.logApiNetWork2Response(
                  "pushChatLog megaGPTRecord",
                  response
                );
              })
              .catch(() => {});
          }

          try {
            this.useStepScale && this.useStepScale();
          } catch (e) {
            console.warn("useStepScale call failed", e);
          }

          try {
            this.triggerRecordAnalysis && this.triggerRecordAnalysis();
          } catch (e) {
            console.warn("triggerRecordAnalysis call failed", e);
          }
        };

        const ttsBody = {
          text: bePush.content,
          voice: this.voice || "alloy",
          format: "mp3",
        };
        if (this.ttsInstructions) ttsBody.instructions = this.ttsInstructions;
        if (this.ttsSpeed) {
          const clampedSpeed = Math.min(
            2.0,
            Math.max(0.5, Number(this.ttsSpeed))
          );
          ttsBody.speed = clampedSpeed;
        }
        this.ttsLoading = true;
        fetch(this.apiNetWork + "tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ttsBody),
        })
          .then((res) => res.json())
          .then((data) => {
            const audio = new Audio(
              `data:${data.mimeType};base64,${data.audioBase64}`
            );
            audio.volume = 1;
            audio.onended = showText;
            audio.play().catch(() => showText());
          })
          .catch(() => showText());

        return;
      }

      this.chatLog.push(bePush);

      if (isNpc && !isStepScale && !shouldLockNpcTyping) {
        this.awaitingNpcReply = false;
      }

      this.maybeAnalyze();

      if (isNpc && !isStepScale) {
        this.stopAnimationTimer();
        if (this.pendingStepScale) {
          this.chatLog.push(this.pendingStepScale);
          this.pendingStepScale = null;
        }
      }

      if (this.endConDb.length > 0 && this.endConSwitch) this.useEndCon(a);
      if (this.progressiveSCDb.length > 0 && this.progressiveSCSwitch)
        this.useProgressiveSC(a);

      if (bePush.role === "user") {
        this.lastSenderName = bePush.userName;
      }

      // 多人模式下避免重複寫入 DB：
      const shouldWriteToDb =
        this.hideRoomCode ||
        bePush.userName === this.userName ||
        (bePush.role === "assistant" && this.lastSenderName === this.userName);

      if (shouldWriteToDb) {
        const dbChatName = bePush.isStepScale
          ? this.showName + " 階段評估"
          : bePush.userName;
        axios
          .post(`${this.apiNetWork2}/megaGPTRecord`, {
            gameId: this.name,
            userId: this.userName,
            roomId: this.roomCode,
            chatName: dbChatName,
            chatContent: bePush.content,
            tokenCal: this.extractTokenCal(bePush),
          })
          .then((response) => {
            this.logApiNetWork2Response("pushChatLog megaGPTRecord", response);
          })
          .catch(() => {});
      }

      if (bePush.role === "user" && bePush.userName === this.userName) {
        this.userTurnCount += 1;
      }

      try {
        this.useStepScale && this.useStepScale();
      } catch (e) {
        console.warn("useStepScale call failed", e);
      }

      try {
        this.triggerRecordAnalysis && this.triggerRecordAnalysis();
      } catch (e) {
        console.warn("triggerRecordAnalysis call failed", e);
      }
    },

    initHint() {
      if (this.isPeerOnlyMode) return;
      if (this.chatInitSC.length > 0 && this.initState >= this.initStart) {
        var initR = 0;
        if (this.initRandom == "true") {
          initR = Math.floor(Math.random() * this.chatInitSC.length);
        }
        this.initProShow = Math.floor(Math.random() * 101);
        var initP = this.initProShow > 100 - this.initPro;

        if (initP) {
          var b = [
            {
              role: "assistant",
              content: this.chatInitSC[initR],
              userName: this.hintName,
            },
          ];
          this.pushChatLog(b);
          this.chatInitSC.splice(initR, 1);
          this.initState = 0;
        }
      }
    },
    async setGPTData() {
      var bePush = {
        name: this.name,
        character: this.character,
        knowledge: this.knowledge,
        temp: this.temperature,
        token: this.maxTokens,
        showName: this.showName,
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
        initSC: JSON.stringify(this.initSC),
        initStart: parseInt(this.initStart),
        initPro: parseInt(this.initPro),
        initRandom: this.initRandom,
        progressiveSC: JSON.stringify(this.progressiveSC),
        situationText: this.situationText,
        firstTalk: this.firstTalk,
        hintName: this.hintName,
        hintName2: this.hintName2,
        hintName3: this.hintName3,
        autoSCSwitch: this.autoSCSwitch,
        progressiveSCSwitch: this.progressiveSCSwitch,
        initSwitch: this.initSwitch,
        voiceSwitch: this.voiceSwitch,
        voice: this.voice,
        ttsInstructions: this.ttsInstructions,
        ttsSpeed: this.ttsSpeed,
        autoSC: this.autoSC,
        autoSCProbability: this.autoSCProbability,
        autoMessagePrompt: this.autoMessagePrompt,
        askLimit: this.askLimit,
        scaleSC: this.scaleSC,
        scaleSCSwitch: this.scaleSCSwitch,
        open: this.open,
        endCon: JSON.stringify(this.endCon),
        endConSwitch: this.endConSwitch,
        typeSpeed: this.typeSpeed,
        stepScaleSCSwitch: this.stepScaleSCSwitch,
        stepScaleSC: this.stepScaleSC,
        stepScaleSCStep: this.stepScaleSCStep,
        hintName4: this.hintName4,
        avatar: this.avatarUrl, // 加入頭像 URL
        recordSC: this.recordSC,
        recordSCStep: this.recordSCStep,
        ...this.buildExtendedNumericalNpcPayload(),
        docFeedbackSwitch: this.docFeedbackSwitch,
        docFeedbackN: this.docFeedbackN,
        docFeedbackPrompt: this.docFeedbackPrompt,
      };

      var requestUrl = `${this.apiNetWork2}/setNPCMega`;

      if (confirm("確定要上傳嗎？若有相同的專案名稱會覆蓋") == true) {
        try {
          const response = await axios.post(requestUrl, bePush);
          this.logApiNetWork2Response("setGPTData", response);
          await this.saveNPCLayout(this.name);
          alert("上傳成功");
        } catch (error) {
          console.log(error);
          // 不要顯示空白卡片
          this.scaleFailed = true;
          this.endResult = "";
          alert("上傳失敗，請確認後端 API 是否已完成");
        }
      }
    },
    copyNPCUrl(peerOnly = false) {
      const ch = this.accessCodeForm.game_id;
      const multiChs = this.accessCodeAllowedChSelected;
      if (!ch && multiChs.length === 0) {
        alert("請先選擇 NPC 再生成網址");
        return;
      }
      let targetRoom = String(this.roomCode || "").trim();
      if (peerOnly === true && !targetRoom) {
        targetRoom = String(
          prompt("請輸入共同聊天室房間代碼（同組請使用相同代碼）", "") || ""
        ).trim();
        if (!targetRoom) return;
      }
      const base =
        peerOnly === true
          ? this.resolveFrontendBaseUrl()
          : "https://gptmega.ntustmeg.com/";
      let url = `${base}#/login?`;
      if (peerOnly === true) {
        url += `ch=${encodeURIComponent(ch || multiChs[0])}`;
      } else if (multiChs.length > 0) {
        const params = multiChs
          .map((c, i) => `ch${i + 1}=${encodeURIComponent(c)}`)
          .join("&");
        url += params;
      } else {
        url += `ch=${encodeURIComponent(ch)}`;
      }
      if (targetRoom) {
        url += `&room=${encodeURIComponent(targetRoom)}`;
      }
      if (peerOnly === true) {
        url += "&ref=noai";
      }
      navigator.clipboard
        .writeText(url)
        .then(() => {
          alert(`已複製網址：\n${url}`);
        })
        .catch(() => {
          prompt("請手動複製以下網址：", url);
        });
    },
    copyDocUrl() {
      const ch = this.accessCodeForm.game_id;
      const multiChs = this.accessCodeAllowedChSelected;
      if (!ch && multiChs.length === 0) {
        alert("請先選擇 NPC 再生成網址");
        return;
      }
      const roomId = this.docUrlRoomId;
      if (!roomId) {
        alert("請輸入房間 ID");
        return;
      }
      if (roomId.startsWith("solo")) {
        alert("房間 ID 不可以 solo 開頭");
        return;
      }
      const base = "https://gptmega.ntustmeg.com/";
      let url = `${base}#/login?`;
      if (multiChs.length > 0) {
        const params = multiChs
          .map((c, i) => `ch${i + 1}=${encodeURIComponent(c)}`)
          .join("&");
        url += params;
      } else {
        url += `ch=${encodeURIComponent(ch)}`;
      }
      url += `&room=${encodeURIComponent(roomId)}&doc=true`;
      navigator.clipboard
        .writeText(url)
        .then(() => {
          alert(`已複製網址：\n${url}`);
        })
        .catch(() => {
          prompt("請手動複製以下網址：", url);
        });
    },
    resolveFrontendBaseUrl(fallbackUrl = "https://gptmega.ntustmeg.com/") {
      const prefixMap = {
        npcrussia: "gptmega",
        devrussia: "devgpt",
        gptmega: "gptmega",
        devgpt: "devgpt",
        devnpc: "devgpt",
      };
      const { protocol, hostname, origin, pathname, port } = window.location;
      const hostParts = hostname.split(".");
      const hostPrefix = hostParts[0]?.toLowerCase();

      if (hostPrefix && prefixMap[hostPrefix]) {
        hostParts[0] = prefixMap[hostPrefix];
        const resolvedHost = hostParts.join(".");
        const resolvedPort = port ? `:${port}` : "";
        return `${protocol}//${resolvedHost}${resolvedPort}/`;
      }

      const pathSegments = pathname.split("/").filter(Boolean);
      const pathPrefix = pathSegments[0]?.toLowerCase();
      if (pathPrefix && prefixMap[pathPrefix]) {
        return `${origin}/${prefixMap[pathPrefix]}/`;
      }
      return fallbackUrl;
    },
    normalizeLayoutSetting(data) {
      const source = data?.data || data || {};
      return {
        text_size_level: source.text_size_level || "medium",
        text_color: source.text_color || "#2c3e50",
        background_color: source.background_color || "#ffffff",
        input_text_color: source.input_text_color || "#2c3e50",
        input_background_color: source.input_background_color || "#ffffff",
        button_color: source.button_color || "#007bff",
        ask_limit_background_color:
          source.ask_limit_background_color || "#007bff",
        guide_background_color: source.guide_background_color || "#17a2b8",
        messenger_style:
          source.messenger_style === true ||
          source.messenger_style === 1 ||
          source.messenger_style === "1" ||
          source.messenger_style === "true",
        messenger_other_bubble_color:
          source.messenger_other_bubble_color || "#f0f0f0",
        messenger_own_bubble_color:
          source.messenger_own_bubble_color || "#2f6bff",
      };
    },
    async getNPCLayout(gameId) {
      if (!gameId) return;
      try {
        const response = await axios.post(`${this.apiNetWork2}/getNPCLayout`, {
          game_id: gameId,
        });
        this.logApiNetWork2Response("getNPCLayout", response);
        this.layoutSetting = this.normalizeLayoutSetting(response.data);
      } catch (error) {
        console.warn("讀取對話框版面風格失敗，使用預設樣式:", error);
      }
    },
    async saveNPCLayout(gameId) {
      if (!gameId) {
        throw new Error("game_id is required");
      }
      const response = await axios.post(`${this.apiNetWork2}/setNPCLayout`, {
        game_id: gameId,
        ...this.layoutSetting,
      });
      this.logApiNetWork2Response("setNPCLayout", response);
    },
    async copyNumericalModeUrl() {
      // 在本機 HTTP 開發環境也要產生可直接開啟的網址；正式站則仍沿用網域轉換規則。
      const productionBase = this.resolveFrontendBaseUrl(
        `${window.location.origin}/`
      );
      const npcCode = String(this.accessCodeForm.game_id || "").trim();

      if (!npcCode) {
        alert("請先在通行密鑰管理選擇 NPC，再生成數值模式網址");
        return;
      }

      const params = new URLSearchParams();
      params.set("mode", "numerical");
      params.set("ch", npcCode);
      // 正式分享網址不綁定編輯器當下的 solo 代理房號；
      // 玩家進入後再由既有單人模式流程建立自己的代理房號。

      const url = `${productionBase}#/login?${params.toString()}`;

      // navigator.clipboard 僅在 HTTPS 或 localhost 等安全環境可用；
      // 以區網 IP（http://192.168.x.x）測試時要保留手動複製的備援。
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          alert(`已複製數值模式網址：\n${url}`);
          return;
        }
      } catch (error) {
        console.warn("無法寫入剪貼簿，改以手動複製網址：", error);
      }

      prompt("請手動複製以下數值模式網址：", url);
    },
    getGPTData(i) {
      var bePush = {
        name: i,
      };
      var requestUrl = `${this.apiNetWork2}/getNPCMega`;
      axios
        .post(requestUrl, bePush)
        .then((response) => {
          this.logApiNetWork2Response("getGPTData", response);
          var a = response.data;
          this.character = a.character;
          this.knowledge = a.knowledge;
          this.temperature = a.temp;
          this.maxTokens = a.token;
          this.replaceRule = JSON.parse(a.replace);
          this.showName = a.showName;
          this.name = a.name;
          this.startDateTime = a.startDateTime;
          this.endDateTime = a.endDateTime;
          this.initSC = JSON.parse(a.initSC);
          this.initStart = a.initStart;
          this.initPro = a.initPro;
          this.initRandom = a.initRandom;
          this.situationText = a.situationText;
          this.firstTalk = a.firstTalk;
          this.chatInitSC = this.initSC.slice();
          this.progressiveSC = JSON.parse(a.progressiveSC);
          this.loadDateTime(this.startDateTime, this.endDateTime);
          this.progressiveSCDb = JSON.parse(a.progressiveSC);
          this.autoSCSwitch = Boolean(a.autoSCSwitch);
          this.progressiveSCSwitch = Boolean(a.progressiveSCSwitch);
          this.initSwitch = Boolean(a.initSwitch);
          this.voiceSwitch = Number(a.voiceSwitch) === 1;
          this.voice = a.voice;
          this.ttsInstructions = a.ttsInstructions || "";
          this.ttsSpeed = a.ttsSpeed || null;
          this.autoSC = a.autoSC;
          this.autoSCProbability = a.autoSCProbability;
          this.autoMessagePrompt = a.autoMessagePrompt;
          this.hintName = a.hintName;
          this.hintName2 = a.hintName2;
          this.hintName3 = a.hintName3;

          this.askLimit = a.askLimit;
          this.askLimitCal = a.askLimit;

          this.scaleSC = a.scaleSC;
          this.scaleSCSwitch = Boolean(a.scaleSCSwitch);
          this.open = a.open;

          this.stepScaleSCSwitch = Boolean(a.stepScaleSCSwitch);
          this.stepScaleSC = a.stepScaleSC;
          this.stepScaleSCStep = a.stepScaleSCStep;
          this.hintName4 = a.hintName4;
          this.avatarUrl = a.avatar || "";
          this.recordSC = a.recordSC || "";
          this.recordSCStep =
            typeof a.recordSCStep === "number" ? a.recordSCStep : 0;

          this.endConSwitch = Boolean(a.endConSwitch);
          this.endCon = JSON.parse(a.endCon);
          this.endConDb = JSON.parse(a.endCon);
          this.typeSpeed = a.typeSpeed;
          this.docFeedbackSwitch = Boolean(a.docFeedbackSwitch);
          this.docFeedbackN = a.docFeedbackN || 10;
          this.docFeedbackPrompt = a.docFeedbackPrompt || "";
          this.getNPCLayout(a.name || i);
          this.applyExtendedNumericalNpcPayload(a);
          this.applyNumericalPreviewPayloadIfNeeded(a.name || i);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getGPTDataCheck(i) {
      if (this.isNumericalPreviewRoute) {
        return;
      }

      var bePush = {
        name: i,
      };
      var requestUrl = `${this.apiNetWork2}/getNPCMega`;
      axios
        .post(requestUrl, bePush)
        .then((response) => {
          this.logApiNetWork2Response("getGPTDataCheck", response);
          var a = response.data;
          if (!a.open) {
            Swal.fire("尚未開放");
            this.render = false;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    addInitSC() {
      this.initSC.push(this.initSCcontent);
      this.chatInitSC = this.initSC.slice();
    },
    removeInitSC(a) {
      this.initSC.splice(a, 1);
      this.chatInitSC = this.initSC.slice();
    },
    addProgressiveSC() {
      const bePush = {
        concept: this.progressiveSCConcept,
        q: parseInt(this.progressiveSCQ),
        qState: 0,
        a: parseInt(this.progressiveSCA),
        aState: 0,
        content: this.progressiveSCString,
        gen: this.progessiveSCGen,
        id: this.progressiveSCID,
      };
      // 先在原陣列新增
      this.progressiveSC.push(bePush);
      // 然後用 slice() 複製一份新的到 progressiveSCDb
      this.progressiveSCDb = this.progressiveSC.slice();

      // 清空輸入欄位
      this.progressiveSCConcept = "";
      this.progressiveSCQ = 0;
      this.progressiveSCA = 0;
      this.progessiveSCGen = false;
      this.progressiveSCString = "";
    },
    removeProgressiveSC(a) {
      this.progressiveSC.splice(a, 1);
      this.progressiveSCDb = this.progressiveSC.slice();
    },
    useProgressiveSC(msgs) {
      if (this.isPeerOnlyMode) return;
      // 檢查是否已經有相同的 progressiveSCID 在 msgs 中出現過
      const hasExistingId = msgs.some(
        (msg) =>
          msg.progressiveSCID && msg.progressiveSCID === this.progressiveSCID
      );

      // 如果已經有相同 ID，則不顯示鷹架
      if (hasExistingId) {
        return;
      }

      // 先拿最後一筆
      const last = msgs[msgs.length - 1];
      const content = last.content;
      const role = last.role;

      for (let i = 0; i < this.progressiveSCDb.length; i++) {
        const rule = this.progressiveSCDb[i];

        // 檢查最新訊息裡有沒有包含這個概念
        if (content.includes(rule.concept)) {
          // 只有使用者訊息才算一次
          if (role === "user") {
            rule.qState++;
          }
          if (role === "assistant") {
            rule.aState++;
          }
          // 到達設定的次數就觸發
          if (rule.qState >= rule.q && rule.aState >= rule.a) {
            // 組好要送出的 next prompt
            if (rule.gen == true) {
              const question = {
                role: "user",
                content: rule.content,
              };

              let payload;
              payload = {
                message: [this.mergeSystemCharacter(this.traceback), question],
                gpt: true,
                temperature: this.temperature,
                maxTokens: this.calcToken(),
                character: this.showName,
                hint: this.hintName2,
                room: this.roomCode,
              };

              // console.log("執行 post:", payload);
              this.startAnimationTimer();

              axios
                .post(
                  this.apiNetWork + "message",
                  this.withApiNetWorkPay(payload)
                )
                .then((response) => {
                  this.logAzureResponse("useProgressiveSC message", response);
                })
                .catch(console.error);
            } else {
              var b = [
                {
                  role: "assistant",
                  content: rule.content,
                  userName: this.hintName2,
                  progressiveSCID: this.progressiveSCID,
                },
              ];
              this.pushChatLog(b);
            }
            this.progressiveSCDb.splice(i, 1);
            i--;
          }
        }
      }
    },
    // 關於結束條件判斷
    addEndCon() {
      const bePush = {
        concept: this.endConConcept,
        q: parseInt(this.endConQ),
        qState: 0,
        a: parseInt(this.endConA),
        aState: 0,
        content: this.endConString,
      };
      // 先在原陣列新增
      this.endCon.push(bePush);
      this.endConDb = this.endCon.slice();

      // 清空輸入欄位
      this.endConConcept = "";
      this.endConQ = 0;
      this.endConA = 0;
      this.endConString = "";
    },
    removeEndCon(a) {
      this.endCon.splice(a, 1);
      this.endConDb = this.endCon;
    },
    useEndCon(msgs) {
      if (this.isPeerOnlyMode) return;
      // 先拿最後一筆
      const last = msgs[msgs.length - 1];
      const content = last.content;
      const role = last.role;

      for (let i = 0; i < this.endConDb.length; i++) {
        const rule = this.endConDb[i];

        // 檢查最新訊息裡有沒有包含這個概念
        if (content.includes(rule.concept)) {
          // 只有使用者訊息才算一次
          if (role === "user") {
            rule.qState++;
          }
          if (role === "assistant") {
            rule.aState++;
          }
          // 到達設定的次數就觸發
          if (rule.qState >= rule.q && rule.aState >= rule.a) {
            // 組好要送出的 next prompt

            var b = [
              {
                role: "assistant",
                content: rule.content,
                userName: this.showName,
              },
            ];
            setTimeout(() => {
              this.pushChatLog(b);
            }, 5000);
            this.endConDb.splice(i, 1);
            i--;
            this.endConTrigger = true;
          }
        }
      }
    },

    autoMessage() {
      if (this.isPeerOnlyMode) return;
      console.log("執行");

      var data = this.chatLog.filter((item) => item.role === "user");
      let a = false;
      // console.log("餘數", data.length % this.autoSC);
      // console.log(userMessages[userMessages.length - 1].ask);

      if (data.length % this.autoSC == 1 && data.length >= this.autoSC) {
        a = true;
      } else {
        return null;
      }

      if (data.length > 0) {
        var lastUserName = data[data.length - 1].userName;
        // console.log(data[data.length - 1]);
        if (lastUserName != this.userName) {
          a = false;
        }
        // if (data[data.length - 1].ask == true) {
        //   a = false;
        // }
      }

      if (a === true) {
        this.autoComment = 0;

        this.autoComment = this.autoComment + this.autoSC;
        if (Math.random() < (100 - this.autoSCProbability) / 100) {
          a = false;
        }
      }

      if (a) {
        var b;
        var requestUrl = this.apiNetWork + "message";

        var question = {
          role: "user",
          content: this.autoMessagePrompt,
        };

        b = {
          message: [this.mergeSystemCharacter(this.traceback), question],
          temperature: this.temperature,
          maxTokens: this.calcToken(),
          gpt: true,
          character: this.showName,
          hint: this.hintName3,
          room: this.roomCode,
        };
        this.startAnimationTimer();
        axios
          .post(requestUrl, this.withApiNetWorkPay(b))
          .then((response) => {
            this.logAzureResponse("autoMessage", response);
            return null;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    async triggerDocFeedback() {
      if (this.isPeerOnlyMode) return;
      console.log("[docFeedback] triggerDocFeedback() 進入", {
        currentDocContentLength: this.currentDocContent
          ? this.currentDocContent.length
          : 0,
      });
      if (!this.currentDocContent.trim()) {
        console.log("[docFeedback] currentDocContent 為空，中止觸發");
        return;
      }
      const prompt =
        this.docFeedbackPrompt +
        "\n\n以下是學生目前的文件內容：\n\n" +
        this.currentDocContent;
      const question = { role: "user", content: prompt };
      const payload = {
        message: [...this.chatLog, question],
        temperature: this.temperature,
        maxTokens: 1000,
        gpt: true,
      };
      try {
        const response = await axios.post(
          this.apiNetWork + "generalMessage",
          this.withApiNetWorkPay(payload)
        );
        this.logAzureResponse("triggerDocFeedback", response);
        const feedback = response.data?.data;
        console.log("[docFeedback] API 回應", { feedback });
        if (feedback) {
          this.chatLog.push({
            role: "assistant",
            content: feedback,
            userName: this.showName,
          });
          console.log("[docFeedback] 已將回饋加入 chatLog");
        }
      } catch (e) {
        console.error("[docFeedback] triggerDocFeedback error:", e);
      }
    },
    scaleMessage() {
      if (this.isPeerOnlyMode) return null;
      if (this.debug) {
        console.log("執行量表鷹架");
      }

      const shouldExecuteScale = this.scaleSCSwitch || !this.hideRoomCode;

      if (shouldExecuteScale && !this.endResult) {
        var b;
        var requestUrl = this.apiNetWork + "generalMessage";

        let scalePrompt;

        if (this.scaleSCSwitch) {
          scalePrompt = this.scaleSC;
        } else {
          // 生成包含所有對話內容的預設 prompt
          const chatHistory = this.chatLog
            .filter((msg) => !msg.hidden)
            .map((msg) => `${msg.userName || "系統"}: ${msg.content}`)
            .join("\n");

          scalePrompt = `以下是完整的對話記錄：\n\n${chatHistory}\n\n請分析並評估表現。`;
        }
        var question = {
          role: "user",
          content:
            scalePrompt +
            "，請用 markdown 語法格式列出內容，只要列出內容不需要回覆內容外的文字或註記(像是 ```markdown)",
        };
        b = {
          message: [...this.chatLog, question],
          temperature: 0,
          maxTokens: 2000,
          gpt: true,
        };

        axios
          .post(requestUrl, this.withApiNetWorkPay(b))
          .then((response) => {
            this.logAzureResponse("scaleMessage generalMessage", response);
            console.log(response.data.data);
            this.endResult = "評分鷹架：" + response.data.data;
            var recordData = {
              gameId: this.name,
              userId: this.userName,
              roomId: this.roomCode,
              chatName: this.showName + " 表現評估",
              chatContent: this.endResult,
              tokenCal: this.extractTokenCal(response?.data),
            };
            axios
              .post(`${this.apiNetWork2}/megaGPTRecord`, recordData)
              .then((response) => {
                this.logApiNetWork2Response(
                  "scaleMessage megaGPTRecord",
                  response
                );
              });
            return null;
          })
          .catch((error) => {
            console.log(error);
            this.endResult = "遊戲已結束，感謝您的參與！";
          });
      } else if (!this.endResult) {
        this.endResult = "遊戲已結束，感謝您的參與！";
      }

      this.endGame = true;
    },

    sendName() {
      if (this.userName != "") {
        this.$refs["nameModal"].hide();
        if (!this.isPeerOnlyMode && this.firstTalk.length > 0) {
          this.chatLog.push({
            role: "assistant",
            content: this.firstTalk,
            userName: this.showName,
            time:
              new Date().toLocaleDateString() +
              " " +
              new Date().toLocaleTimeString("en-US", { hour12: false }),
          });
        }

        // 在多人模式下，用戶輸入名稱後立即廣播加入
        if (!this.hideRoomCode && this.roomCode && this.isConnected) {
          this.broadcastUserJoined();
        }
      } else {
        alert("請輸入名稱");
      }
    },
    submitNumericalName() {
      if (!String(this.userName || "").trim()) {
        return;
      }

      this.sendName();
      this.numericalNameSubmitted = true;
    },
    normalizeNumericalPlayerCode(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    syncNumericalPlayerCodeFromInput(event) {
      this.numericalPlayerCode = this.normalizeNumericalPlayerCode(
        event?.target?.value
      );
    },
    commitNumericalPlayerCodeInput() {
      const input = this.$refs.numericalPlayerCodeInput;
      const inputValue = Array.isArray(input) ? input[0]?.value : input?.value;

      this.numericalPlayerCode = this.normalizeNumericalPlayerCode(
        inputValue ?? this.numericalPlayerCode
      );
    },
    onAskLimitInput() {
      if (this.demoMode == false) {
        this.askLimitCal = this.askLimit;
      }
    },
    toogleOpen() {
      this.open = !this.open;
    },
    // 手動結束遊戲
    forceEndGame() {
      if (confirm("確定要結束遊戲嗎？這將顯示遊戲結束畫面。")) {
        // 多人模式且需要生成評估時，先生成評估再廣播
        if (!this.hideRoomCode) {
          this.generateAndBroadcastResult();
        } else {
          // 單人模式直接結束
          this.executeGameOver(true);
        }
      }
    },

    // 生成評估結果並廣播給所有人
    async generateAndBroadcastResult() {
      if (this.isPeerOnlyMode) return;
      try {
        const { result: evaluationResult, tokenCal: evaluationTokenCal } =
          await this.generateEvaluation();

        // 由觸發者寫入指標性總結評估結果到 DB
        axios
          .post(`${this.apiNetWork2}/megaGPTRecord`, {
            gameId: this.name,
            userId: this.userName,
            roomId: this.roomCode,
            chatName: this.showName + " 表現評估",
            chatContent: evaluationResult,
            tokenCal: evaluationTokenCal,
          })
          .then((response) => {
            this.logApiNetWork2Response(
              "generateAndBroadcastResult megaGPTRecord",
              response
            );
          })
          .catch(() => {});

        const gameOverMessage = {
          message: [
            {
              role: "user",
              content: `遊戲已結束||評估結果||${evaluationResult}`,
              userName: "gameover", // 特殊標記
              room: this.roomCode,
            },
          ],
          temperature: this.temperature,
          maxTokens: this.calcToken(),
          gpt: false,
          character: this.showName,
          room: this.roomCode,
          hint: null,
          voice: null,
        };

        // 發送遊戲結束訊息
        const response = await axios.post(
          this.apiNetWork + "message",
          this.withApiNetWorkPay(gameOverMessage)
        );
        this.logAzureResponse("generateAndBroadcastResult message", response);
        console.log("遊戲結束訊息和評估結果已發送");
      } catch (error) {
        console.error("生成評估或發送訊息失敗:", error);
        this.broadcastGameOverWithDefaultResult();
      }
    },

    // 生成評估結果（不設置本地狀態）
    async generateEvaluation() {
      if (this.isPeerOnlyMode) {
        return { result: "", tokenCal: null };
      }
      // 生成包含所有對話內容的 prompt
      let scalePrompt;

      if (this.scaleSCSwitch && this.scaleSC) {
        // 開關啟用且有設定自訂 prompt，使用自訂的
        scalePrompt = this.scaleSC;
      } else {
        const chatHistory = this.chatLog
          .filter((msg) => !msg.hidden)
          .map((msg) => `${msg.userName || "系統"}: ${msg.content}`)
          .join("\n");

        scalePrompt = `以下是完整的對話記錄：\n\n${chatHistory}\n\n請分析並評估表現。`;
      }

      const requestData = {
        message: [
          ...this.chatLog,
          {
            role: "user",
            content: scalePrompt,
          },
        ],
        temperature: 0,
        maxTokens: 2000,
        gpt: true,
      };

      const response = await axios.post(
        this.apiNetWork + "generalMessage",
        this.withApiNetWorkPay(requestData)
      );
      this.logAzureResponse("generateEvaluation generalMessage", response);
      return {
        result:
          "評分鷹架：" + (response.data.data || "遊戲已結束，感謝您的參與！"),
        tokenCal: this.extractTokenCal(response?.data),
      };
    },

    // 用預設結果廣播遊戲結束
    broadcastGameOverWithDefaultResult() {
      const gameOverMessage = {
        message: [
          {
            role: "user",
            content: "遊戲已結束||評估結果||遊戲已結束，感謝您的參與！",
            userName: "gameover",
            room: this.roomCode,
          },
        ],
        temperature: this.temperature,
        maxTokens: this.calcToken(),
        gpt: false,
        character: this.showName,
        room: this.roomCode,
        hint: null,
        voice: null,
      };

      axios
        .post(
          this.apiNetWork + "message",
          this.withApiNetWorkPay(gameOverMessage)
        )
        .then((response) => {
          this.logAzureResponse(
            "broadcastGameOverWithDefaultResult message",
            response
          );
        })
        .catch((error) => {
          console.error("發送預設遊戲結束訊息失敗:", error);
          // 最後的備案：本地結束
          this.executeGameOver(true);
        });
    },

    // 執行遊戲結束邏輯
    executeGameOver(isManualEnd = false, receivedResult = null) {
      this.endGame = true;
      this.askLimitCal = 0;

      if (isManualEnd) {
        this.manualEndGame = true; // 標記為手動結束
      }

      if (receivedResult) {
        this.endResult = receivedResult;
        return;
      }

      // 判斷是否顯示表現評估
      const shouldShowScale =
        this.scaleSCSwitch || (isManualEnd && !this.hideRoomCode);

      // 只有在需要顯示評估且還沒有評估結果時才生成
      if (shouldShowScale && this.hideRoomCode) {
        this.endResult = "";
        this.scaleMessage();
      }
    },
    async linkChat() {
      let ws;
      const self = this;

      function safeJSONParse(input) {
        try {
          return typeof input === "string" ? JSON.parse(input) : input;
        } catch (e) {
          console.warn("WS payload 不是合法 JSON：", input);
          return null;
        }
      }

      async function connect(changeIsConnected, messageRoomCheck) {
        try {
          const res = await fetch(self.apiNetWork + "negotiate");
          if (!res.ok) {
            throw new Error(
              `Failed to fetch negotiate endpoint: ${res.statusText}`
            );
          }
          const data = await res.json();
          self.logAzureResponse("linkChat negotiate", res, data);
          const url = data.url;

          ws = new WebSocket(url);
          ws.onopen = () => {
            changeIsConnected(true);
            // 連接成功後，在多人模式下廣播自己的加入
            if (!self.hideRoomCode && self.roomCode) {
              setTimeout(() => {
                // 再次檢查 userName 是否有效
                if (self.userName && self.userName.trim() !== "") {
                  self.broadcastUserJoined();
                }
              }, 1000);
            }
          };

          ws.onmessage = (event) => {
            const msg = safeJSONParse(event.data);
            if (!msg) return;
            messageRoomCheck(msg);
          };

          ws.onclose = () => {
            changeIsConnected(false);
            setTimeout(
              () => connect(changeIsConnected, messageRoomCheck),
              5000
            ); // ✅ 帶回參數
          };

          ws.onerror = (error) => {
            console.error("WebSocket error:", error);
          };
        } catch (error) {
          console.error("Error:", error);
        }
      }

      connect(this.changeIsConnected, this.messageRoomCheck);
    },

    messageRoomCheck(a) {
      if (Array.isArray(a)) {
        a.forEach((item) => this.messageRoomCheck(item));
        return;
      }

      if (a.room === this.roomCode) {
        if (this.isPeerOnlyMode) {
          // 不接收一般 NPC 模式的回覆、評估或遊戲結束廣播。
          if (
            a.role === "user" &&
            ![
              "system_user_joined",
              "system_ask_limit_update",
              "gameover",
            ].includes(a.userName)
          ) {
            this.pushChatLog([a]);
          }
          return;
        }
        // 處理用戶加入房間的廣播
        if (a.userName === "system_user_joined") {
          try {
            const payload = JSON.parse(a.content);
            // 更新 roomPlayersAskLimit，記錄該用戶的提問次數
            this.roomPlayersAskLimit[payload.userName] = payload.askLimitCal;
          } catch (e) {
            console.error("解析用戶加入訊息失敗:", e);
          }
          return;
        }

        if (a.userName === "gameover") {
          // 檢查訊息是否包含評估結果
          let evaluationResult = null;
          if (a.content && a.content.includes("||評估結果||")) {
            const parts = a.content.split("||評估結果||");
            if (parts.length >= 2) {
              evaluationResult = parts[1];
            }
          }

          this.executeGameOver(true, evaluationResult);
          return;
        }

        // 處理玩家提問次數更新的廣播
        if (a.userName === "system_ask_limit_update" && a.content) {
          try {
            const data = JSON.parse(a.content);
            if (data.userName && typeof data.askLimitCal === "number") {
              this.roomPlayersAskLimit[data.userName] = data.askLimitCal;
            }
          } catch (e) {
            console.warn("解析提問次數更新失敗", e);
          }
          return;
        }

        if (a.room === this.roomCode) {
          this.pushChatLog([a]);

          if (this.askLimit > 0 && !this.endGame) {
            if (!this.hideRoomCode) {
              // 多人模式：只有最後發言者判斷，避免所有客戶端同時觸發導致重複寫入
              if (
                this.lastSenderName === this.userName &&
                this.checkAllPlayersFinished()
              ) {
                this.generateAndBroadcastResult();
              }
            } else {
              if (this.askLimitCal <= 0 && a.role === "assistant") {
                this.executeGameOver(false);
              }
            }
          }

          if (this.chatInitSC.length > 0 && this.initSwitch) {
            this.initHint();
          }
        }
      }
    },

    changeIsConnected(status) {
      // status = true 代表連線成功，false 代表斷線
      this.isConnected = status;
    },

    // 廣播自己的提問次數更新
    broadcastAskLimitUpdate() {
      const updateMessage = {
        message: [
          {
            role: "user",
            content: JSON.stringify({
              userName: this.userName,
              askLimitCal: this.askLimitCal,
            }),
            userName: "system_ask_limit_update", // 特殊標記:系統提問次數更新廣播
            room: this.roomCode,
          },
        ],
        temperature: 0,
        maxTokens: 100,
        gpt: false,
        character: this.showName,
        room: this.roomCode,
        hint: null,
        voice: null,
      };

      axios
        .post(
          this.apiNetWork + "message",
          this.withApiNetWorkPay(updateMessage)
        )
        .then((response) => {
          this.logAzureResponse("broadcastAskLimitUpdate message", response);
        })
        .catch((error) => {
          console.error("廣播提問次數更新失敗:", error);
        });
    },

    // 廣播用戶加入房間
    broadcastUserJoined() {
      // 檢查 userName 是否有效
      if (!this.userName || this.userName.trim() === "") {
        return;
      }

      // 初始化自己在 roomPlayersAskLimit 中的記錄
      this.roomPlayersAskLimit[this.userName] = this.askLimitCal;

      const joinMessage = {
        message: [
          {
            role: "user",
            content: JSON.stringify({
              userName: this.userName,
              askLimitCal: this.askLimitCal,
            }),
            userName: "system_user_joined", // 特殊標記:用戶加入房間廣播
            room: this.roomCode,
          },
        ],
        temperature: 0,
        maxTokens: 100,
        gpt: false,
        character: this.showName,
        room: this.roomCode,
        hint: null,
        voice: null,
      };

      axios
        .post(this.apiNetWork + "message", this.withApiNetWorkPay(joinMessage))
        .then((response) => {
          this.logAzureResponse("broadcastUserJoined message", response);
        })
        .catch((error) => {
          console.error("廣播用戶加入失敗:", error);
        });
    },

    // 檢查房間內所有玩家是否都用完提問次數
    checkAllPlayersFinished() {
      // 更新自己的次數
      this.roomPlayersAskLimit[this.userName] = this.askLimitCal;

      // 獲取所有玩家的次數
      const allCounts = Object.values(this.roomPlayersAskLimit);

      // 如果房間內只有自己，直接檢查自己
      if (allCounts.length === 0) {
        return this.askLimitCal <= 0;
      }

      // 檢查是否所有人都 <= 0
      return allCounts.every((count) => count <= 0);
    },

    mergeSystemCharacter(a) {
      var chatLogMix = this.chatLog.slice(0 - a);
      var chatLogMixOutPut = "";
      chatLogMix.forEach((element) => {
        var t = "";
        if (element.role != "system") {
          if (element.role == "assistant") {
            t = "你";
          } else if (element.role == "user") {
            t = "組員" + element.userName;
          }
          chatLogMixOutPut =
            chatLogMixOutPut + "," + t + "說:" + element.content;
        }
      });

      var systemCharacter = {
        role: "system",
        content:
          this.character +
          " " +
          this.knowledge +
          " 以下是你跟使用者(一位或多位)的聊天室對話紀錄" +
          chatLogMixOutPut,
      };
      return systemCharacter;
    },
    startAnimationTimer() {
      // 如果已經有計時器，就先清掉
      if (this.beforeTimerId !== null) {
        clearTimeout(this.beforeTimerId);
        this.beforeTimerId = null;
      }

      if (this.timerId !== null) {
        clearTimeout(this.timerId);
      }
      this.beforeTimerId = setTimeout(() => {
        this.waitAnimation = true;
        this.beforeTimerId = null;

        // 再啟動 60 秒計時，到時候把 waitAnimation 關掉
        this.timerId = setTimeout(() => {
          this.waitAnimation = false;
          this.timerId = null;
        }, 8000);
      }, 3000);
    },
    stopAnimationTimer() {
      this.waitAnimation = false;

      if (this.beforeTimerId !== null) {
        clearTimeout(this.beforeTimerId);
        this.beforeTimerId = null;
      }
      // 中斷 60 秒計時
      if (this.timerId !== null) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
    setRoomCode() {
      Swal.fire({
        title: "請輸入房號",
        input: "text",
        inputLabel: "任意英數",
        inputValue: "",
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return "You need to write something!";
          }
          this.roomCode = value;
          this.hideRoomCode = false;
        },
      });
    },

    // 搜尋聊天數據
    async searchChatData(isNewSearch = false) {
      this.isLoadingChatData = true;
      if (isNewSearch) {
        this.chatDataSearched = false;
      }

      try {
        // 轉換時間格式為 ISO 格式
        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }

        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
          startTime: startTimeISO,
          endTime: endTimeISO,
          page: this.currentPage,
          pageSize: this.pageSize,
        };

        // 移除 undefined 值
        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );
        this.logApiNetWork2Response("searchChatData", response);

        // 處理後端回應
        if (response.data) {
          let results = [];

          if (Array.isArray(response.data)) {
            results = response.data;
          } else {
            results = response.data.data || [];
          }

          // 時間過濾
          if (startTimeISO || endTimeISO) {
            results = results.filter((item) => {
              if (!item.timestamp) return true;

              const itemTime = new Date(item.timestamp).getTime();

              if (startTimeISO) {
                const startMs = new Date(startTimeISO).getTime();
                if (itemTime < startMs) return false;
              }

              if (endTimeISO) {
                const endMs = new Date(endTimeISO).getTime();
                if (itemTime > endMs) return false;
              }

              return true;
            });
          }

          this.chatDataResults = results;
          this.total = results.length;
          this.totalPages = Math.ceil(results.length / this.pageSize);

          this.chatDataSearched = true;

          if (isNewSearch) {
            if (this.chatDataResults.length > 0) {
              alert(`找到 ${this.total} 筆聊天記錄`);
            } else {
              alert("沒有找到符合條件的聊天記錄");
            }
          }
        }
      } catch (error) {
        console.error("查詢聊天數據失敗:", error);
        alert("查詢失敗，請檢查網路連線或聯絡管理員");
      } finally {
        this.isLoadingChatData = false;
      }
    },

    // 格式化 ISO 時間戳
    formatTimestampISO(timestamp) {
      if (!timestamp) return "";
      try {
        const date = new Date(timestamp);
        return date.toLocaleString("zh-TW", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch (error) {
        console.error("時間格式化錯誤:", error);
        return timestamp;
      }
    },

    withApiNetWorkPay(payload = {}) {
      if (!this.apiNetWorkPay) return payload;
      return {
        ...payload,
        pay: payload.pay ?? this.apiNetWorkPay,
      };
    },

    calculateManualCost() {
      const hasManualValue =
        this.costInputAmount !== "" || this.costOutputAmount !== "";

      if (!hasManualValue) {
        this.costTotalAmount = "";
        this.costCalculated = false;
        alert("請先輸入金額");
        return;
      }

      let totalP = 0;
      let totalT = 0;
      this.chatDataResults.forEach((item) => {
        totalP += parseFloat(this.parseTokenCal(item.tokenCal, "p")) || 0;
        totalT += parseFloat(this.parseTokenCal(item.tokenCal, "t")) || 0;
      });

      const inputPricePerM = Math.max(parseFloat(this.costInputAmount) || 0, 0);
      const outputPricePerM = Math.max(
        parseFloat(this.costOutputAmount) || 0,
        0
      );
      const inputCost = (totalP / 1000000) * inputPricePerM;
      const outputCost = (totalT / 1000000) * outputPricePerM;

      this.costTotalAmount = (inputCost + outputCost).toFixed(6);
      this.costCalculated = true;
    },

    // 清空查詢條件與結果（僅負責重置，不做任何匯出或請求）
    clearChatDataQuery() {
      this.chatDataQuery = {
        gameId: "",
        userId: "",
        startTime: "",
        endTime: "",
      };
      this.chatDataResults = [];
      this.chatDataSearched = false;
      this.currentPage = 1;
      this.total = 0;
      this.totalPages = 0;
      this.costInputAmount = "";
      this.costOutputAmount = "";
      this.costTotalAmount = "";
      this.costCalculated = false;
    },

    // 匯出 CSV (原始資料)
    async exportChatData(event) {
      if (this.total === 0) {
        alert("沒有數據可以匯出");
        return;
      }

      const button = event.target;
      const originalText = button.textContent;

      try {
        button.disabled = true;
        button.textContent = "匯出中...";

        // 準備時間過濾參數
        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }
        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        // 準備請求參數
        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
        };

        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        // 一次獲取所有數據
        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );
        this.logApiNetWork2Response("exportChatData", response);

        let allData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            allData = response.data;
          } else if (response.data.data) {
            allData = response.data.data;
          }
        }

        // 前端時間過濾
        if (startTimeISO || endTimeISO) {
          allData = allData.filter((item) => {
            if (!item.timestamp) return true;
            const itemTime = new Date(item.timestamp).getTime();
            if (startTimeISO && itemTime < new Date(startTimeISO).getTime())
              return false;
            if (endTimeISO && itemTime > new Date(endTimeISO).getTime())
              return false;
            return true;
          });
        }

        if (allData.length === 0) {
          alert("沒有符合條件的數據可以匯出");
          return;
        }

        // 生成 CSV 內容
        const headers = [
          "時間",
          "遊戲ID",
          "用戶ID",
          "房間ID",
          "發言者",
          "對話內容",
        ];
        const csvRows = [headers.join(",")];

        allData.forEach((item) => {
          const row = [
            item.timestamp || "",
            item.gameId || "",
            item.userId || "",
            item.roomId || "",
            item.chatName || "",
            `"${(item.chatContent || "").replace(/"/g, '""')}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvContent = "\uFEFF" + csvRows.join("\n"); // 加入 BOM 以支援中文
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `聊天記錄_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        alert(`CSV 文件已下載（共 ${allData.length} 筆記錄）`);
      } catch (error) {
        console.error("匯出 CSV 失敗:", error);
        alert("匯出失敗，請檢查網路連線或聯絡管理員");
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    },

    // 匯出 Excel (整理後資料)
    async exportExcelData(event) {
      if (this.total === 0) {
        alert("沒有數據可以匯出");
        return;
      }

      const button = event.target;
      const originalText = button.textContent;

      try {
        button.disabled = true;
        button.textContent = "匯出中...";

        // 準備時間過濾參數
        let startTimeISO = undefined;
        let endTimeISO = undefined;

        if (this.chatDataQuery.startTime) {
          startTimeISO = new Date(this.chatDataQuery.startTime).toISOString();
        }
        if (this.chatDataQuery.endTime) {
          endTimeISO = new Date(this.chatDataQuery.endTime).toISOString();
        }

        // 準備請求參數
        const requestData = {
          gameId: this.chatDataQuery.gameId || undefined,
          userId: this.chatDataQuery.userId || undefined,
        };

        Object.keys(requestData).forEach((key) => {
          if (requestData[key] === undefined || requestData[key] === "") {
            delete requestData[key];
          }
        });

        // 一次獲取所有數據
        const response = await axios.post(
          `${this.apiNetWork2}/queryMegaGPTRecord`,
          requestData
        );
        this.logApiNetWork2Response("exportExcelData", response);

        let allData = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            allData = response.data;
          } else if (response.data.data) {
            allData = response.data.data;
          }
        }

        // 前端時間過濾
        if (startTimeISO || endTimeISO) {
          allData = allData.filter((item) => {
            if (!item.timestamp) return true;
            const itemTime = new Date(item.timestamp).getTime();
            if (startTimeISO && itemTime < new Date(startTimeISO).getTime())
              return false;
            if (endTimeISO && itemTime > new Date(endTimeISO).getTime())
              return false;
            return true;
          });
        }

        if (allData.length === 0) {
          alert("沒有符合條件的數據可以匯出");
          return;
        }

        // 依照房間名稱分類數據
        const roomData = {};
        allData.forEach((item) => {
          const roomId = item.roomId || "未知房間";
          if (!roomData[roomId]) {
            roomData[roomId] = [];
          }
          roomData[roomId].push(item);
        });

        // 對每個房間的數據按時間排序
        Object.keys(roomData).forEach((roomId) => {
          roomData[roomId].sort((a, b) => {
            return new Date(a.timestamp) - new Date(b.timestamp);
          });
        });

        // 準備 Excel 數據結構
        const workbook = XLSX.utils.book_new();

        Object.keys(roomData).forEach((roomId) => {
          const sheetData = roomData[roomId].map((item, index) => ({
            序號: index + 1,
            時間: item.timestamp,
            遊戲ID: item.gameId || "",
            用戶ID: item.userId || "",
            發言者: item.chatName || "",
            對話內容: item.chatContent || "",
          }));

          // 創建工作表
          const worksheet = XLSX.utils.json_to_sheet(sheetData);

          // 設定欄位寬度
          const colWidths = [
            { wch: 6 }, // 序號
            { wch: 20 }, // 時間
            { wch: 15 }, // 遊戲ID
            { wch: 15 }, // 用戶ID
            { wch: 12 }, // 發言者
            { wch: 50 }, // 對話內容
          ];
          worksheet["!cols"] = colWidths;

          let sheetName = roomId.replace(/[\\/*?[\]:]/g, "_");
          if (sheetName.length > 31) {
            sheetName = sheetName.substring(0, 31);
          }

          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        button.textContent = "生成 Excel 中...";

        const fileName = `聊天記錄_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        alert(
          `Excel 文件已下載（共 ${allData.length} 筆記錄，分為 ${
            Object.keys(roomData).length
          } 個房間）`
        );
      } catch (error) {
        console.error("匯出 Excel 失敗:", error);
        alert("匯出失敗，請檢查網路連線或聯絡管理員");
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    },

    useStepScale() {
      if (this.isPeerOnlyMode) return;
      if (!this.stepScaleSCSwitch) return;

      // 多人模式下只有最後發言者觸發，避免 N 個客戶端各自呼叫 AI API 與寫入 DB
      if (!this.hideRoomCode && this.lastSenderName !== this.userName) return;

      const n = parseInt(this.stepScaleSCStep, 10);
      if (!Number.isFinite(n) || n <= 0) return;

      const currentChatCount = this.getTotalTurnCount();

      const hit = currentChatCount > 0 && currentChatCount % n === 0;
      if (!hit) return;

      // 檢查 chatLog 中已存在的 step scale 回合 ID，找出最大的數字
      const existingRounds = this.chatLog
        .filter(
          (msg) =>
            msg.stepScaleID && msg.stepScaleID.startsWith("step_scale_round_")
        )
        .map((msg) => {
          const match = msg.stepScaleID.match(/step_scale_round_(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        });

      const maxExistingRound =
        existingRounds.length > 0 ? Math.max(...existingRounds) : 0;

      // 更新 lastStepScaleFiredAt 為已存在的最大值
      if (maxExistingRound > this.lastStepScaleFiredAt) {
        this.lastStepScaleFiredAt = maxExistingRound;
      }

      if (this.lastStepScaleFiredAt === currentChatCount) return;
      this.lastStepScaleFiredAt = currentChatCount;
      const currentID = `step_scale_round_${currentChatCount}`;
      const alreadyExists = this.chatLog.some(
        (msg) => msg.stepScaleID === currentID
      );
      if (alreadyExists) return;

      const hintName =
        this.hintName4 && this.hintName4.trim() ? this.hintName4 : "[提示]";

      const question = {
        role: "user",
        content: typeof this.stepScaleSC === "string" ? this.stepScaleSC : "",
      };

      const visibleChat = Array.isArray(this.chatLog)
        ? this.chatLog.filter((m) => !m.hidden)
        : [];

      const payload = {
        message: [...visibleChat, question],
        temperature: 0,
        maxTokens: 2000,
        gpt: true,
      };

      axios
        .post(
          this.apiNetWork + "generalMessage",
          this.withApiNetWorkPay(payload)
        )
        .then((res) => {
          this.logAzureResponse("useStepScale generalMessage", res);
          const text = res?.data?.data ?? this.stepScaleSC;
          const broadcastMessage = {
            message: [
              {
                role: "assistant",
                content: text,
                userName: hintName,
                isStepScale: true,
                stepScaleID: currentID,
                mark: 1,
                room: this.roomCode,
              },
            ],
            gpt: false,
            temperature: 0,
            maxTokens: 2000,
            character: this.showName,
            room: this.roomCode,
            hint: null,
            voice: null,
          };

          axios
            .post(
              this.apiNetWork + "message",
              this.withApiNetWorkPay(broadcastMessage)
            )
            .then((response) => {
              this.logAzureResponse("useStepScale broadcast message", response);
            })
            .catch(console.error);
        })
        .catch(() => {
          const broadcastMessage = {
            message: [
              {
                role: "assistant",
                content: "",
                userName: hintName,
                isStepScale: true,
                stepScaleID: currentID,
                mark: 1,
                room: this.roomCode,
              },
            ],
            gpt: false,
            character: this.showName,
            room: this.roomCode,
          };
          axios
            .post(
              this.apiNetWork + "message",
              this.withApiNetWorkPay(broadcastMessage)
            )
            .then((response) => {
              this.logAzureResponse(
                "useStepScale fallback broadcast message",
                response
              );
              // 階段性鷹架廣播成功
            })
            .catch((err) => {
              console.error("階段性鷹架廣播失敗", err);
            });
        })
        .catch((err) => {
          console.error("生成階段性鷹架失敗", err);
        });
    },

    async triggerRecordAnalysis() {
      if (this.isPeerOnlyMode) return;
      try {
        if (!this.recordSC) {
          console.log("[triggerRecordAnalysis] 跳過：recordSC 未設定");
          return;
        }

        // 多人模式下只有最後發言者觸發，避免 N 個客戶端各自呼叫 AI API 與寫入 DB
        if (!this.hideRoomCode && this.lastSenderName !== this.userName) {
          console.log(
            `[triggerRecordAnalysis] 跳過：多人模式，lastSenderName(${this.lastSenderName}) !== userName(${this.userName})`
          );
          return;
        }

        const n = Number(this.recordSCStep);
        if (!Number.isFinite(n) || n <= 0) {
          console.log(
            `[triggerRecordAnalysis] 跳過：recordSCStep 無效 (${this.recordSCStep})`
          );
          return;
        }

        const turns = this.getTotalTurnCount();
        const gap = turns - (this.lastRecordFiredAt || 0);
        console.log(
          `[triggerRecordAnalysis] turns=${turns}, lastFiredAt=${this.lastRecordFiredAt}, gap=${gap}, n=${n}`
        );

        if (!Number.isFinite(turns) || turns <= 0) return;
        if (gap < n) return;

        this.lastRecordFiredAt = turns;
        console.log("[triggerRecordAnalysis] 觸發分析，呼叫 AI API...");

        // 用不含 hidden 的對話送去分析，避免循環
        const filteredChat = Array.isArray(this.chatLog)
          ? this.chatLog.filter((i) => !i.hidden)
          : [];

        const question = { role: "user", content: String(this.recordSC || "") };
        const payload = {
          message: [...filteredChat, question],
          temperature: 0,
          maxTokens: 2000,
          gpt: true,
        };

        const res = await axios.post(
          this.apiNetWork + "generalMessage",
          this.withApiNetWorkPay(payload)
        );
        this.logAzureResponse("triggerRecordAnalysis generalMessage", res);
        const text =
          res && res.data && (res.data.data || res.data)
            ? res.data.data || res.data
            : this.recordSC || "";

        const recordText = `${text}`;
        console.log(
          "[triggerRecordAnalysis] 分析完成:",
          recordText.slice(0, 80)
        );
      } catch (err) {
        console.error("[triggerRecordAnalysis] 發生錯誤：", err);
      }
    },

    maybeAnalyze() {
      if (this.isPeerOnlyMode) return;
      if (this.demoMode !== false) return;
      if (!this.stepScaleSCSwitch) return;
      const step = Number(this.stepScaleSCStep) || 0;
      if (step === 0) return;

      const n = (this.chatLog && this.chatLog.length) || 0;
      if (n > 0 && n % step === 0) {
        const text = (this.stepScaleSC || "").trim();
        if (text) this.analysisLogs.push(text);
      }
    },

    // 處理圖片載入錯誤
    onImageError(event) {
      console.warn("頭像載入失敗:", this.avatarUrl);
      event.target.style.display = "none";
    },

    //數值鷹架
    addNumericalSC() {
      const bePush = {
        position: this.numericalSCPosition,
        operator: this.numericalSCOperator,
        number: parseInt(this.numericalSCNumber),
        content: this.numericalSCString,
        gen: this.numericalSCGen,
        id: this.numericalSCID,
      };
      this.numericalSC.push(bePush);
      this.numericalSCDb = this.numericalSC.slice();
      this.numericalSCPosition = "";
      this.numericalSCOperator = ">";
      this.numericalSCNumber = 0;
      this.numericalSCGen = false;
      this.numericalSCString = "";
      this.numericalSCID = Math.floor(Math.random() * 1000000) + Date.now();
    },
    removeNumericalSC(number) {
      this.numericalSC.splice(number, 1);
      this.numericalSCDb = this.numericalSC.slice();
    },
    useNumericalSC(change) {
      const position = this.normalizeSheetCellPosition(change?.a1Notation);
      if (!position || !Array.isArray(this.numericalSCDb)) return;

      for (let i = 0; i < this.numericalSCDb.length; i++) {
        const rule = this.numericalSCDb[i];
        const rulePosition = this.normalizeSheetCellPosition(rule?.position);

        if (position !== rulePosition) continue;

        const isMatched = this.compareNumericalCondition(
          change?.newValue,
          rule?.operator,
          rule?.number
        );

        if (!isMatched) continue;

        const promptContent = this.buildNumericalPromptContent(rule);
        const promptContext = this.buildNumericalPromptContext(change);

        if (rule.gen === true) {
          const question = {
            role: "user",
            content: `${promptContent}\n\n請根據以下試算表變更資訊決定提示內容，但最終回覆只輸出給玩家看的提示，不要重述欄位、原始值、目前值、編輯者等資訊。\n\n${promptContext}`,
          };

          const payload = {
            message: [this.mergeSystemCharacter(this.traceback), question],
            gpt: true,
            temperature: this.temperature,
            maxTokens: this.calcToken(),
            character: this.showName,
            hint: this.hintName5,
            room: this.roomCode,
          };

          this.startAnimationTimer();

          axios
            .post(this.apiNetWork + "message", this.withApiNetWorkPay(payload))
            .then((response) => {
              this.logAzureResponse("useNumericalSC message", response, change);
            })
            .catch((error) => {
              console.error("數值鷹架生成失敗:", error);
            });
        } else {
          const hintMessage = [
            {
              role: "assistant",
              content: promptContent,
              userName: this.hintName5,
              numericalSCID: rule.id,
            },
          ];
          this.pushChatLog(hintMessage);
        }

        this.numericalSCDb.splice(i, 1);
        i--;
      }
    },
    async onSubmitSheet() {
      const sheetId = String(this.numericalSCSheetID || "").trim();
      const range = String(this.numericalSCSheetRange || "").trim();

      if (!sheetId) {
        this.numericalSheetSubmitMessage = "目前無法讀取玩家試算表。";
        return;
      }

      if (!this.numericalBaselineReady) {
        this.numericalSheetSubmitMessage =
          this.numericalBaselineError || "初始資料尚未載入完成，請稍後再試。";
        return;
      }

      this.isLoadingNumericalSheet = true;
      this.numericalSheetSubmitMessage = "";

      try {
        const sheetData = await this.fetchSheetData(sheetId, range);
        const nextSnapshot = this.buildSheetSnapshot(sheetData);
        const previousSnapshot = this.numericalSheetSnapshot || {};

        const changes = this.extractSheetChanges(
          sheetData,
          nextSnapshot,
          previousSnapshot
        );

        this.numericalSheetSnapshot = nextSnapshot;

        if (changes.length === 0) {
          this.numericalSheetChanges = [];
          this.numericalSheetSubmitMessage = "已完成本回合檢查。";
          return;
        }

        changes.forEach((change) => this.handleSheetEdit(change));
        this.numericalSheetSubmitMessage = "已完成本回合檢查。";
      } catch (error) {
        console.error("讀取 Google Sheet 失敗:", error);
        this.numericalSheetSubmitMessage =
          error?.response?.data?.message ||
          error?.message ||
          "讀取試算表失敗，請稍後再試。";
      } finally {
        this.isLoadingNumericalSheet = false;
      }
    },

    // ===== 數值模式延伸支援 =====
    buildExtendedNumericalNpcPayload() {
      return {
        numericalSCSwitch: this.numericalSCSwitch,
        hintName5: this.hintName5,
        numericalSC: JSON.stringify(this.numericalSC || []),
        numericalSCSheetID: this.numericalSCSheetID,
        numericalSCSheetRange: this.numericalSCSheetRange,
        numericalTaskIframeUrl: this.normalizeNumericalTaskIframeUrl(
          this.numericalTaskIframeUrl
        ),
      };
    },

    safeParseExtendedNpcArray(value) {
      if (Array.isArray(value)) return value;
      if (!value) return [];

      try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn("數值模式設定解析失敗:", error);
        return [];
      }
    },

    extractExtendedNumericalNpcPayload(rawNpc = {}) {
      return {
        numericalSCSwitch:
          rawNpc?.numericalSCSwitch == null
            ? null
            : Boolean(rawNpc.numericalSCSwitch),
        hintName5:
          rawNpc?.hintName5 == null || rawNpc?.hintName5 === ""
            ? null
            : String(rawNpc.hintName5),
        numericalSC: this.safeParseExtendedNpcArray(rawNpc?.numericalSC),
        numericalSCSheetID:
          rawNpc?.numericalSCSheetID == null
            ? ""
            : String(rawNpc.numericalSCSheetID).trim(),
        numericalSCSheetRange:
          rawNpc?.numericalSCSheetRange == null
            ? ""
            : String(rawNpc.numericalSCSheetRange).trim(),
        numericalTaskIframeUrl:
          rawNpc?.numericalTaskIframeUrl == null
            ? ""
            : this.normalizeNumericalTaskIframeUrl(
                rawNpc?.numericalTaskIframeUrl
              ),
      };
    },

    applyExtendedNumericalNpcPayload(rawNpc = {}) {
      const numericalConfig = this.extractExtendedNumericalNpcPayload(rawNpc);
      const fallbackSheetId = String(
        this.numericalSCSheetID || this.$route?.query?.sheetId || ""
      ).trim();
      const fallbackSheetRange = String(
        this.numericalSCSheetRange || this.$route?.query?.range || ""
      ).trim();

      if (numericalConfig.numericalSCSwitch != null) {
        this.numericalSCSwitch = numericalConfig.numericalSCSwitch;
      }
      if (numericalConfig.hintName5) {
        this.hintName5 = numericalConfig.hintName5;
      }

      this.numericalSC = numericalConfig.numericalSC;
      this.numericalSCDb = numericalConfig.numericalSC.slice();
      this.numericalSCSheetID =
        numericalConfig.numericalSCSheetID || fallbackSheetId;
      this.numericalSCSheetRange =
        numericalConfig.numericalSCSheetRange || fallbackSheetRange;
      this.numericalTaskIframeUrl = this.normalizeNumericalTaskIframeUrl(
        numericalConfig.numericalTaskIframeUrl
      );
    },

    // 檢查或建立同步文件
    async checkOrCreateMegDoc(roomId) {
      try {
        const res = await axios.post(
          `${this.apiNetWork2}/megDoc/checkOrCreate`,
          { roomId }
        );
        return res.data;
      } catch (e) {
        console.error("megDoc API error:", e);
        return null;
      }
    },
    // 發送打字紀錄
    async postTypingLog(session) {
      if (!this.loginRoom) return;
      try {
        await axios.post(`${this.apiNetWork2}/megDocLog`, {
          roomId: this.loginRoom,
          userId: this.userName || this.name,
          startTime: session.startTime,
          endTime: session.endTime,
          duration: session.duration,
          type: "typing",
        });
      } catch (e) {
        console.error("megDocLog API error:", e);
      }
    },
  },
  created() {
    // 從 URL 讀取 ch1/ch2/ch3 及 room 參數
    const query = this.$route?.query || {};
    this.initialRouteQuery = { ...query };
    this.persistNumericalRouteQuery(query);
    this.applyNumericalRouteState(query);

    // 單 NPC 指定登入
    this.loginCh = query.ch || "";
    if (String(query.mode || "").toLowerCase() === "numerical" && query.ch) {
      this.demoMode = true;
    }

    this.loginRoom = query.room || "";

    // 同步文件模式 URL 參數解析
    this.loginDoc = query.doc === "true";

    // 多 NPC 模式
    const chs = [];
    for (let i = 1; i <= 3; i++) {
      if (query[`ch${i}`]) chs.push(query[`ch${i}`]);
    }
    // 無 GAI 連結即使帶有多 NPC 參數，也只開啟一個共同聊天室。
    if (this.isPeerOnlyMode) {
      this.loginCh = this.loginCh || chs[0] || "";
      chs.length = 0;
    }
    this.demoChs = chs;
    this.multiCh = chs;
    if (query.sheetId && !this.numericalSCSheetID) {
      this.numericalSCSheetID = String(query.sheetId);
    }
    if (query.range && !this.numericalSCSheetRange) {
      this.numericalSCSheetRange = String(query.range);
    }
    //開發測試專用，正式環境不會顯示
    if (this.devBypassEnabled && query.devBypass === "1") {
      this.enterDevBypass();
    }
    if (
      this.loginCh ||
      chs.length > 0 ||
      String(query.demo || query.demoMode || "").toLowerCase() === "true"
    ) {
      this.demoMode = true;
    }
  },
  mounted() {
    if (!this.isChatRoute) {
      return;
    }

    const preservedNumericalQuery = this.getPreservedNumericalRouteQuery();
    const preservedNumericalMode =
      String(preservedNumericalQuery.mode || "").toLowerCase() === "numerical";

    if (!this.isNumericalMode && preservedNumericalMode) {
      const currentCh = String(this.$route?.query?.ch || "").trim();
      const preservedCh = String(preservedNumericalQuery.ch || "").trim();

      if (!currentCh || !preservedCh || currentCh === preservedCh) {
        const restoredQuery = this.buildRouteQuery({
          ...preservedNumericalQuery,
          ch: currentCh || preservedCh,
        });
        console.info("[mounted] restore numerical query:", restoredQuery);
        this.$router.replace({ path: "/chat", query: restoredQuery });
        return;
      }
    }

    //  ch 與 session 對應驗證
    const urlCh =
      this.$route.query.ch || (this.isPeerOnlyMode ? this.loginCh : "");
    const adminRole = localStorage.getItem("admin_role") || "";
    const sessionGameId = localStorage.getItem("game_id") || "";
    const sessionAllowedCh = JSON.parse(
      localStorage.getItem("session_allowed_ch") || "[]"
    );

    if (urlCh && !adminRole && sessionGameId) {
      // 允許進入：urlCh 等於主 game_id，或在共用 ch 名單內
      const isAllowed =
        urlCh === sessionGameId || sessionAllowedCh.includes(urlCh);

      if (!isAllowed) {
        localStorage.removeItem("session_token");
        localStorage.removeItem("game_id");
        localStorage.removeItem("session_allowed_ch");
        this.error = `此連結的聊天室（${urlCh}）與您目前的登入身份不符，請重新輸入通行密鑰。`;
        const relockQuery = { ch: urlCh };
        if (this.loginRoom) relockQuery.room = this.loginRoom;
        if (this.loginDoc) relockQuery.doc = "true";
        if (this.isPeerOnlyMode) relockQuery.ref = "noai";
        this.$router.replace({
          path: "/login",
          query: this.buildRouteQuery(relockQuery),
        });
        return;
      }
    } else if (!urlCh && !this.multiCh.length && !adminRole) {
      // 無 ch 自動偵測模式：不保留 session，每次都要重新登入
      const token = localStorage.getItem("session_token");
      if (token) {
        localStorage.removeItem("session_token");
        localStorage.removeItem("game_id");
        localStorage.removeItem("session_allowed_ch");
        const relockQuery = {};
        if (this.loginRoom) relockQuery.room = this.loginRoom;
        if (this.loginDoc) relockQuery.doc = "true";
        this.$router.replace({
          path: "/login",
          query: this.buildRouteQuery(relockQuery),
        });
        return;
      }
    }

    // 1. 由 URL 參數取得 ch，預設為 'default'
    // 多 NPC 時先從 demoChs[0] 取 ch；否則從 ?ch= 或 localStorage 取
    const ch =
      this.demoMode && this.demoChs.length > 0
        ? this.demoChs[0]
        : urlCh || sessionGameId;
    const p = this.$route.query.p;
    const room = this.$route.query.room;

    // 同步文件模式：確保 megDoc 存在
    if (this.isDocMode) {
      this.checkOrCreateMegDoc(this.loginRoom);
    }

    // const storageKey = `appState_${ch}`;
    // 2. 嘗試從 localStorage 讀回對應的狀態
    // const saved = localStorage.getItem(storageKey);
    // if (saved && this.demoMode) {
    //   const parsed = JSON.parse(saved);
    //   if (parsed.gameStartTime) {
    //     const prev = new Date(parsed.gameStartTime);
    //     const now = new Date();
    //     const diff = now - prev; // 毫秒差
    //     if (diff < 1000 * 60 * 60) {
    //       // 小於一小時，詢問是否繼續
    //       Swal.fire({
    //         title: "要從上次的進度繼續嗎？",
    //         showDenyButton: true,
    //         confirmButtonText: "重新來過",
    //         denyButtonText: "繼續",
    //         confirmButtonColor: "#dc3545",
    //         denyButtonColor: "#28a745",
    //         focusDeny: true,
    //       }).then((result) => {
    //         if (result.isConfirmed) {
    //           localStorage.removeItem(storageKey);
    //         } else if (result.isDenied) {
    //           Object.assign(this.$data, parsed);
    //         }
    //       });
    //     } else {
    //       // 超過一小時，自動清除舊紀錄並開啟 modal
    //       localStorage.removeItem(storageKey);
    //     }
    //   }
    // } else {
    //   // 沒有舊紀錄，或 demoMode=false
    //   localStorage.removeItem(storageKey);
    // }

    // 3. 不論如何，記錄這次 session 開始時間
    this.gameStartTime = new Date().toISOString();
    this.resetIdleTimer();

    if (this.adminRole === "keyManager") {
      this.fetchAccessCodes();
      return;
    }

    if (!this.demoMode) {
      this.defaultDateTime();
      if (!this.isDevBypassSession) {
        this.fetchNPCOptions();
        this.fetchAccessCodes();
        if (this.adminRole === "super_admin") this.fetchAdminAccounts();
      }
    }

    if (this.demoMode) {
      if (ch) {
        this.gameId = ch;
        this.studentGameId = ch;

        if (!this.isNumericalPreviewRoute) {
          this.getGPTDataCheck(ch);
        }
        this.getGPTData(ch);
      } else {
        Swal.fire("尚未開放");
        this.render = false;
        return;
      }
    } else if (ch) {
      this.gameId = ch;
      this.getGPTData(ch);
    }
    if (p) {
      this.gameId = p;
    }
    if (room) {
      this.roomCode = room;
    } else {
      const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      this.roomCode = "solo";
      for (let i = 0; i < 5; i++) {
        this.roomCode += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.hideRoomCode = true;
    }
    if (this.isNumericalMode && !this.isNumericalPreviewRoute) {
      this.loadNumericalBaseline();
    }
    this.linkChat();

    // 每 5 分鐘 touch 一次 session，防止後端閒置過期
    this.heartbeatInterval = setInterval(async () => {
      const token = localStorage.getItem("session_token");
      if (!token) return;
      try {
        await axios.post(`${this.apiNetWork2}/api/session/touch`);
        this.resetIdleTimer();
      } catch (e) {
        // 401/403 會由 axios interceptor 統一處理強制登出
      }
    }, 5 * 60 * 1000);

    // 分頁從背景切回時立即 touch 確保 session 有效
    this._onVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const token = localStorage.getItem("session_token");
      if (!token) return;
      try {
        await axios.post(`${this.apiNetWork2}/api/session/touch`);
        this.resetIdleTimer();
      } catch (e) {
        // 401/403 會由 axios interceptor 統一處理強制登出
      }
    };
    document.addEventListener("visibilitychange", this._onVisibilityChange);

    // 處理Safari bfcache還原
    this._onPageShow = (event) => {
      if (event.persisted) this._onVisibilityChange();
    };
    window.addEventListener("pageshow", this._onPageShow);
  },

  beforeDestroy() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.stopSpeechRecognition();
    this.typingActiveMessageId = null;
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    window.removeEventListener("pageshow", this._onPageShow);
  },

  updated() {
    if (!this.isChatRoute) return;

    const chatLogLength = Array.isArray(this.chatLog) ? this.chatLog.length : 0;
    if (chatLogLength <= this.lastAutoScrolledChatLogLength) return;

    this.lastAutoScrolledChatLogLength = chatLogLength;

    if (
      this.$refs.chatRef &&
      typeof this.$refs.chatRef.scrollTo === "function"
    ) {
      this.$refs.chatRef.scrollTo({
        top: 99999,
        left: 0,
        behavior: "smooth",
      });
    }
  },
  watch: {
    // 監聽 chatLog 陣列本身或裡面元素的變化
    chatLog: {
      handler(newLog) {
        // console.log("autom");
        if (this.isPeerOnlyMode) return;
        const last = newLog[newLog.length - 1];
        // 只對使用者訊息做自動鷹架
        if (last.role == "user") {
          this.autoMessage();
        }
        // 階段式文件回饋：每累積 docFeedbackN 句可見對話
        const visibleCount = newLog.filter((m) => !m.hidden).length;
        console.log("[docFeedback][watch]", {
          docFeedbackSwitch: this.docFeedbackSwitch,
          isDocMode: this.isDocMode,
          docFeedbackN: this.docFeedbackN,
          lastRole: last.role,
          visibleCount,
          mod: this.docFeedbackN > 0 ? visibleCount % this.docFeedbackN : "N/A",
          currentDocContentLength: this.currentDocContent
            ? this.currentDocContent.length
            : 0,
        });
        if (
          !this.isPeerOnlyMode &&
          this.docFeedbackSwitch &&
          this.isDocMode &&
          this.docFeedbackN > 0 &&
          last.role === "assistant" &&
          visibleCount % this.docFeedbackN === 0
        ) {
          console.log(
            "[docFeedback][watch] 條件滿足，觸發 triggerDocFeedback()"
          );
          this.triggerDocFeedback();
        }
      },
    },
    $data: {
      handler() {
        // 每次 data 改變就存回對應的 storageKey
        // if (this.demoMode) {
        //   const params = new URLSearchParams(window.location.search);
        //   const ch = params.get("ch") || "default";
        //   const storageKey = `appState_${ch}`;
        //   localStorage.setItem(storageKey, JSON.stringify(this.$data));
        // }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.login-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  background-color: #f4f6f9;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  margin: -30px;
  box-sizing: border-box;
}

.login-card {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  text-align: center;
  box-sizing: border-box;
  margin-top: 20vh;
}

.login-card-wide {
  max-width: 560px;
}

.login-header {
  margin-bottom: 25px;
}

.login-title {
  margin: 0 0 8px;
  color: #2c3e50;
  font-size: 22px;
  font-weight: bold;
}

.login-subtitle {
  color: #7f8c8d;
  font-size: 14px;
  margin: 0;
}

.login-form-group {
  display: flex;
  gap: 12px;
}

.login-row-group {
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.login-col-group {
  flex-direction: column;
}

.login-numerical-group {
  align-items: stretch;
}

.login-access-code-group {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.login-access-code-group-inline {
  width: auto;
  flex-direction: row;
  align-items: center;
}

.login-form-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 15px;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}

.login-form-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.15);
}

.login-code-input {
  text-align: center;
  font-size: 18px;
  letter-spacing: 3px;
  font-weight: bold;
}

.login-btn {
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.login-btn-primary {
  background-color: #409eff;
  color: white;
}

.login-btn-primary:hover {
  background-color: #66b1ff;
}

.login-btn-primary:active {
  transform: scale(0.98);
}

.login-btn-secondary {
  background-color: #eef5ff;
  color: #1f4fa3;
}

.login-btn-secondary:hover {
  background-color: #dce9ff;
}

.login-btn-block {
  width: 100%;
  margin-top: 6px;
}

.login-btn-submit {
  width: 100%;
}
/* 開發測試專用，正式環境不會顯示 */
.login-btn-dev-bypass {
  background-color: #ffffff;
  color: #1f3c88;
  border: 1px dashed rgba(31, 60, 136, 0.35);
}

.login-btn-dev-bypass:hover {
  background-color: rgba(31, 60, 136, 0.06);
}

.login-error-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 10px;
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 6px;
  color: #f56c6c;
  font-size: 14px;
}

.login-error-icon {
  flex-shrink: 0;
}

.numerical-player-code-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 6px;
}

.numerical-player-code-label {
  text-align: left;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 600;
}

.numerical-player-code-row {
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;
}

.numerical-player-code-input {
  text-align: left;
  letter-spacing: 0;
  font-weight: 400;
}

@media (max-width: 640px) {
  .login-card {
    padding: 32px 20px;
    margin-top: 10vh;
  }

  .login-row-group,
  .login-access-code-group-inline,
  .numerical-player-code-row {
    flex-direction: column;
    align-items: stretch;
  }

  .login-btn-submit {
    width: 100%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 30px;
  overflow: hidden !important;
}

#app.app-numerical-mode {
  height: auto;
  min-height: 100vh;
  margin-top: 0;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

#app.app-numerical-mode .numerical-chat-shell {
  height: 100%;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

#app.app-numerical-mode .numerical-chat-status {
  flex-shrink: 0;
  width: 100%;
}

#app.app-numerical-mode .numerical-name-prompt {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(248, 251, 255, 0.96);
  text-align: left;
}

#app.app-numerical-mode .numerical-name-card {
  width: min(420px, 100%);
  padding: 28px;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.16);
}

#app.app-numerical-mode .numerical-situation-banner {
  flex-shrink: 0;
  width: 100%;
  padding: 12px 14px 4px;
  text-align: center;
}

#app.app-numerical-mode .numerical-situation-banner .layout-guide-badge {
  display: inline-block;
  max-width: 100%;
  padding: 8px 12px;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.5;
  white-space: normal;
  text-align: left;
}

#app.app-numerical-mode .numerical-name-card h3 {
  margin: 0 0 8px;
  color: #102a43;
  font-size: 24px;
  text-align: center;
}

#app.app-numerical-mode .numerical-name-card p {
  margin: 0 0 20px;
  color: #627d98;
  text-align: center;
}

#app.app-numerical-mode .numerical-name-prompt label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

#app.app-numerical-mode .numerical-chat-layout {
  flex: 1;
  min-height: 0;
  height: auto;
  display: flex;
  flex-direction: column;
}

#app.app-numerical-mode .numerical-chat-box {
  flex: 1;
  min-height: 0;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
}

#app.app-numerical-mode .numerical-chat-input-wrap {
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 12px;
  background-color: var(--chat-bg-color);
}

.peer-icon {
  height: 0.7em;
  width: auto;
  vertical-align: middle;
  filter: brightness(0) invert(1);
}

.chat-input-autosize.layout-chat-input,
.chat-input-autosize.layout-chat-input:disabled {
  color: var(--chat-input-text-color);
  background-color: var(--chat-input-bg-color);
}

.chat-input-autosize.layout-chat-input:focus {
  color: var(--chat-input-text-color);
  background-color: var(--chat-input-bg-color);
}

.chat-input-autosize.layout-chat-input::placeholder {
  color: var(--chat-input-text-color);
  opacity: 0.65;
}

.chat-input-autosize {
  height: calc(1.5em + 0.75rem + 2px);
  min-height: calc(1.5em + 0.75rem + 2px);
  max-height: calc(4.5em + 0.75rem + 2px);
  line-height: 1.5;
  overflow-y: hidden;
  resize: none;
}

.speech-button {
  min-width: 44px;
}

.speech-button.listening,
.speech-button.listening:hover,
.speech-button.listening:focus {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
}

.layout-send-button,
.layout-send-button.btn-primary,
.layout-send-button.btn-success,
.layout-send-button.btn-primary:disabled,
.layout-send-button.btn-primary.disabled,
.layout-send-button.btn-success:disabled,
.layout-send-button.btn-success.disabled {
  background-color: var(--chat-button-color);
  border-color: var(--chat-button-color);
}

.layout-ask-limit-badge.badge-primary {
  background-color: var(--chat-ask-limit-bg-color);
}

.layout-guide-badge.badge-info {
  background-color: var(--chat-guide-bg-color);
}

.chatBox {
  width: 100%;
  height: 85%;
  overflow-y: scroll;
  overflow-x: hidden;
  font-size: var(--chat-font-size);
  color: var(--chat-text-color);
  background-color: var(--chat-bg-color);
}

.chatBox p {
  margin-bottom: 5px;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  color: var(--chat-text-color);
}

.messenger-chat-box {
  padding: 10px 0;
}

.messenger-chat-box p {
  margin: 0 0 8px;
  white-space: normal;
}

.messenger-message-row {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.messenger-message-row-own {
  align-items: flex-end;
  justify-content: flex-end;
  text-align: right;
}

.messenger-message-row-other {
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
}

.messenger-message-name {
  display: block;
  width: fit-content;
  max-width: 760px;
  box-sizing: border-box;
  margin-bottom: 3px;
  font-size: 12px;
  line-height: 1.2;
  color: #65676b;
}

.messenger-message-name-own {
  text-align: right;
}

.messenger-message-name-other {
  text-align: left;
}

.messenger-message-row-other .messenger-message-bubble {
  margin-left: 0;
  margin-right: auto;
}

.messenger-message-row-own .messenger-message-bubble {
  margin-left: auto;
  margin-right: 0;
}

.messenger-message-bubble {
  display: inline-block;
  width: fit-content;
  min-width: 4em;
  max-width: 72%;
  box-sizing: border-box;
  padding: 8px 14px;
  border-radius: 18px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  text-align: left;
}

.messenger-message-bubble-own {
  color: #fff;
  background-color: var(--messenger-own-bubble-color);
  border-bottom-right-radius: 6px;
}

.messenger-message-bubble-other {
  color: #050505;
  background-color: var(--messenger-other-bubble-color);
  border-bottom-left-radius: 6px;
}

.chatLayout {
  height: calc(95vh - 120px);
  background-color: var(--chat-bg-color);
}

.badge:empty {
  width: 1px !important;
  display: inline-block !important;
}

.toast {
  width: 500px;
  max-width: 500px !important;
  margin-left: -20%;
}

.sidebar-btn {
  position: fixed;
  top: 50%;
  z-index: 1000;
  left: 16px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 20px;
  transform: translateY(-50%);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.sidebar-btn:hover {
  transform: translateY(-50%) scale(1.1);
  transition: transform 0.2s ease;
}

/* 第二顆按鈕(角色清單)的位置 */
.role-btn {
  top: 65%; /* 往下移一點，避免跟第一顆重疊 */
}

.access-code-btn {
  top: 80%;
}

.admin-account-btn {
  top: 35%;
}

/* 限制側邊欄的角色清單顯示 15 筆，多的要滾動 */
.role-list {
  max-height: 600px; /* 大約 15 筆 (可依實際高度微調) */
  overflow-y: auto; /* 超過就出現垂直捲動條 */
  padding-right: 10px; /* 預留空間避免捲軸壓到文字 */
}

.access-code-list-scroll {
  max-height: 760px;
  overflow-y: auto;
  padding-right: 8px;
}

.npc-dropdown .dropdown-menu {
  max-height: 200px; /* 大約顯示 5 筆 */
  overflow-y: auto;
}

/* NPC 頭像樣式 */
.avatar-section {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-container {
  width: 100%;
  max-width: 300px;
}

.npc-avatar {
  width: 100%;
  aspect-ratio: 1 / 1; /* 強制 1:1 比例 */
  object-fit: cover; /* 保持比例並填滿容器 */
  border-radius: 10px;
  border: 3px solid #dee2e6;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.npc-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.placeholder-avatar {
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 3px dashed #dee2e6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.placeholder-content {
  text-align: center;
}

@media (max-width: 768px) {
  .avatar-section {
    padding: 10px;
  }

  .avatar-container {
    max-width: 200px;
  }
}

.doc-split-layout {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

.doc-split-left {
  flex: 2;
  height: 100%;
  border-right: 1px solid #dee2e6;
  overflow: hidden;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

.chat-in-doc-mode {
  flex: 1 !important;
  height: 100vh !important;
  max-width: none !important;
  overflow-y: auto !important;
  box-sizing: border-box !important;
  -webkit-overflow-scrolling: touch;
}
</style>
